export const griInfo: any = {
  "101-1": `
    <div>
      <strong>Disclosure 101-1 Policies to halt and reverse
biodiversity loss</strong>
  </div><br>
  <div><strong>REQUIREMENTS</strong></div>
  <strong>
The organization shall:<br>
<ul style='list-style-type:lower-alpha;padding-left:40px'>
<li>
describe its policies or commitments to halt and reverse biodiversity loss, and how these are informed by the 2050 Goals and 2030 Targets in the Kunming-Montreal Global Biodiversity Framework;</li>
<li>
report the extent to which these policies or commitments apply to the organization’s activities and to its business relationships;</li>
<li>
report the goals and targets to halt and reverse biodiversity loss, whether they are informed by scientific consensus, the base year, and the indicators used to evaluate progress.</li>
</ul><br>
</strong>
<hr>

  <div><strong>GUIDANCE</strong></div>
  The Convention on Biological Diversity adopted the Kunming-Montreal Global Biodiversity
Framework (hereafter the Global Biodiversity Framework). The Global Biodiversity Framework
lays out its 2050 vision of a world ‘living in harmony with nature’ where ‘biodiversity is valued,
conserved, restored and wisely used, maintaining ecosystem services, sustaining a healthy
planet and delivering benefits essential for all people’.
<br>
The Global Biodiversity Framework recognizes the need to reduce or reverse the drivers of
biodiversity loss. The framework proposes the 2050 Goals, together with the related 2030
Targets. The goals with the related targets are designed to stimulate efforts in three key areas:
<ul style='list-style-type:disc;padding-left:40px'>
<li> reducing the threats to biodiversity;</li>
<li> meeting people’s needs through sustainable use and benefit-sharing;</li>
<li> providing tools and solutions for implementing and integrating practices that conserve and
sustainably use biodiversity.</li>
</ul>

<b>Guidance to 101-1-a</b><br>
The organization can provide a high-level description of its policies or commitments to halt and
reverse biodiversity loss. For example, the organization can describe that it has implemented a
policy in line with Target 5 of the Global Biodiversity Framework to source from suppliers that
take appropriate measures to prevent exporting species that are alien and invasive to the buying
country.<br>
If the policies or commitments to halt and reverse biodiversity loss are not informed by the 2050
Goals and 2030 Targets in the Global Biodiversity Framework, a brief statement of this fact is
sufficient to comply with the requirement. The organization can explain if it intends to do so and,
if so, by which timeframe.<br>
If the organization has described its policies or commitments to halt and reverse biodiversity
loss under Disclosure 2-23 in GRI 2: General Disclosures 2021 or under 3-3-c in GRI 3: Material
Topics 2021, it can provide a reference to this information under 101-1-a and does not need to
repeat the information.<br>

<b>Guidance to 101-1-b</b><br>
If the policies or commitments apply to all of the organization’s activities and business
relationships equally, a brief statement of this fact is sufficient to comply with the requirement.<br>
If the policies or commitments apply to only some of the organization’s activities (e.g., they apply
only to entities located in certain countries or to certain subsidiaries) or to some of its business
relationships (e.g., they apply only to suppliers), then the organization should report to which
activities and business relationships the policies or commitments apply. It can also explain why
the policies or commitments are limited to these activities and business relationships.<br>
The organization should also explain whether its business relationships are obligated to abide
by the policies or commitments, or are encouraged (but not obligated) to do so. When its
business relationships are encouraged to abide by the policies or commitments, the
organization can describe how it encourages adoption and what incentives or support it
provides.<br>
<b>Guidance to 101-1-c</b><br>
To halt and reverse biodiversity loss, the organization may have goals and targets to achieve net
positive impact, no net loss and net gain of biodiversity, or to contribute to nature positive goals.
In such a case, the organization should explain how it has defined these concepts and list the
sources used to inform its definition.<br>
When reporting on goals and targets, the organization should report how the goals and targets
are set. For example, it can use the Science Based Targets Network (SBTN) target-setting tools
and guidance or the SBTN and the Taskforce on Nature-related Financial Disclosures
(TNFD) Guidance for corporates on science-based targets for nature.<br>
The organization should report how scientific consensus informed its goals and targets. For
example, it can use national biodiversity strategies and action plans developed in the context of
the Convention on Biological Diversity, or independent assessments of the ecological status of
an area.<br>
The organization should also report the baseline for the goals and targets and the timeline for
achieving the goals and targets.<br>
When reporting progress toward the goals and targets and assessing if the progress is
satisfactory, the organization can, for example, report that it sourced 60% of deforestation-free
products in 2023. It can further report that it is on track to achieve its target of sourcing 100%
deforestation-free products by 31 December 2030.
  `,
  "101-2": `
      <div>
      <strong>Disclosure 101-2 Management of biodiversity impacts</strong>
  </div><br>
    <div><strong>REQUIREMENTS</strong></div><br>
    <strong>
    The organization shall:
<ul style="list-style-type:lower-alpha;padding-left:40px">
<li>
report how it applies the mitigation hierarchy by describing:
<ul  style="list-style-type:lower-roman;padding-left:40px">
<li>
actions taken to avoid negative impacts on biodiversity;</li>
<li>
actions taken to minimize negative impacts on biodiversity that were not avoided;</li>
<li>
actions taken to restore and rehabilitate affected ecosystems, including the goals of the restoration and rehabilitation, and how stakeholders are engaged throughout the restoration and rehabilitation actions;</li>
<li>
actions taken to offset residual negative impacts on biodiversity;</li>
<li>
transformative actions taken and additional conservation actions taken;</li>
</ul>
</li>
<li>
with reference to 101-2-a-iii, report for each site with the most significant impacts on biodiversity:
<ul style="list-style-type:lower-alpha;padding-left:40px">
<li>
the size in hectares of the area under restoration or rehabilitation;</li>
<li>
the size in hectares of the area restored or rehabilitated;</li>
</ul>
with reference to 101-2-a-iv, report for each offset:
<ul style="list-style-type:lower-alpha;padding-left:40px">
<li>
the goals;</li>
<li>
the geographic location;</li>
<li>
whether and how principles of good offset practices are met;</li>
<li>
whether and how the offset is certified or verified by a third party;</li>
</ul></li>
<li>
list which of its sites with the most significant impacts on biodiversity have a biodiversity management plan and explain why the other sites do not have a management plan;</li>
<li>
describe how it enhances synergies and reduces trade-offs between actions taken to manage its biodiversity and climate change impacts;</li>
<li>
describe how it ensures that the actions taken to manage its impacts on biodiversity avoid and minimize negative impacts and maximize positive impacts for stakeholders.</li>
</ul><br></strong>
<hr>

  <div><strong>GUIDANCE</strong></div>
This disclosure provides information on the actions taken by the organization to manage its
most significant impacts on biodiversity, including impacts in its supply chain. This disclosure
covers the most significant impacts at the sites and for the products and services in the supply
chain identified under Disclosure 101-4. The organization can manage its negative impacts by
managing the contribution to the direct drivers of biodiversity loss reported under Disclosure
101-6 (e.g., avoid pollution or minimize greenhouse gas emissions). The organization should
also report actions taken to manage impacts downstream in its value chain.<br>
Organizations are expected to apply the mitigation hierarchy to manage their negative impacts
on biodiversity and ecosystem services. The mitigation hierarchy consists of steps, including
avoidance, minimization, restoration and rehabilitation, and offset. An organization should
prioritize actions to avoid negative impacts and minimize those impacts when avoidance is not
possible. Restoration and rehabilitation measures should be implemented when negative
impacts cannot be avoided or minimized. After applying all other measures, offsetting measures
can also be applied to residual negative impacts to achieve no net loss or net gain. Building on
the mitigation hierarchy, the Science Based Targets Network (SBTN) Initial Guidance for
Business [38] includes an additional step to cover transformative actions, which aim to change
the socio-economic systems in which organizations are embedded. Additional conservation
actions can be taken to create a positive impact on biodiversity beyond the management of the
organization’s negative impacts.<br>
For more information on the mitigation hierarchy, see the Cross Sector Biodiversity Initiative
(CSBI) A cross-sector guide for implementing the Mitigation Hierarchy [15] and the International
Finance Corporation (IFC) Performance Standard 6: Biodiversity Conservation and Sustainable
Management of Living Natural Resources.<br>
This disclosure covers actions taken at the site level and at the organizational level (e.g., a ban
on sourcing a certain product across the entire organization).<br>
The organization should describe the traceability mechanisms it uses to identify the origin of
products and entities in its supply chain. The organization should also describe actions taken to
improve traceability. The organization should explain whether it sources products certified by a
third party and specify the certification schemes or standards used. Third-party certification can
provide information on whether the products sourced adhere to sustainable management
practices. The organization should explain how these certification schemes or standards help
manage impacts on biodiversity, as they use different criteria related to biodiversity conservation.
The organization can also report the percentage of certified products sourced.<br>
The organization should describe how it works with its suppliers to manage their negative
impacts on biodiversity, for example, by providing them with financial or technical support to
change their practices.<br>
The organization should describe how it works with other organizations and stakeholders to
manage cumulative impacts. For example, an organization can describe how it works with other
organizations and the local community to reduce their combined water withdrawal to mitigate the
negative impact on biodiversity. The organization’s activities may facilitate other organizations
and stakeholders to cause impacts on biodiversity. In such a case, the organization should
describe how it works with other organizations and stakeholders to manage these impacts. For
example, consider an organization that has constructed an access road to a new site. This
access road also becomes a pathway to previously inaccessible areas for individuals engaged
in hunting. In this example, the organization can describe how it works with the government to
limit the use of this road.<br>
Where applicable, the organization should also describe actions taken to ensure the
conservation and sustainable use of marine resources in areas beyond national jurisdictions.<br>
<b>Guidance to 101-2-a-i</b><br>
Avoidance actions aim to anticipate and prevent negative impacts on biodiversity before actions
or decisions leading to such impacts are taken. Impacts can be avoided by finding alternative
locations for the activities (e.g., relocating the site), changing the timing of the activities (e.g.,
timing activities when they do not interfere with a species' breeding or migration), or by deciding
not to undertake activities when they generate irremediable biodiversity losses (e.g., deciding
against expanding the site). Organizations are expected to prioritize avoidance as the primary
step in the mitigation hierarchy.<br>
The organization should explain if it avoids activities in or near ecologically sensitive areas, such
as protected areas and Key Biodiversity Areas. See Disclosure 101-5 and Table 1 in the
Appendix for more information on ecologically sensitive areas.<br>
<b>Guidance to 101-2-a-ii</b><br>
Actions taken to minimize negative impacts on biodiversity aim to reduce the duration, intensity,
and extent of impacts that cannot be completely avoided. The organization should explain why
the impacts could not be avoided.<br>
Examples of minimization measures include preventing the spread of invasive alien species,
designing ecological corridors to minimize ecosystem fragmentation, or locating sites to areas
that are less sensitive to an organization’s activities.<br>

<b>Guidance to 101-2-a-iii</b><br>
This requirement covers the actions taken to restore or rehabilitate ecosystems that are affected
by the organization’s activities. Actions taken outside of the area affected by the organization’s
activities are reported as offsets under 101-2-a-iv or as additional conservation actions under
101-2-a-v. Restoration is the process of assisting the recovery of an ecosystem that has been
degraded, damaged, or destroyed. Rehabilitation is the process of stabilizing the terrain,
ensuring public safety, enhancing aesthetics, and restoring the land to a purpose deemed
useful within the regional context. Actions taken to restore and rehabilitate affected ecosystems
aim to return the environment to its original state or to a state where it has a healthy and
functioning ecosystem.<br>
The organization should specify if the restoration and rehabilitation actions are implemented
while the organization's activities are ongoing or after the activities have ended (e.g., restoration
actions taken after the closure of a site). The organization should also report the stage of its
restoration and rehabilitation actions. Examples of stages of restoration and rehabilitation are
as follows:
<ul style='list-style-type:disc;padding-left:40px'>
<li> planning and design;</li>
<li> implementation;</li>
<li> monitoring, documentation, evaluation, and reporting;</li>
<li> ongoing activities and maintenance.</li>
The UN Decade on Ecosystem Restoration has identified principles that detail best practices for
restoring degraded land, freshwater, and marine ecosystems.<br>
The organization should provide information on the species and ecosystems targeted through
the restoration and rehabilitation actions. The organization should also explain how these
actions support species recovery.<br>
When reporting on the goals of the restoration and rehabilitation, the organization can report to
what extent the actions are proportional, viable, and measurable. ‘Proportional’ means that the
area targeted for restoration or rehabilitation is equivalent in size to the area that has been
affected. ‘Viable’ means that no known constraints can hinder the successful implementation of
the restoration or rehabilitation in the short, medium, and long term, and the set goals are
attainable based on the current ecological assessment results. An example of short, medium
and long term restoration and rehabilitation is that the land ownership is not limited in time.
’Measurable’ means that objectives have been defined and are regularly monitored.
Stakeholder engagement can include co-design, co-management, co-governance, and regular
and inclusive reporting and communication of activities.<br>
Organizations are expected to obtain free, prior, and informed consent (FPIC) before and
throughout restoration and rehabilitation activities that could have impacts on land or resources
that Indigenous Peoples use or own. Organizations are also expected to seek FPIC when
restoration and rehabilitation activities have impacts on land or resources that local
communities use or own.<br>

<b>Guidance to 101-2-a-iv</b>
Offsets are management interventions in areas not affected by the organization’s activities.
These can include the restoration or rehabilitation of degraded ecosystems or actions taken to
halt and reverse biodiversity loss.<br>
The organization should report the types of offsets used. Examples of biodiversity offsets include
averted loss, restoration, and one-off offsets.<br>
The organization should report the phases that the offset projects are in, for example, design,
implementation, or completion. It should also report the delivery deadlines and the conservation
goals.<br>
The organization should also report the co-benefits and trade-offs associated with the offsets,
and how those trade-offs are managed. Examples of co-benefits include the capture and
storage of carbon and social or cultural benefits. An example of a trade-off would be replacing
non-native trees with native trees, while the local community preferred the non-native species for
the purpose of firewood.<br>

<b>Guidance to 101-2-a-v</b>
Transformative actions contribute to systemic change inside and outside the organization’s
value chain to generate positive impacts on biodiversity. They address the drivers of biodiversity
loss through technological, economic, institutional, and social factors, emphasizing the
importance of underlying values and behavioral changes. Transformative actions can happen
before, during, and after other avoidance, minimization, restoration and rehabilitation, and offset
actions. Transformative actions include actions taken with third parties (e.g., experts,
governments, local communities), and actions that enable other organizations to generate
positive impacts on biodiversity.<br>
The organization can describe how it ensures that its business model is compatible with the
transition to halt and reverse biodiversity loss or the steps taken to transition to a circular
economy. The organization can also describe actions that advance the sustainable use of
biodiversity, for example, promoting farming practices that support biodiversity.
Additional conservation actions aim to have a positive impact on biodiversity and should not be
used to manage the organization’s negative impacts. They include actions taken to conserve or
restore biodiversity in collaboration with third parties, such as scientific experts, nongovernmental organizations, or local communities. For example, joint research projects,
technical and scientific cooperation, capacity-building, training, or knowledge sharing.

<b>Guidance to 101-2-b</b>
Requirement 101-2-b provides information about the size of the area under restoration or
rehabilitation and the size of the area restored or rehabilitated for each site with the most
significant impacts on biodiversity. The sites with the most significant impacts are those
reported under 101-5-a. This information can be compared to the size of the ecosystem affected
by the organization’s activities reported under 101-7-a-ii. It can also be compared to the size of
the site reported under 101-5-a. These comparisons provide insight into how much of the
affected area is under restoration and rehabilitation and how much has been restored and
rehabilitated.<br>
<b>Guidance to 101-2-c-i</b><br>
An offset aims to deliver or contribute to no net loss or net gain goals for a site, a species, or
other biodiversity features. The organization can report the delivered outcomes in the case an
offset has been finalized.<br>
The organization should report how the goal to achieve no net loss or net gain is demonstrated
and verified. The organization should provide information on the species and ecosystems
targeted through the actions to offset its residual negative impacts.<br>
The organization can also report the residual negative impacts of its activities. It can apply a no
net loss and loss-gain calculation as described in the Business and Biodiversity Offsets
Programme (BBOP) Resource Paper: No Net Loss and Loss-Gain Calculations in Biodiversity
Offsets.<br>

<b>Guidance to 101-2-c-iii</b><br>
The organization should explain whether it identifies, designs, and manages offsets according
to applicable national legislation or principles of good offset practices, such as the BBOP
Standard on Biodiversity Offsets [11] or the International Union for Conservation of Nature (IUCN)
Policy on Biodiversity Offsets [26]. The Organisation for Economic Cooperation and Development
(OECD) Biodiversity Offsets: Effective Design and Implementation [33] also provides lessons
learned and insights on good practices, such as additionality, ecological equivalence, and
permanence.<br>
‘Additionality’ is a property of a biodiversity offset, where the conservation outcomes are
demonstrably new and additional and would not have resulted without the offset (e.g., weed
control measures required by legislation cannot contribute to an offset). As no two areas are
ecologically identical, ‘ecological equivalence’ means that the biodiversity gains from the offset
must be equivalent to the residual impacts. ‘Permanence’ means that the offsets must provide
biodiversity gains that correspond to the duration of the biodiversity loss from the residual
impacts.<br>

<b>Guidance to 101-2-d</b><br>
Requirement 101-2-d provides information about which sites with the most significant impacts
on biodiversity have a biodiversity management plan. The sites with the most significant impacts
are those reported under 101-5-a.<br>
A biodiversity management plan describes how the actions to manage biodiversity impacts are
implemented within a particular site. It includes a monitoring plan, a time schedule, milestones,
and targets. The plans to manage biodiversity impacts may be integrated into broader site
environmental management plans.<br>
<b>Guidance to 101-2-e</b><br>
Synergies include actions taken to protect biodiversity that contribute to climate change
mitigation or adaptation. Actions can also improve the capacity of species or ecosystems to
adapt to unavoidable climate change impacts. For example, planting mangroves can protect
biodiversity by increasing the wildlife population and contribute to climate change mitigation and
adaptation by capturing and storing carbon and controlling floods.<br>
In contrast, trade-offs include climate change mitigation or adaptation actions that result in
biodiversity loss. For example, foresting an area with non-native species may contribute to
climate change mitigation and adaptation by absorbing greenhouse gases and controlling
erosion. However, it may also result in the loss of biodiversity and ecosystem services that flow
from the affected ecosystems.<br>
If the organization does not enhance synergies or reduce trade-offs between actions taken to
manage its biodiversity and climate change impacts, a brief statement of this fact is sufficient to
comply with the requirement.<br>
<b>Guidance to 101-2-f</b><br>
Actions taken to manage impacts on biodiversity may lead to negative impacts on stakeholders.
For example, when an organization’s offset measures form a new protected area restricting the
local community from using the area and accessing natural resources.<br>
The organization should report which stakeholders are affected or potentially affected and
explain how it identifies, addresses, and monitors the negative and positive impacts on
stakeholders. The organization should explain how it engages with stakeholders to identify and
avoid negative impacts that are considered unacceptable and cannot be mitigated or
compensated for. It should also describe the actions taken to achieve equitable social
outcomes. For example, a privately owned protected area invests part of its revenue from
tourism in local energy and healthcare projects, but it restricts local communities from utilizing
the land for agricultural purposes. The organization should also explain how it engages with
stakeholders and describe any conflict resolution or grievance mechanisms it has
implemented. For more information on good practice principles to generate positive social
outcomes while mitigating biodiversity impacts, 
  `,
  "101-3": `
    <div>
      <strong>Disclosure 101-3 Access and benefit-sharing</strong>
  </div><br>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The organization shall:<br>
<ul style='list-style-type:lower-alpha;padding-left:40px'>
<li>
describe the process to ensure compliance with access and benefit-sharing regulations and measures;</li>
<li>
describe voluntary actions taken to advance access and benefit-sharing that are additional to legal obligations or when there are no regulations and measures.</li>
</ul></strong>
    <br>
    <hr>
  <div><strong>GUIDANCE</strong></div>
This disclosure provides information on how the organization complies with access and benefitsharing (ABS) regulations and measures regarding access to genetic resources and
associated traditional knowledge held by Indigenous Peoples and local communities. These
regulations and measures also establish the rules on fair and equitable benefit-sharing arising
from the utilization of genetic resources and the associated traditional knowledge. It also
provides information on the voluntary actions taken by the organization to advance access and
fair and equitable benefit-sharing.<br>
This disclosure is relevant for organizations that use genetic resources to conduct research and
development on the genetic or biochemical composition of resources, including through the
application of biotechnology. It also applies to organizations that use traditional knowledge
associated with genetic resources. These organizations are active in cosmetics,
pharmaceuticals, and agriculture, among other sectors.<br>
The fair and equitable sharing of benefits arising from the utilization of genetic resources is one
of the objectives of the Convention on Biological Diversity. The Nagoya Protocol on Access to
Genetic Resources and the Fair and Equitable Sharing of Benefits Arising from their Utilization
(hereafter the Nagoya Protocol) further advances this objective.<br>
The organization can consult the ABS Clearing-House [13] for more information on ABS. The
platform intends to provide information on national regulations and measures for accessing
genetic resources and associated traditional knowledge. In addition, national focal points might
be established to provide information on ABS on the national level.<br>
When countries lack ABS regulations and measures, an organization can still take action to
share the benefits arising from its use of genetic resources and associated traditional
knowledge fairly and equitably. These actions are referred to as voluntary actions.<br>
The Nagoya Protocol does not cover ABS of genetic resources and associated traditional
knowledge found in areas of the sea that are beyond national jurisdiction. Under the UN
Convention on the Law of the Sea, an agreement has been adopted to conserve and sustain
marine biological diversity in areas beyond national jurisdiction. This agreement covers access
and benefit-sharing of marine genetic resources, including the digital genetic sequence
information of resources located in areas beyond national jurisdiction. If an organization has
activities on the sea beyond national jurisdiction, it can report if it implements processes and
actions to ensure access and fair and equitable benefit-sharing of marine genetic resources.<br>

<b>Guidance to 101-3-a</b><br>
Where ABS regulations and measures apply, the organization should describe:
<ul style='list-style-type:disc;padding-left:40px'>
<li> how it allocates responsibility to ensure compliance with ABS regulations and measures
across different levels within the organization;</li>
<li> how it identifies which provider countries have access and benefit-sharing regulations and
measures;</li>
<li> how it integrates ABS regulations and measures into organizational strategies, operational
policies, and operational procedures;</li>
<li> what training it provides on implementing the ABS regulations and measures.</li>
</ul>
When the organization has identified significant instances of non-compliance with laws and
regulations related to ABS, these are reported under Disclosure 2-27 in GRI 2: General
Disclosures 2021.<br>

<b>Guidance to 101-3-b</b><br>
Examples of voluntary actions include joint research projects, training, or knowledge sharing
related to using genetic resources or associated traditional knowledge in research and
innovation. The ABS Clearing-House [13] includes examples of good practices, codes of
conduct, guidelines, and standards. The UN Nagoya Protocol [4] lists examples of monetary
and non-monetary benefits, which can inform the organization’s voluntary actions.<br>
The organization can report how engagement with stakeholders, particularly Indigenous
Peoples and local communities, has informed its voluntary actions.<br>
If the organization has not taken any voluntary actions to advance access and fair and equitable
benefit-sharing, a brief statement of this fact is sufficient to comply with the requirement.
  `,
  "101-4": `
    <div>
      <strong>Disclosure 101-4 Identification of biodiversity impacts</strong>
    </div><br>
      <div><strong>REQUIREMENTS</strong></div>
      <strong>
      The organization shall:<br>
      <ul style='list-style-type:lower-alpha;padding-left:40px'>
      <li>
explain how it has determined which of its sites and which products and services in its supply chain have the most significant actual and potential impacts on biodiversity.
</li>
</ul></strong>
<br><hr>
  <div><strong>GUIDANCE</strong></div>
  This disclosure enables the organization to explain how it has determined which of its sites and
which products and services in its supply chain have the most significant actual and potential
impacts on biodiversity. It covers products and services from suppliers throughout the
organization’s supply chain, including from suppliers beyond the first tier. This provides an
understanding of where in the supply chain, potentially many tiers removed from the
organization, the most significant impacts on biodiversity are. The organization can additionally
report the information for entities downstream in its value chain.<br>
The activities undertaken by the organization in its sites can have impacts on biodiversity. Sites
include sites owned, leased, or managed by the organization and locations where it conducts its
activities. Examples are a mine site owned by an organization or a fishing ground where an
organization operates. Sites also include those for which future operations have been
announced but not yet started, as well as those no longer active. Sites include subsurface
infrastructures under the land or seabed surface, such as underground mining tunnels, cables,
and pipelines.<br>
The organization may also be involved with negative impacts on biodiversity as a result of its
business relationships with suppliers. Suppliers are entities upstream from the organization,
which provide products or services used to develop the organization’s own products or services.
The activities undertaken by the suppliers to develop their products or services can have
impacts on biodiversity. Suppliers that provide products to the organization can provide raw
materials, semi-manufactured goods, or final products.<br>
<b>Guidance to 101-4-a</b><br>
The organization should describe the methods used and the assumptions made to determine
which of its sites and which products and services in its supply chain have the most significant
actual and potential impacts on biodiversity.<br>
It is up to the organization to set the threshold to determine which sites and which products and
services in its supply chain have the most significant impacts on biodiversity. For example, the
organization can determine that all of its sites have the most significant impacts on biodiversity,
except for its headquarters. An organization that sources many products or services can
determine to prioritize the products or services in its supply chain that are likely to have the most
significant impacts on biodiversity and of which it sources a high volume or on which it spends a
large amount.<br>
The organization should describe any limitations or exclusions, for example, whether it has
excluded certain parts of its supply chain when identifying the impacts.<br>
The organization should describe the sources and the evidence it has used to identify the
impacts. It should also explain whether and how it engages with stakeholders to identify
impacts on biodiversity.<br>
The organization should explain which information draws on primary, secondary, or modeled
data. When reporting secondary or modeled data, the organization should report which datasets
it has used and if it plans to improve the accuracy of data.<br>
<b>Most significant impacts on biodiversity</b><br>
The following provides guidance for organizations on how to identify the most significant
impacts on biodiversity. The organization is not required to comply with this guidance.<br>
Locating where impacts are most likely to be present and significant
The organization should identify impacts on biodiversity across its sites, and products and
services in its supply chain. In some cases, the organization might be unable to identify actual
and potential negative impacts across all its sites, and products and services in its supply
chain. This could be, for example, because the organization has diverse or multiple global
operations or because its supply chain comprises many entities. In such cases, the
organization may carry out an initial assessment or scoping exercise to identify general areas
across its sites and products and services in its supply chain (e.g., product lines, suppliers
located in specific geographic locations) where negative impacts are most likely to be present
and significant.<br>
Once the organization has conducted the initial assessment or scoping exercise, it can identify
and assess actual and potential negative impacts for these general areas.<br>
Activities undertaken by organizations lead or could lead to direct drivers of biodiversity loss
(hereafter direct drivers). These direct drivers can in turn lead to impacts on biodiversity and
related ecosystem services. To identify which activities in its operations and supply chain are
likely to have the most significant impacts on biodiversity, the organization can use the following:
• The ENCORE tool and the SBTN Materiality Screening Tool provide ratings of materiality for
direct drivers associated with different activities.<br>
• The SBTN High Impact Commodity List shows the direct drivers commonly associated with
the production of the high-impact commodities on the list.<br>
The organization can also prioritize products that are or contain threatened species listed in the
IUCN Red List of Threatened Species or species listed in the Convention on International Trade
in Endangered Species of Wild Fauna and Flora (CITES) Appendices.<br>
Activities that occur in different geographic locations can have different impacts on biodiversity,
depending on factors such as the sensitivity of the local ecosystem, the presence of threatened
species, or people’s reliance on a natural resource. Information on the location of the
organization’s sites, and its suppliers’ activities and their proximity to ecologically sensitive
areas, helps understand where these activities could be particularly harmful to biodiversity.<br>
The organization should assess which of its sites are in or near ecologically sensitive areas. If
the organization has information about the location of its suppliers, it can also assess which of
those suppliers are in or near ecologically sensitive areas.<br>
The organization can refer to the Scoping and Locate phases of the Taskforce on Nature-related
Financial Disclosures (TNFD) Guidance on the identification and assessment of nature-related
issues: The LEAP approach [41] for more guidance on locating where impacts are most likely to
be present and significant.<br>
<b>Identification of the most significant impacts<b><br>
To identify and assess the significance of its impacts on biodiversity, the organization should
identify and measure the direct drivers associated with the activities in its operations and its
supply chain, as well as identify and measure the changes to the state of biodiversity. It can also
identify changes in the provision of ecosystem services.<br>
If no primary data is available, the organization can estimate the direct drivers and changes to
the state of biodiversity. The indicators in Disclosure 101-6 can be used to measure the direct
drivers (e.g., the size of the natural ecosystem converted, or the quantity of the pollutants
generated). See Disclosure 101-7 for more information on changes to the state of biodiversity.<br>
To determine which of the impacts are most significant, the organization should assess the
severity and likelihood of the impacts. The severity of a negative impact is determined by the
following characteristics:
<ul style='list-style-type:disc;padding-left:40px'>
<li> Scale: how grave the impact is.</li>
<li> Scope: how widespread the impact is, for example, the number of species affected or the
extent of ecosystem damage.</li>
<li> Irremediable character: how hard it is to counteract or make good the resulting harm.</li>
</ul>
Any of the three characteristics (scale, scope, and irremediable character) can make an impact
severe.<br>
The contribution to the direct drivers, the proximity to ecologically sensitive areas, and the
changes to the state of biodiversity can make the severity and likelihood of an impact on
biodiversity greater. For example, when a site or supplier is in or near an ecologically sensitive
area, it can increase the likelihood of an impact on biodiversity. When a site or supplier is in or
near an ecosystem close to a tipping point, or where threatened species are present, it can
increase the severity of an impact on biodiversity, for example, because the impact would result
in irremediable harm.<br>
See section 1 in GRI 3: Material Topics 2021 for more guidance on assessing the significance
of impacts. For more guidance on how to identify biodiversity impacts, the organization can use
the following sources:
<ul style='list-style-type:disc;padding-left:40px'>
<li>
Aligning accounting approaches for nature (Align) Recommendations and implementation
guidance;</li>
<li> Natural Capital Protocol from the Natural Capital Coalition;</li>
<li> Science Based Targets Network (SBTN) Technical Guidance: Step 1: Assess;</li>
<li> The Locate and Evaluate phases of the TNFD LEAP approach.</li>
</ul>
<br>
<i>Methodologies</i>
Where possible, the organization should use primary data to identify its sites and products and
services in its supply chain with the most significant impacts on biodiversity (e.g., using data
collected through field or supplier surveys or derived from satellite imagery).<br>
The organization can use secondary or modeled data when primary data is unavailable (e.g.,
data layers on ecosystem extent and condition, life cycle impact assessments). For example,
the organization can use secondary data to identify and measure changes to the state of
biodiversity. In such a case, geospatial data layers can be overlaid with geographic location data
to reflect the size and condition of ecosystems or identify species that may be present at specific
sites. For example, the WWF Biodiversity Risk Filter
 provides information on the ecosystem
condition in different locations and the direct drivers most likely to be present and significant for
an organization’s or its suppliers’ activities.<br>
Secondary data may be appropriate to gain initial information about an organization’s impacts
on biodiversity across its sites and products and services in its supply chain. Once the sites and
products and services in its supply chain with the most significant impacts have been identified,
the organization may collect primary data for those sites and products and services in its supply
chain.<br>
The organization should use precise locations to assess the proximity to ecologically sensitive
areas and to assess the changes to the state of biodiversity.<br>

For products and services in its supply chain, the organization can use sourcing regions or
countries if it does not know the precise locations of its suppliers. The organization can also use
life cycle assessment tools, pressure or impact assessment tools, and global trade datasets to
make assumptions about likely locations, which are usually countries associated with its supply
chain (e.g., the soy used in its products is likely to come from the United States, Brazil, or
Argentina).<br>
The organization can use the data it has collected on the direct drivers, the proximity to
ecologically sensitive areas, and the changes to the state of biodiversity to identify its impacts on
biodiversity for reporting the information required under Disclosures 101-5 to 101-8.
  `,
  "101-5": `
  <div>
      <strong>Disclosure 101-5 Locations with biodiversity impacts</strong>
  </div>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
   The organization shall:<br>
<ul style='list-style-type:lower-alpha;padding-left:40px'>
<li>report the location and size in hectares of its sites with the most significant impacts on biodiversity;</li>
<li>
for each site reported under 101-5-a, report whether it is in or near an ecologically sensitive area, the distance to these areas, and whether these are:
<ul style='list-style-type:lower-roman;padding-left:40px'>
<li>areas of biodiversity importance;</li>
<li>areas of high ecosystem integrity;</li>
<li>areas of rapid decline in ecosystem integrity;</li>
<li>areas of high physical water risks;</li>
<li>areas important for the delivery of ecosystem service benefits to Indigenous Peoples, local communities, and other stakeholders;</li>
</ul>
</li>
<li>report the activities that take place in each site reported under 101-5-a;</li>
<li>
report the products and services in its supply chain with the most significant impacts on biodiversity and the countries or jurisdictions where the activities associated with these products and services take place.</li>
</ul>
</strong><br>
<hr>
  <div><strong>GUIDANCE</strong></div>
  This disclosure provides information about the locations of the organization’s sites with the
most significant impacts on biodiversity. It also provides information on the location of the
activities associated with the products and services in its supply chain with the most significant
impacts on biodiversity. The sites and products and services with the most significant impacts
on biodiversity are identified under Disclosure 101-4. These sites and products and services
are the focus of Disclosure 101-2 and Disclosures 101-5 to 101-8 of this Standard.<br>
If available, the organization can additionally report the information for entities downstream in its
value chain with the most significant impacts on biodiversity.<br>
For an example of how to present information on requirements in Disclosure 101-5, see Tables
3 and 4 in the Appendix.<br>
<b>Guidance to 101-5-a</b><br>
The organization should use polygon outlines or maps to report on the location of its sites with
the most significant impacts on biodiversity. A polygon is a geographic feature defined by a
series of grid references, points, or vertices connected to form an enclosed shape. If available,
the organization should also report the names and coordinates of its sites.<br>
Providing the coordinates for the sites of transport and fishing activities may not be possible. In
these cases, the organization can report departure and arrival locations and transport routes for
transport activities. For fishing activities, it can report FAO major fishing areas and subareas.<br>
<b>Guidance to 101-5-b</b><br>
The Taskforce on Nature-related Financial Disclosures (TNFD) defines ecologically sensitive
areas as areas of biodiversity importance, areas of high ecosystem integrity, areas of rapid
decline in ecosystem integrity, areas of high physical water risks, and areas important for the
delivery of ecosystem service benefits to Indigenous Peoples, local communities, and other
stakeholders.<br>
The organization can consult the criteria listed in Table 1 in the Appendix to identify ecologically
sensitive areas. An area is ecologically sensitive when it meets one or more criteria.
For more guidance and examples of tools to identify ecologically sensitive areas, see the TNFD
Guidance on the identification and assessment of nature-related issues: The LEAP approach
[41], pages 57-63.<br>
A site is in an ecologically sensitive area when it is completely or partially located in the
ecologically sensitive area. A site is near an ecologically sensitive area when the ecologically
sensitive area falls within the area affected or potentially affected (sometimes referred to as the
area of influence) or within the radius set by the organization. The organization can use a radius
if it cannot identify the area affected or potentially affected by its activities. If the organization uses
a radius, it should explain this and report the distance of the radius used.<br>
The organization is required to report the distance only in cases where the site is near an
ecologically sensitive area.<br>
The organization should report the size in hectares of the ecologically sensitive areas within its
sites.<br>
The organization can also report polygon outlines, or maps of the ecologically sensitive areas
and overlay them with the polygon outlines or maps of its sites.<br>
The organization can also report the percentage of sites in or near ecologically sensitive areas.
This information provides a high-level understanding of the significance of biodiversity across
the organization’s operations.<br>
The percentage of sites in or near ecologically sensitive areas is calculated using the following
formula:<br>
Percentage of
sites in or near
ecologically
sensitive areas = <br>
(Number of sites in or near ecologically sensitive areas/Total number of sites) x 100<br>

<b>Guidance to 101-5-b-i</b><br>
The organization should specify whether the areas of biodiversity importance are:
<ul style='list-style-type:disc;padding-left:40px'>
<li> protected through legal or other effective means;</li>
<li> scientifically recognized for their importance to biodiversity;</li>
<li> important for species;</li>
<li> important for ecosystems; or</li>
<li> important for ecological connectivity.</li>
</ul>

<b>Guidance to 101-5-d</b><br>
Where possible, the organization should also report the location within the country or jurisdiction
(e.g., state, city, Exclusive Economic Zone) or a precise location, such as polygon outlines or
maps. The organization can report departure and arrival locations and transport routes for
transport activities. For fishing activities, it can report FAO major fishing areas and subareas.<br>
For each product and service with the most significant impacts on biodiversity, the organization
should describe the level of traceability in place, for example, whether the product or service can
be traced to the national, regional, or local level, or a specific point of origin (e.g., farms). The
organization can also report the volume sourced or the amount spent on each product and
service.<br>
If available, the organization should also report the information on ecologically sensitive areas
required by 101-5-b for the products and services in its supply chain with the most significant
impacts on biodiversity.<br>
If the products in its supply chain are or contain high-impact commodities
, the organization can
report the quantity of each high-impact commodity sourced (e.g., tons of avocado) and the
proportion of total high-impact commodities sourced. This information provides a high-level
understanding of the significance of biodiversity across products in the organization’s supply
chain. The organization can use the SBTN High Impact Commodity List to identify whether it
sources products that are or contain high-impact commodities.<br>
The proportion of total high-impact commodities sourced is calculated using the following
formula:
Proportion of total
high-impact
commodities
sourced = <br>
(Quantity of high-impact commodity sourced/
Quantity of total high-impact commodities sourced)
  `,
  "101-6": `
    <div>
      <strong>Disclosure 101-6 Direct drivers of biodiversity loss</strong>
  </div><br>
  <div><strong>REQUIREMENTS</strong></div>
  <strong>The organization shall:<br>
<ul style='list-style-type:lower-alpha;padding-left:40px'>
<li>
for each site reported under 101-5-a where its activities lead or could lead to land and sea use change, report:
<ul style='list-style-type:lower-roman;padding-left:40px'>
<li>
the size in hectares of natural ecosystem converted since a cut-off or reference date, the cut-off date or reference date, and the type of ecosystem before and after conversion;</li>
<li>
the size in hectares of land and sea converted from one intensively used or modified ecosystem to another during the reporting period, and the type of ecosystem before and after conversion;</li>
</ul>
</li>
<li>
for each site reported under 101-5-a where its activities lead or could lead to the exploitation of natural resources, report:
<ul style='list-style-type:lower-roman;padding-left:40px'>
<li>
for each wild species harvested, the quantity, the type, and extinction risk;</li>
<li>
water withdrawal and water consumption in megaliters;</li>
</ul>
</li>
<li>
for each site reported under 101-5-a where its activities lead or could lead to pollution, report the quantity and the type of each pollutant generated;</li>
<li>
for each site reported under 101-5-a where its activities lead or could lead to the introduction of invasive alien species, describe how invasive alien species are or may be introduced;</li>
<li>
for each product and service in its supply chain reported under 101-5-d, report the information required under 101-6-a, 101-6-b, 101-6-c, and 101-6-d, with a breakdown by country or jurisdiction;</li>
<li>
report contextual information necessary to understand how the data has been compiled, including standards, methodologies, and assumptions used.</li>
</ul>

</strong>
<br><hr>
  <div><strong>GUIDANCE</strong></div>
  This disclosure provides an understanding of the direct drivers of biodiversity loss (hereafter the
direct drivers) leading to the most significant impacts. The organization should additionally
report the information on the direct drivers for its downstream value chain.<br>
According to the Intergovernmental Science-Policy Platform on Biodiversity and Ecosystem
Services (IPBES), direct drivers are the drivers that ‘unequivocally influence biodiversity and
ecosystem processes’. Direct drivers are sometimes referred to as ‘pressures’ or ‘impact
drivers’. The IPBES global assessment has identified land and sea use change and the
exploitation of natural resources as the main direct drivers, followed by climate change,
pollution, and the introduction of invasive alien species. These direct drivers can also lead to the
fragmentation and degradation of ecosystems. See Box 1 for more information on direct drivers
of biodiversity loss.<br>
Information on the direct drivers informs decisions on prioritizing actions to manage biodiversityrelated impacts by applying the mitigation hierarchy. See Disclosure 101-2 for more information
on the mitigation hierarchy. The organization’s actions to mitigate direct drivers are reported
under Disclosure 101-2.<br>
Through its activities, an organization can use natural resources for its production processes
(e.g., sand used in a construction project) or produce non-product outputs (e.g., pollutants or
greenhouse gas emissions). These activities, responsible for the direct drivers of biodiversity
loss, can have negative impacts on biodiversity.<br>
The organization needs to report only the information for the direct drivers relevant to its activities
and its supply chain. Direct drivers can vary by location. For example, an organization has
activities in site A that lead to pollution. It also has activities in site B that lead to the exploitation
of natural resources. In this case, the organization only needs to report the information on
pollution for site A and on the exploitation of natural resources for site B.

<b>Direct drivers of biodiversity loss</b><br>
<b>Land and sea use change</b><br>
Land and sea use change refers to how humans use and manage land and seascapes,
which may lead to a change in land and sea cover. These are changes to terrestrial and
aquatic ecosystems, including freshwater and marine ecosystems. Examples of changes in
the use of freshwater ecosystems are the construction of a hydropower dam in a river or the
drainage of a wetland for urban settlements. Land and sea use change results from the
conversion of natural, intensively used, or other modified ecosystems into another
ecosystem.<br>
<b>Exploitation of natural resources</b><br>
The exploitation of natural resources encompasses the harvest of wild organisms (animal,
fungi, and plant species) and the exploitation of water.<br>
The exploitation of wild species can lead to their extinction. Some of the most exploited
species include marine fish, invertebrates, and trees. Various species are hunted for
bushmeat and harvested for use in the medicinal or pet trade. Unsustainable water use can
lead to loss, fragmentation, and degradation of species habitats, reduce the availability of food
and water for species, as well as disrupt the functioning of ecosystems.<br>
<b>Climate change</b><br>
Climate change is a direct driver as it alters species’ distribution, functioning, and
interactions, reducing ecosystems’ capacity to adapt. Climate change leads to changes in
temperatures and weather patterns that, in turn, can affect species (e.g., by reducing habitats
and food supply, and altering migration patterns and breeding seasons). Sea level rise and
ocean acidification also negatively affect marine organisms.<br>
The greenhouse gas (GHG) emissions from a particular site may not lead to biodiversity loss
in the direct vicinity of the site but contribute to climate change that drives biodiversity loss.
Therefore, an organization’s GHG emissions and those from other organizations contribute to
climate change as a global direct driver of biodiversity loss.<br>
This disclosure does not require information on climate change. An organization’s GHG
emissions can be reported under Disclosures 305-1, 305-2, and 305-3 in GRI 305:
Emissions 2016.<br>
<b>Pollution</b><br>
Air, water, and soil pollutants include substances (e.g., heavy metals, pesticides, solid waste)
and other pollutants such as heat, light, noise, or vibrations.<br>
Emission of pollutants can affect ecosystems and species. The toxicity and persistence of
some pollutants can affect species’ health (e.g., with immune, reproductive, neurotoxic, or
carcinogenic effects). Pesticides and insecticides lead to the decline of pollinators and other
species. Waste not properly disposed of can lead to leaks of hazardous substances into the
environment, while plastic litter accumulates in soil and affects marine species through
entanglement and ingestion. Light and noise can disrupt wildlife species’ breeding or
migration behavior, resulting in a population decline.<br>
<b>Invasive alien species</b><br>
Invasive alien species are animals, plants, and other organisms that are introduced,
accidentally or deliberately by humans, to an area outside of their natural geographical range
and cause negative impacts on local biodiversity. Invasive alien species negatively affect
biodiversity as they often lack predators in their new environment, allowing them to spread,
become established and abundant. They can carry diseases, outcompete or prey on native
species, alter food chains, and change ecosystems by, for example, altering soil composition
or creating habitats that are vulnerable to wildfires. These impacts can lead to the extinction of
species.<br>

<b>Guidance to 101-6-a</b><br>
The organization should report which ecosystem classification it uses to identify the types of
ecosystems converted. The organization can report ecosystem types using the biomes or
ecosystem functional groups in the IUCN Global Ecosystem Typology. Alternatively, the
organization can report according to another global classification, national classification, or
register. If the organization cannot use ecosystem classifications, it can utilize land use
classifications (e.g., Globio land use categories).<br>
<b>Guidance to 101-6-a-i</b><br>
The Accountability Framework initiative defines a natural ecosystem as an ecosystem that
substantially resembles – in terms of species composition, structure, and ecological function –
one that is or would be found in a given area without major human impacts. It includes humanmanaged ecosystems where much of the natural species composition, structure, and
ecological function are present. Natural ecosystem conversion is the human-induced change of
a natural ecosystem to another use or profound change in an ecosystem's species
composition, structure, or function. It can include severe degradation or introducing
management practices that lead to substantial and sustained change in the ecosystem's former
species composition, structure, or function.<br>
Natural ecosystem conversion is measured from a cut-off date7 associated with an
organization’s policy related to natural ecosystem conversion (e.g., deforestation-free policy). If
the organization does not have such a policy in place, it should select a reference date to
measure natural ecosystem conversion. For instance, if 2015 has been set as a cut-off date or
reference date, the organization reports the size of the ecosystem converted from 2015 until the
reporting period. Common cut-off dates apply to organizations operating in the same or similar
context. They support the monitoring, verification, and management of natural ecosystem
conversion, including in supply chains. Cut-off dates can, therefore, be selected based on
sector-wide or regional cut-off dates (e.g., the 2008 cut-off date from the Brazil Soy Moratorium)
or those specified in certification programs (e.g., Forest Stewardship Council), legislation (e.g.,
EU regulation on deforestation-free products), or voluntary initiatives (e.g., Science Based
Targets for Nature). Cut-off dates may differ between commodities and regions. More guidance
can be found in the Accountability Framework initiative Operational Guidance on Cutoff Dates [6].
The organization should explain why it has determined the cut-off or reference dates as
appropriate.<br>

<b>Guidance to 101-6-a-ii</b><br>
Intensively used and other modified ecosystems are ecosystems where human activity has
substantially modified an area’s primary ecological functions and species composition to
ecosystems dominated by agriculture, urban, and other industrial activities. Intensively used
ecosystems are those covered by the intensive land-use systems biome (T7) in the IUCN
Global Ecosystem Typology. Other modified ecosystems include anthropogenic subterranean
freshwaters (SF2), artificial freshwaters (F3), anthropogenic marine systems (M4), and
anthropogenic shorelines (MT3).<br>

<b>Guidance to 101-6-b-i</b><br>
Harvesting wild species involves gathering, catching, or hunting wild organisms (animal, fungi,
and plant species) by the organization, including those incidentally taken.<br>
 
The organization can report if the species are listed in one of the CITES Appendices. It can also
report if the species are harvested from ecologically sensitive areas (e.g., from a Key Biodiversity
Area, which aims to protect or conserve the harvested species).<br>
To report on the extinction risk of a species, the organization can use information from the IUCN
Red List of Threatened Species.<br>
<b>Guidance to 101-6-b-ii</b><br>
The organization should use information reported under Disclosures 303-3 Water withdrawal
and 303-5 Water consumption in GRI 303: Water and Effluents 20188
to report water withdrawal
and water consumption for each site.<br>

<b>Guidance to 101-6-c</b><br>
The organization is only required to report the type and quantity of pollutants that lead or could
lead to the most significant impacts on biodiversity.<br>
To report on air pollution, the organization should use, where relevant, information reported
under Disclosure 305-7 Nitrogen oxides (NOx), sulfur oxides (SOx), and other significant air
emissions in GRI 305: Emissions 2016 for:
<ul style='list-style-type:disc;padding-left:40px'>
<li> NOx</li>
<li> SOx</li>
<li> Persistent organic pollutants (POP);</li>
<li> Volatile organic compounds (VOCs);</li>
<li> Hazardous air pollutants (HAP);</li>
<li> Particulate matter (PM);</li>
<li> Other standard categories of air emissions from relevant regulations.</li>
</ul><br>
To report on water and soil pollution, the organization should use, where relevant, information
reported under:
<ul style='list-style-type:disc;padding-left:40px'>
<li>
 Disclosure 303-4 Water discharge in GRI 303: Water and Effluents 2018 to have information
on priority substances of concern that may cause water pollution (e.g., those leading to
eutrophication).</li>
<li>
 Disclosure 306-3 Significant spills in GRI 306: Effluents and Waste 2016.
</li>
</ul><br>
For heat, light, noise, or vibration pollution, the organization should report instances that do not
comply with legal requirements for permitted pollution levels.<br>
<b>Guidance to 101-6-d</b><br>
Non-invasive alien species are not required to be reported under 101-6-d.<br>
Invasive alien species can be introduced accidentally (e.g., transport, discharge of ballast
waters) or on purpose (e.g., for pest control, horticulture, pets, zoological gardens, and aquaria).
The organization should report the species that are or may be introduced. For example, an
organization imports ornamental plants to new areas, which may threaten local biodiversity. A
maritime shipping organization may introduce shellfish, crustaceans, and other species to new
areas through contaminated ballast water or encrusted organisms on ships. It can also
inadvertently introduce other species, such as insects and rodents, through the transport of
goods.<br>
National regulations define which species are considered invasive alien species in a particular
country. The Global Invasive Species Database and Global Register of Introduced and Invasive
Species also provide information on invasive alien species.<br>
The organization can also describe how those species affect or may affect surrounding species and
ecosystems.<br>

<b>Guidance to 101-6-e</b><br>
It may not be feasible for the organization to obtain primary data on direct drivers from suppliers.
In such a case, the organization can estimate the direct drivers using multi-regional input-output
models and lifecycle impact assessments in combination with data on the volume or amount
spent on products and services. Multi-regional input-output models can provide estimates of the
environmental inputs (e.g., water use) and outputs (e.g., air emissions) associated with the
products and services in its supply chain. See Aligning accounting approaches for nature (Align)
Measuring and valuing biodiversity across supply chains [47] for more information on the
methodologies and data to measure direct drivers in supply chains.<br>
If the organization cannot report the size of the natural ecosystem converted for the products in
its supply chain, it can report, for each product, the percentage of sourced volume determined to
be deforestation- or conversion-free and describe the assessment methods used. For example,
out of 100 tons of soy sourced, an organization has determined that 90% is deforestation-free.
Assessment methods can include monitoring, certification, sourcing from low-risk jurisdictions
with no or negligible recent conversion, or sourcing from verified suppliers. To be deemed
conversion- or deforestation-free, products must be assessed as not causing or contributing to
natural ecosystem conversion, including deforestation, after an appropriate cut-off date.<br>

<b>Guidance to 101-6-f</b><br>
The organization should use primary data to report information on the direct drivers where
possible. When primary data is unavailable, the organization can use secondary or modeled
data (e.g., lifecycle impact assessments). However, such data are less accurate and may not
reflect the effectiveness of actions to manage the organization’s impacts.<br>
The organization should explain which information draws on primary, secondary, or modeled
data, as well as any limitations of methodologies and data used. When reporting secondary or
modeled data, the organization should report which datasets it used and if it plans to improve
the accuracy of data.
  `,
  "101-7": `
    <div>
      <strong>Disclosure 101-7 Changes to the state of biodiversity</strong>
  </div><br>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The organization shall:<br>
<ul style='list-style-type:lower-alpha;padding-left:40px'>
<li>for each site reported under 101-5-a, report the following information on affected or potentially affected ecosystems:
<ul style='list-style-type:lower-roman;padding-left:40px'>
<li>the ecosystem type for the base year;</li>
<li>the ecosystem size in hectares for the base year;</li>
<li>the ecosystem condition for the base year and the current reporting period;</li>
</ul>
</li>
<li>report contextual information necessary to understand how the data has been compiled, including standards, methodologies, and assumptions used.</li>
</ul>
</strong><br><hr>

  <div><strong>GUIDANCE</strong></div>

  This disclosure provides information about the changes in the condition of the ecosystem
affected or potentially affected by the organization.<br>
The state of biodiversity is the holistic quality of the components of biodiversity (genes, species,
and ecosystems), and is a function of the condition and size of its components. This disclosure
focuses on the condition and size of affected ecosystems. By reporting this information for the
base year and the current reporting period, the organization provides information about the
ecosystem's overall health over time.<br>
Changes in the state of biodiversity may reflect the cumulative impacts of the organization's
activities and the activities of others, such as governments, local communities, or other
organizations. It is not always possible to determine how much of the change in the state of
biodiversity is due to the activities of the organization or others. However, the information
reported under this disclosure, together with Disclosure 101-6, helps to understand the
organization’s actual and potential impacts on biodiversity and can inform the management of
these impacts.<br>
The organization should report information on changes to the state of biodiversity for each
product and service reported under 101-5-d by country or jurisdiction. It should also report this
information for its downstream value chain.<br>
The organization can organize the information on the state of biodiversity into structured
biodiversity accounts. Biodiversity accounts enable more accurate monitoring of gains and
losses of biodiversity over time. A core component of biodiversity accounts is the compilation of
an asset inventory for each ecosystem type so that gains in one type do not compensate for
losses in another. They are also useful in tracking progress against targets to halt and reverse
biodiversity loss. See the Endangered Wildlife Trust Biological Diversity Protocol [16] for more
information on biodiversity accounts.<br>
For an example of how to present information on requirements in Disclosure 101-7, see Table 3
in the Appendix.<br>

<b>Guidance to 101-7-a</b><br>
When reporting information on the affected or potentially affected ecosystems, the organization
should consider all ecosystem types in the area that is or could be affected by its activities,
including beyond its sites, if relevant. The state of the overall ecosystem, which extends beyond
the areas affected by the organization, is not required for reporting. For example, an organization
that owns a soy plantation in the Amazon is required to report information on the type, size, and
condition of the affected part of the ecosystem rather than reporting on the entire Amazon
region.<br>
The base year is when the organization collects baseline information on the ecosystem type,
size, and condition. The base year may be the start of an organization’s activities, the date from
which it owned, leased, or managed a particular site, or when it committed to halt and reverse
biodiversity loss.<br>
Baseline information can be collected through environmental impact assessments, which
provide information on the condition of and trends in biodiversity in a particular area before an
organization’s activities start. For more information on best practices to conduct baseline
studies.<br>
The organization should report the base year. It should report how it has determined the base
year and baseline information under 101-7-b.<br>
The size and condition of an affected ecosystem can be combined into one unit: conditionadjusted area. This is the size of the ecosystem adjusted for its condition, and the unit (e.g.,
equivalent hectares) represents the residual condition within that area. The organization can
also report impacts on affected ecosystems using condition-adjusted hectares. See Align
Measuring Ecosystem Condition – A primer for business [50] and the Endangered Wildlife Trust
Biological Diversity Protocol [16] for more information on condition-adjusted areas.<br>

<b>Guidance to 101-7-a-i</b><br>
The organization should report which ecosystem classification it uses to identify the types of
ecosystems. The organization can report ecosystem types using the biomes or ecosystem
functional groups in the IUCN Global Ecosystem Typology. Alternatively, the organization can
report according to another global classification, national classification, or register. If the
organization cannot use ecosystem classifications, it can use land use classifications (e.g.,
Globio land use categories) instead.<br>

<b>Guidance to 101-7-a-ii</b><br>
Ecosystem size, also referred to as ecosystem extent, is the area coverage of the ecosystem
that is affected or potentially affected by the organization’s activities. This is a fixed area over
which the condition of the ecosystem is measured over time.<br>

<b>Guidance to 101-7-a-iii</b><br>
Ecosystem condition is the quality of an ecosystem measured by its living and non-living
characteristics against a reference condition.10 Living and non-living characteristics include:
<ul style='list-style-type:disc;padding-left:40px'>
<li> the ecosystem’s composition, function, and structure;</li>
<li> the landscape characteristics (e.g., connectivity);</li>
<li> the physical and chemical state characteristics (e.g., soil structure and soil nutrient levels).</li>
</ul>
Ecosystem condition can also provide information on the ecosystem’s capacity to supply
ecosystem services now and in the future.<br>
The activities of an organization may degrade the condition of affected ecosystems through the
direct drivers of biodiversity loss (hereafter direct drivers). For example, the emission of
pollutants, partial deforestation, or water withdrawal in an area with water stress, may affect the
ecosystem's structure, composition, or function. If land and sea use change is the primary direct
driver of biodiversity loss, the activities of an organization lead to the conversion of an ecosystem
into a different type of ecosystem. In this case, the conversion of the ecosystem results in a
complete reduction in ecosystem condition.<br>
Methods to measure ecosystem condition should reflect the relevant characteristics of the
ecosystem. The organization can use methods that directly measure characteristics or estimate
ecosystem conditions based on direct drivers. These methods can be specific to certain types of
ecosystems (e.g., types of wetlands or forests) or applicable to different ecosystem types (i.e.,
applicable across terrestrial, freshwater, or marine realms). See Align Measuring
Ecosystem Condition – A primer for business and Taskforce on Nature-related Financial
Disclosures (TNFD) Guidance on the identification and assessment of nature-related issues: The
LEAP approach [41] for more information on how to measure ecosystem condition.<br>
If the organization monitors the condition of affected or potentially affected ecosystems at a
frequency different from its sustainability reporting frequency, it should report the most recent
information and does not need additional measurement to meet the requirement.<br>

<b>Guidance to 101-7-b</b><br>
The organization should use primary data to report on direct drivers where possible (e.g., data
collected through field surveys, eDNA, or derived from satellite imagery).<br>
When primary data is unavailable, the organization can use secondary or modeled data (e.g.,
data layers on ecosystem extent and condition, life cycle impact assessments). Modeled data
are issued from models that quantify how the different direct drivers affect the state of
biodiversity. These models use globally collected data, and the results are applied locally to
estimate how the organization’s activities can lead to ecosystem changes. They include
geospatial data layers that can be used to identify changes in the size and condition of
ecosystems, such as the level of habitat fragmentation and connectivity, or identify species that
may be present at specific sites.<br>
The organization should explain which information draws on primary, secondary, or modeled
data, as well as any limitations of methodologies and data used. When reporting secondary or
modeled data, the organization should report which datasets it has used and if it plans to
improve the accuracy of data.<br>
<b>Guidance to 101-7</b><br>
The organization should additionally report information on affected or potentially affected
species for the sites reported under 101-5-a. The organization can report the species, its
extinction risk, and population size for the baseline and current reporting period.<br>
The extinction risk measures the threat status of a species and how an organization’s activities
may affect the threat status. The global, regional, and national IUCN Red Lists can be used to
determine the species extinction risk (i.e., Critically Endangered, Endangered, Vulnerable, Near
Threatened, and Least Concern). Change in the available species habitat can potentially be
used as a proxy to measure impact on local or global extinction risk, noting that other factors can
drive extinction risk (e.g., hunting, climate change).<br>
Population size measures the number of individuals of a species within an area. It can be
measured by the number of mature individuals or the number of breeding pairs. When the
population size is unavailable, trends in relative population abundance or in species area of
habitat can be used as a proxy.<br>
The organization can report information for the following species:<br>
<ul style='list-style-type:disc;padding-left:40px'>
<li> Species whose local or overall populations have or could be changed significantly.</li>
<li> Species that are legally protected by local, national, or international laws and conventions
(e.g., species listed in one of the CITES Appendices).</li>
<li> Species that are recognized as a priority species at the local, national, or international level
(e.g., species listed as threatened on the international IUCN Red List or species that trigger
a Key Biodiversity Area designation)</li>
<li> Species that have a critical role in the ecosystem (e.g., keystone species).</li>
<li> Species that have a significant cultural or economic role for stakeholders (e.g., hunting,
harvesting, pollination).</li>
</ul>
  `,
  "101-8": `
    <div>
      <strong>Disclosure 101-8 Ecosystem services</strong>
  </div><br>
  <div><strong>REQUIREMENTS</strong></div>
  <strong>
  The organization shall:<br>
<ul style='list-style-type:lower-alpha;padding-left:40px'>
<li>for each site reported under 101-5-a, list the ecosystem services and beneficiaries affected or potentially affected by the organization’s activities;</li>
<li>explain how the ecosystem services and beneficiaries are or could be affected by the organization's activities.</li>
  </ul>
  </strong><br><hr>
  <div><strong>GUIDANCE</strong></div>
  Ecosystem services occur through an ecosystem's normal functioning and can fall into one or
more of the following categories:<br>
<ul style='list-style-type:disc;padding-left:40px'>
<li> provisioning services;</li>
<li> regulating and maintenance services;</li>
<li> cultural services.</li>
</ul><br>
Provisioning services contribute to benefits extracted or harvested from ecosystems. Examples
are timber in a forest, freshwater from a river, and subsistence hunting. Regulating and
maintenance services result from the ability of ecosystems to regulate biological processes and
influence climate, hydrological, and biochemical cycles, thereby maintaining environmental
conditions beneficial to people. An example is the role of forests in preventing soil erosion.
Cultural services are the non-material benefits people (beneficiaries) obtain from ecosystems
through spiritual enrichment, cognitive development, reflection, recreation, and aesthetic
experiences. Examples are the recreational value of a forest and the cultural importance of a
heritage landscape for a local community.<br>
Biodiversity plays an important role in maintaining the quality, quantity, and resilience of
ecosystem service flows, and it provides ecosystem services that beneficiaries rely upon now
and into the future. The diversity of genes, species, and ecosystems provides a greater range of
ecosystem service and higher overall quantity, quality, and resilience of ecosystem services and
improves the capacity of ecosystems to function effectively. A change in the state of biodiversity
can lead to changes in ecosystem services. This, in turn, can have an impact on the
beneficiaries of these ecosystem services. For example, a decline in the number of bees
caused by the organization’s activities can lead to decreased pollination services. If the crops
are not properly pollinated by the bees, the quality and quantity of the crops produced may be
affected, reducing the available food for the local community that grows the crops.<br>
This disclosure gives insight into the ecosystem services and beneficiaries affected or
potentially affected by the organization’s activities. The organization should also list the
ecosystem services and beneficiaries affected or potentially affected by its suppliers’ activities
for each country or jurisdiction reported under 101-5-d and those affected or potentially affected
by the activities of its downstream entities.<br>
For an example of how to present information on requirements in Disclosure 101-8, see Table 3
in the Appendix.<br>

<b>Guidance to 101-8-a</b><br>
Beneficiaries can include Indigenous Peoples, local communities, and other organizations. The
reporting organization can also be one of the beneficiaries. The organization can report the
number of beneficiaries when disclosing information for this requirement (e.g., 50 farmers
located in the area).<br>
The organization should describe the approach used to identify the ecosystem services reported
under 101-8-a. The organization can explain if it engages with stakeholders to identify the
ecosystem services and beneficiaries affected. It can also use the following methodologies and
tools to identify ecosystem services:
<ul style='list-style-type:disc;padding-left:40px'>
<li> the ENCORE tool;</li>
<li>the Natural Capital Protocol from the Natural Capital Coalition;</li>
<li> the Taskforce on Nature-related Financial Disclosures (TNFD) LEAP approach, which draws
on the UN SEEA Ecosystem Accounting;</li>
<li> the World Resources Institute (WRI) Corporate Ecosystem Services Review.</li>
</ul>

<b>Guidance to 101-8-b</b><br>
The organization’s activities may lead to an increase or decrease in the quality and quantity of
ecosystem services. For example, the organization can explain that cutting trees in the forest
has decreased food provisioning services, which has a negative impact on the local community
that needs to find an alternative food source. In another example, the organization can explain
that switching to agroforestry has resulted in an increase in soil erosion control services, which
has a positive impact on the local community that will face fewer risks from flooding.
  `,
  "201-1": `
    <div>
      <strong>Disclosure 201-1 Direct economic value generated and distributed</strong>
  </div>
  <div><strong>REQUIREMENTS</strong></div><br>
  <strong>
  The reporting organization shall report the following information:<br>
  <ul style='list-style-type:lower-alpha;padding-left:40px'>
<li>
Direct economic value generated and distributed (EVG&D) on an accruals basis,
including the basic components for the organization’s global operations as listed below.
If data are presented on a cash basis, report the justification for this decision in addition
to reporting the following basic components:
<ul style='list-style-type:lower-roman;padding-left:40px'>
<li>Direct economic value generated: revenues;</li>
<li>Economic value distributed: operating costs, employee wages and benefits,
payments to providers of capital, payments to government by country, and
community investments;</li>
<li>Economic value retained: ‘direct economic value generated’ less ‘economic value
distributed’.</li>
</ul>
</li>
<li>
Where significant, report EVG&D separately at country, regional, or market levels, and
the criteria used for defining significance.
</li>
</ul>
Compilation requirements<br>
2.1 When compiling the information specified in Disclosure 201-1, the reporting
organization shall, if applicable, compile the EVG&D from data in the organization’s
audited financial or profit and loss (P&L) statement, or its internally audited
management accounts.<br>
</strong><hr>
<div><strong>GUIDANCE</strong></div>
<strong>Background</strong><br>
Information on the creation and distribution of economic value provides a basic indication of how
an organization has created wealth for stakeholders. Several components of the economic value
generated and distributed (EVG&D) also provide an economic profile of an organization, which
can be useful for normalizing other performance figures.<br>
If presented in country-level detail, EVG&D can provide a useful picture of the direct monetary
value added to local economies.<br>
<b>Guidance for Disclosure 201-1</b><br>
<i>Revenues</i>
An organization can calculate revenues as net sales plus revenues from financial investments
and sales of assets.<br>
Net sales can be calculated as gross sales from products and services minus returns,
discounts, and allowances.<br>
Revenues from financial investments can include cash received as:
<ul style='list-style-type:disc;padding-left:40px'>
<li>
interest on financial loans;</li>
<li>
dividends from shareholdings;</li>
<li>
royalties;</li>
<li>
direct income generated from assets, such as property rental.</li>
</ul>
Revenues from sale of assets can include:
<ul style='list-style-type:disc'>
<li>
physical assets, such as property, infrastructure, and equipment;</li>
<li>
intangibles, such as intellectual property rights, designs, and brand names.</li>
</ul>
<br>
<i>Operating costs</i>
An organization can calculate operating costs as cash payments made outside the organization
for materials, product components, facilities, and services purchased.<br>
Services purchased can include payments to self-employed persons, temporary placement
agencies and other organizations providing services. Costs related to workers who are not
employees working in an operational role are included as part of services purchased, rather
than under employee wages and benefits.<br><br>
Operating costs can include:
<ul style='list-style-type:disc;padding-left:40px'>
<li>property rental;</li>
<li>license fees;</li>
<li>facilitation payments (since these have a clear commercial objective);</li>
<li>royalties;</li>
<li>payments for contract workers;</li>
<li>training costs, if outside trainers are used;</li>
<li>personal protective clothing.</li>
</ul><br>

The use of facilitation payments is also addressed in GRI 205: Anti-corruption 2016.<br>
<i>Employee wages and benefits</i>
An organization can calculate employee wages and benefits as total payroll (including employee
salaries and amounts paid to government institutions on behalf of employees) plus total
benefits (excluding training, costs of protective equipment or other cost items directly related to
the employee’s job function).<br><br>
Amounts paid to government institutions on behalf of employees can include employee taxes,
levies, and unemployment funds.<br>

Total benefits can include:
<ul style='list-style-type:disc;padding-left:20px'>
<li>
regular contributions, such as to pensions, insurance, company vehicles, and private health;
</li>
<li>
other employee support, such as housing, interest-free loans, public transport assistance,
educational grants, and redundancy payments.</li>
</ul>
<br>
<i>Payments to providers of capital</i>
An organization can calculate payments to providers of capital as dividends to all shareholders,
plus interest payments made to providers of loans.<br><br>
Interest payments made to providers of loans can include:
<ul style='list-style-type:disc;padding-left:20px'>
<li>
interest on all forms of debt and borrowings (not only long-term debt);</li>
<li>
arrears of dividends due to preferred shareholders.</li>
</ul><br><br>
<i>Payments to government</i>
An organization can calculate payments to governments as all of the organization’s taxes plus
related penalties paid at the international, national, and local levels. Organization taxes can
include corporate, income, and property.<br><br>
Payments to government exclude deferred taxes, because they may not be paid.<br>
If operating in more than one country, the organization can report taxes paid by country, including
the definition of segmentation used.<br><br>
<i>Community investments</i><br>
Total community investments refers to actual expenditures in the reporting period, not
commitments. An organization can calculate community investments as voluntary donations
plus investment of funds in the broader community where the target beneficiaries are external to
the organization. Voluntary donations and investment of funds in the broader community where
the target beneficiaries are external to the organization can include:<br>
<ul style='list-style-type:disc;padding-left:20px'>
<li>
contributions to charities, NGOs and research institutes (unrelated to the organization’s
commercial research and development);</li>
<li>
funds to support community infrastructure, such as recreational facilities;</li>
<li>
direct costs of social programs, including arts and educational events.</li>
</ul><br>

If reporting infrastructure investments, an organization can include costs of goods and labor, in
addition to capital costs, as well as operating costs for support of ongoing facilities or programs.
An example of support for ongoing facilities or programs can include the organization funding
the daily operations of a public facility.<br><br>
Community investments exclude legal and commercial activities or where the purpose of the
investment is exclusively commercial (donations to political parties can be included, but are also
addressed separately in more detail in GRI 415: Public Policy 2016).<br>
Community investments also exclude any infrastructure investment that is driven primarily by
core business needs, or to facilitate the business operations of an organization. Infrastructure
investments driven primarily by core business needs can include, for example, building a road
to a mine or a factory. The calculation of investment can include infrastructure built outside the
main business activities of the organization, such as a school or hospital for workers and their
families.<br>
  `,
  "201-2": `
    <div>
      <strong>Disclosure 201-2 Financial implications and other risks and opportunities due to climate change</strong>
  </div>
  <div><strong>REQUIREMENTS</strong></div> <strong>
The reporting organization shall report the following information:
<ul style='list-style-type:lower-alpha;padding-left:20px'>
<li>Risks and opportunities posed by climate change that have the potential to generate
substantive changes in operations, revenue, or expenditure, including:
<ul style='list-style-type:lower-roman;padding-left:20px'>
<li>a description of the risk or opportunity and its classification as either physical,
regulatory, or other;</li>
<li>a description of the impact associated with the risk or opportunity;</li>
<li>the financial implications of the risk or opportunity before action is taken;</li>
<li>the methods used to manage the risk or opportunity;</li>
<li>the costs of actions taken to manage the risk or opportunity.</li>
</ul>
</li>
</ul><br>
Compilation requirements<br>
2.2 When compiling the information specified in Disclosure 201-2, if the reporting
organization does not have a system in place to calculate the financial implications or
costs, or to make revenue projections, it shall report its plans and timeline to develop
the necessary systems.<br>
</strong>
  <div><strong>GUIDANCE</strong></div>
  Risk and opportunities due to climate change can be classified as:<br>
<ul style='list-style-type:disc;padding-left:20px'>
<li>physical</li>
<li>regulatory</li>
<li>other</li>
</ul>
Physical risks and opportunities can include:<br>
<ul style='list-style-type:disc;padding-left:20px'>
<li>the impact of more frequent and intense storms;</li>
<li>changes in sea level, ambient temperature, and water availability;</li>
<li>impacts on workers – such as health effects, including heat-related illness or disease, and
the need to relocate operations.</li>
</ul>
Other risks and opportunities can include the availability of new technologies, products, or
services to address challenges related to climate change, as well as changes in customer
behavior.<br>
Methods used to manage the risk or opportunity can include:<br>
<ul style='list-style-type:disc;padding-left:20px'>
<li>carbon capture and storage;</li>
<li>fuel switching;</li>
<li>use of renewable and lower carbon footprint energy;</li>
<li>improving energy efficiency;</li>
<li>flaring, venting, and fugitive emission reduction;</li>
<li>renewable energy certificates;</li>
<li>use of carbon offsets.</li>
</ul><br>
<strong>Background</strong><br>
Climate change presents risks and opportunities to organizations, their investors, and their
other stakeholders.<br>
As governments move to regulate activities that contribute to climate change, organizations that
are directly or indirectly responsible for emissions face regulatory risks and opportunities. Risks
can include increased costs or other factors impacting competitiveness. However, limits on
greenhouse gas (GHG) emissions can also create opportunities for organizations as new
technologies and markets are created. This is especially the case for organizations that can use
or produce energy and energy-efficient products more effectively.<br>
  `,
  "201-3": `
    <div>
      <strong>Disclosure 201-3 Defined benefit plan obligations and other retirement plans</strong>
  </div>
  <div><strong>REQUIREMENTS</strong></div>
<strong>
The reporting organization shall report the following information:<br>
<ul style='list-style-type:lower-alpha;padding-left:20px'>
<li>If the plan’s liabilities are met by the organization’s general resources, the estimated
value of those liabilities.</li>
<li>
If a separate fund exists to pay the plan’s pension liabilities:
<ul style='list-style-type:lower-roman;padding-left:20px'>
<li>the extent to which the scheme’s liabilities are estimated to be covered by the
assets that have been set aside to meet them;</li>
<li>the basis on which that estimate has been arrived at;</li>
<li>when that estimate was made.</li>
</ul>
</li>
<li>
If a fund set up to pay the plan’s pension liabilities is not fully covered, explain the
strategy, if any, adopted by the employer to work towards full coverage, and the
timescale, if any, by which the employer hopes to achieve full coverage.
Percentage of salary contributed by employee or employer.</li>
<li>
Level of participation in retirement plans, such as participation in mandatory or
voluntary schemes, regional, or country-based schemes, or those with financial impact.</li>
</ul>
</strong><br><hr>
  <div><strong>RECOMMENDATIONS</strong></div><br>
2.4  When compiling the information specified in Disclosure 201-3, the reporting organization
should:<br>
<ul style ='list-style-type:none;padding-left:20px'>
<li>2.4.1 calculate the information in accordance with the regulations and methods for
relevant jurisdictions, and report aggregated totals;</li>
<li>2.4.2 use the same consolidation techniques as those applied in preparing the
financial accounts of the organization.</li>
</ul>
<br>
  <div><strong>GUIDANCE</strong></div><br>
The structure of retirement plans offered to employees can be based on:
<ul style='list-style-type:disc;padding-left:20px'>
<li>
defined benefit plans;</li>
<li>
defined contribution plans;
</li>
<li>other types of retirement benefits.</li>
</ul>
Different jurisdictions, such as countries, have varying interpretations and guidance regarding
calculations used to determine plan coverage.<br>
Note that benefit pension plans are part of the International Accounting Standards Board (IASB)
IAS 19 Employee Benefits, however IAS 19 covers additional topics.<br>
<strong>Background</strong><br>
When an organization provides a retirement plan for its employees, these benefits can become
a commitment that members of the schemes plan on for their long-term economic well-being.<br>
Defined benefit plans have potential implications for employers in terms of the obligations that
need to be met. Other types of plans, such as defined contribution plans, do not guarantee
access to a retirement plan or the quality of the benefits. Thus, the type of plan chosen has
implications for both employees and employers. Conversely, a properly funded pension plan
can help to attract and maintain employees and support long-term financial and strategic
planning on the part of the employer.
  `,
  "201-4": `
    <div>
      <strong>Disclosure 201-4 Financial assistance received from government</strong>
  </div>
  <div><strong>REQUIREMENTS</strong></div>
  The reporting organization shall report the following information:<br>
<ul style='list-style-type:lower-alpha'>
<li>
Total monetary value of financial assistance received by the organization from any
government during the reporting period, including:
    <ul style='list-style-type:lower-roman;padding-left:20px'>
        <li>tax relief and tax credits;</li>
        <li>subsidies;</li>
        <li>investment grants, research and development grants, and other relevant types of
        grant;</li>
        <li>awards;</li>
        <li>royalty holidays;</li>
        <li>financial assistance from Export Credit Agencies (ECAs);</li>
        <li>financial incentives;</li>
        <li>other financial benefits received or receivable from any government for any
        operation.
        </li>
    </ul>
</li>
<li>
The information in 201-4-a by country.</li>
<li>
Whether, and the extent to which, any government is present in the shareholding
structure.</li>
</ul><br>
Compilation requirements<br>
2.5 When compiling the information specified in Disclosure 201-4, the reporting
organization shall identify the monetary value of financial assistance received from
government through consistent application of generally accepted accounting
principles.<br>
  <div><strong>GUIDANCE</strong></div>
<strong>Background</strong><br>
This disclosure provides a measure of governments’ contributions to an organization.<br>
The significant financial assistance received from a government, in comparison with taxes paid,
can be useful for developing a balanced picture of the transactions between the organization
and government.<br>
  `,
  "202-1": `
  <div>
      <strong>Disclosure 202-1 Ratios of standard entry level wage by gender compared to local minimum wage</strong>
  </div><br>
    <div><strong>REQUIREMENTS</strong></div><strong>
    The reporting organization shall report the following information:<br>
<ul style='list-style-type:lower-alpha;padding-left:20px'>
<li>
When a significant proportion of employees are compensated based on wages subject to minimum wage rules, report the relevant ratio of the entry level wage by gender at significant locations of operation to the minimum wage.</li>
<li>
When a significant proportion of other workers (excluding employees) performing the organization’s activities are compensated based on wages subject to minimum wage rules, describe the actions taken to determine whether these workers are paid above the minimum wage.</li>
<li>
Whether a local minimum wage is absent or variable at significant locations of operation, by gender. In circumstances in which different minimums can be used as a reference, report which minimum wage is being used.</li>
<li>
The definition used for ‘significant locations of operation’.</li>
</ul></strong><br><hr>

  <div><strong>RECOMMENDATIONS</strong></div>
  When compiling the information specified in Disclosure 202-1, the reporting organization should:
  <ul style="padding-left:40px">
      <li>
          2.1.1  use the data from Disclosure 2-8 in GRI 2: General Disclosures 2021 to identify the total number of workers who are not 
          employees and whose work is controlled by the organization;
      </li>
      <li>
          2.1.2  if applicable, convert the entry level wage to the same units used in the minimum wage (e.g., hourly or monthly basis);</li>
      <li>
      <li>
          2.1.3  when a significant proportion of other workers (excluding employees) performing the organization’s activities are
          compensated based on wages subject to minimum wage rules, report the relevant ratio of the entry level wage by gender at 
          significant locations of operation to the minimum wage.
      </li>
  </ul>
  <br>
  <div><strong>GUIDANCE</strong></div>
  <p style="text-indent:30px;">
      <ul style="padding-left:40px;list-style-type:disc">
          <li>
              This disclosure applies to those organizations in which a substantial portion of their employees, and workers (excluding employees) performing the organization’s activities, are compensated in a manner or scale that is closely linked to laws or regulations on minimum wage.
          </li>
          <li>
              Providing wages above the minimum wage can help contribute to the economic well-being of workers performing the organization’s activities. The impacts of wage levels are immediate, and they directly affect individuals, organizations, countries and economies. The distribution of wages is crucial for eliminating inequalities, such as wage gap differences between women and men, or nationals and migrants.
          </li>
          <li>
              Also, entry level wages paid compared to local minimum wages show the competitiveness of an organization’s wages and provide information relevant for assessing the effect of wages on the local labor market. Comparing this information by gender can also be a measure of an organization’s approach to equal opportunity in the workplace.
          </li>
      </ul>
  </p>`,
  "202-2": `
  <div>
      <strong>Disclosure 202-2 Proportion of senior management hired from the local community</strong>
  </div><br>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The reporting organization shall report the following information:<br>
<ul style='list-style-type:lower-alpha;padding-left:20px'>
<li>
Percentage of senior management at significant locations of operation that are hired from the local community.</li>
<li>
The definition used for ‘senior management’.</li>
<li>
The organization’s geographical definition of ‘local’.</li>
<li>
The definition used for ‘significant locations of operation’.</li>
</ul>
Compilation requirements<br>
2.2	When compiling the information specified in Disclosure 202-2, the reporting organization shall calculate this percentage using data on full-time employees.
    </strong>
  <div><strong>GUIDANCE</strong></div>
  <p style="text-indent:30px;">
      Senior management hired from the local community includes those individuals either born or who have the legal right to reside indefinitely (such as naturalized citizens or permanent visa holders) in the same geographic market as the operation. The geographical definition of ‘local’ can include the community surrounding operations, a region within a country, or a country.
      <br>
      <strong>Background</strong>
      Including members from the local community in an organization’s senior management demonstrates the organization’s positive market presence. Including local community members in the management team can enhance human capital. It can also increase the economic benefit to the local community, and improve an organization’s ability to understand local needs.
  </p>`,
  "203-1": `
  <div>
      <strong>Disclosure 203-1 Infrastructure investments and services supported</strong>
  </div><br>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The reporting organization shall report the following information:<br>
<ul style='list-style-type:lower-alpha;padding-left:20px'>
<li>Extent of development of significant infrastructure investments and services supported.</li>
<li>
Current or expected impacts on communities and local economies, including positive and negative impacts where relevant.</li>
<li>
Whether these investments and services are commercial, in-kind, or pro bono engagements.</li>

</ul>
    </strong>
  <div><strong>RECOMMENDATIONS</strong></div>
  When compiling the information specified in Disclosure 203-1, the reporting organization should disclose:
  <ul style="padding-left:40px">
      <li>
          2.1.1  the size, cost and duration of each significant infrastructure investment or service supported;
      </li>
      <li>
          2.1.2  the extent to which different communities or local economies are impacted by the organization’s infrastructure investments and services supported.
      </li>
  </ul>
  <br>
  <div><strong>GUIDANCE</strong></div>
  <strong>Background</strong>
  <p style="text-indent:30px;">
      <ul style="padding-left:40px;list-style-type:disc">
          <li>
              This disclosure concerns the impact that an organization’s infrastructure investments and services supported have on its stakeholders and the economy.
          </li>
          <li>
              The impacts of infrastructure investment can extend beyond the scope of an organization’s own operations and over a longer timescale. Such investments can include transport links, utilities, community social facilities, health and welfare centers, and sports centers. Along with investment in its own operations, this is one measure of the organization’s capital contribution to the economy.
          </li>
      </ul>
  </p>`,
  "203-2": `
  <div>
      <strong>Disclosure 203-2 Significant indirect economic impacts</strong>
  </div><br>
  <strong>
    <div><strong>REQUIREMENTS</strong></div>
    The reporting organization shall report the following information:<br>
<ul style='list-style-type:lower-alpha;padding-left:20px'>
<li>
Examples of significant identified indirect economic impacts of the organization, including positive and negative impacts.</li>
<li>
Significance of the indirect economic impacts in the context of external benchmarks and stakeholder priorities, such as national and international standards, protocols, and policy agendas.</li>
</ul><br><hr>
</strong>
  <div><strong>GUIDANCE</strong></div>
  This disclosure concerns the spectrum of indirect economic impacts that an organization can have on its stakeholders and the economy.
  <p style="text-indent:30px;">
      <ul style="padding-left:40px;list-style-type:disc">
          <li>
              changes in the productivity of organizations, sectors, or the whole economy (such as through greater adoption of information technology);
          </li>
          <li>
              economic development in areas of high poverty (such as changes in the total number of dependents supported through the income of a single job);
          </li>
          <li>
              economic impacts of improving or deteriorating social or environmental conditions (such as changing job market in an area converted from small farms to large plantations, or the economic impacts of pollution);
          </li>
          <li>
              availability of products and services for those on low incomes (such as preferential pricing of pharmaceuticals, which contributes to a healthier population that can participate more fully in the economy; or pricing structures that exceed the economic capacity of those on low incomes);
          </li>
          <li>
              enhanced skills and knowledge in a professional community or in a geographic location (such as when shifts in an organization’s needs attract additional skilled workers to an area, who, in turn, drive a local need for new learning institutions);
          </li>
          <li>
              number of jobs supported in the supply or distribution chain (such as the employment impacts on suppliers as a result of an organization’s growth or contraction);
          </li>
          <li>
              stimulating, enabling, or limiting foreign direct investment (such as when an organization changes the infrastructure or services it provides in a developing country, which then leads to changes in foreign direct investment in the region);
          </li>
          <li>
              economic impacts from a change in operation or activity location (such as the impact of outsourcing jobs to an overseas location);
          </li>
          <li>
              economic impacts from the use of products and services (such as economic growth resulting from the use of a particular product or service).
          </li>
      </ul>
  </p>`,
  "204-1": `
  <div>
      <strong>Disclosure 204-1 Proportion of spending on local suppliers</strong>
  </div><br>
    <div><strong>REQUIREMENTS</strong></div>
<strong>
The reporting organization shall report the following information:
<ul style='list-style-type:lower-alpha;padding-left:20px'>
<li>
Percentage of the procurement budget used for significant locations of operation that is
spent on suppliers local to that operation (such as percentage of products and services
purchased locally).</li>
<li>
The organization’s geographical definition of ‘local’.<li>
<li>
The definition used for ‘significant locations of operation’.</li>
</ul>
</strong><br><hr>
  <div><strong>RECOMMENDATIONS</strong></div>
  When compiling the information specified in Disclosure 204-1, the reporting organization should:
  <ul style="padding-left:40px">
      <li>
          2.1  When compiling the information specified in Disclosure 204-1, the reporting organization should calculate the percentages based on invoices or commitments made during the reporting period, i.e., using accruals accounting.
      </li>
  </ul>
  <br>
  <div><strong>GUIDANCE</strong></div>
  <p style="text-indent:30px;">
      <ul style="padding-left:40px;list-style-type:disc">
          <li>
              Local purchases can be made either from a budget managed at the location of operation or at an organization’s headquarters.
          </li>
      </ul><br>
      <b>Background</b><br>
      By supporting local suppliers, an organization can indirectly attract additional investment to the local economy. Local sourcing can be a strategy to help ensure supply, support a stable local economy, and maintain community relations.
  </p>`,
  "205-1": `
  <div>
      <strong>Disclosure 205-1 Operations assessed for risks related to corruption</strong>
  </div>
    <div><strong>REQUIREMENTS</strong></div>
    The reporting organization shall report the following information:<br>
<ul style='list-style-type:lower-alpha'>
<li>
Total number and percentage of operations assessed for risks related to corruption.</li>
<li>
Significant risks related to corruption identified through the risk assessment.</li>
</ul>

  <div><strong>GUIDANCE</strong></div>
  <p style="text-indent:30px;">
      <ul style="padding-left:40px;list-style-type:disc">
          <li>
              This disclosure can include a risk assessment focused on corruption or the inclusion of corruption as a risk factor in overall risk assessments.            
          </li>
          <li>
              The term ‘operation’ refers to a single location used by the organization for the production, storage and/or distribution of its goods and services, or for administrative purposes. Within a single operation, there can be multiple production lines, warehouses, or other activities. For example, a single factory can be used for multiple products or a single retail outlet can contain several different retail operations that are owned or managed by the organization.
          </li>
      </ul>
      <b>Background</b><br>
      This disclosure measures the extent of the risk assessment’s implementation across an organization. Risk assessments can help to assess the potential for incidents of corruption within and related to the organization, and help the organization to design policies and procedures to combat corruption.
  </p>`,
  "205-2": `
  <div>
      <strong>Disclosure 205-2 Communication and training about anti-corruption policies and procedures</strong>
  </div>
    <div><strong>REQUIREMENTS</strong></div><br><strong>
    The reporting organization shall report the following information:
<ul style='list-style-type:lower-alpha;padding-left:20px'>
<li>Total number and percentage of governance body members that the organization’s anti- corruption policies and procedures have been communicated to, broken down by region.</li>
<li>Total number and percentage of employees that the organization’s anti-corruption policies and procedures have been communicated to, broken down by employee category and region.</li>
<li>
Total number and percentage of business partners that the organization’s anti- corruption policies and procedures have been communicated to, broken down by type of business partner and region. Describe if the organization’s anti-corruption policies and procedures have been communicated to any other persons or organizations.</li>
<li>
Total number and percentage of governance body members that have received training on anti-corruption, broken down by region.</li>

<li>
Total number and percentage of employees that have received training on anti- corruption, broken down by employee category and region.</li>

</ul>
</strong>
  <div><strong>RECOMMENDATIONS</strong></div>
  When compiling the information specified in Disclosure 205-2, the reporting organization should:
  <ul style="padding-left:40px">
      <li>
          2.1.1  draw from the information used for Disclosure 405-1 in GRI 405: Diversity and Equal Opportunity 2016 to identify:
          <ul style="padding-left:40px">
              <li>
                  2.1.1.1  the governance bodies that exist within the organization, such as the board of directors, management committee, or similar body for non- corporate organizations;
              </li>
              <li>
                  2.1.1.2  the total number of individuals and/or employees who comprise these governance bodies;
              </li>
              <li>
                  2.1.1.3  the total number of employees in each employee category, excluding governance body members;
              </li>
          </ul>
      </li>
      <li>
          2.1.2	estimate the total number of business partners 
      <li>
  </ul>
  <br>
  <div><strong>GUIDANCE</strong></div>
  <p style="text-indent:30px;">
      <ul style="padding-left:40px;list-style-type:disc">
          <li>
              In the context of this GRI Standard, the term ‘business partners’ includes, among others, suppliers, agents, lobbyists and other intermediaries, joint venture and consortia partners, governments, customers, and clients.           
          </li>
      </ul>
      <b>Background</b>
      Communication and training build the internal and external awareness and the necessary capacity to combat corruption.
  </p>`,
  "205-3": `
  <div>
      <strong>Disclosure 205-3 Confirmed incidents of corruption and actions taken</strong>
  </div>
    <div><strong>REQUIREMENTS</strong></div><br>
    <strong>
    The reporting organization shall report the following information:<br>
<ul style='list-style-type:lower-alpha;padding-left;20px'>
<li>Total number and nature of confirmed incidents of corruption.</li>
<li>Total number of confirmed incidents in which employees were dismissed or disciplined for corruption.</li>
<li>Total number of confirmed incidents when contracts with business partners were terminated or not renewed due to violations related to corruption.</li>
<li>Public legal cases regarding corruption brought against the organization or its employees during the reporting period and the outcomes of such cases.</li>
</ul>
    </strong><br><hr>

  <div><strong>GUIDANCE</strong></div>
  <p style="text-indent:30px;">
      <ul style="padding-left:40px;list-style-type:disc">
          <li>
              For stakeholders, there is an interest in both the occurrence of incidents and an organization’s response to the incidents. Public legal cases regarding corruption can include current public investigations, prosecutions, or closed cases.
          </li>
          <li>
              In the context of this GRI Standard, the term ‘business partners’ includes, among others, suppliers, agents, lobbyists and other intermediaries, joint venture and consortia partners, governments, customers, and clients
          </li>
      </ul>
  </p>`,
  "206-1": `
  <div>
      <strong>Disclosure 206-1  Legal actions for anti-competitive behavior, anti-trust, and monopoly practices</strong>
  </div>
   <div><strong>REQUIREMENTS</strong></div><br>
   <strong>
   The reporting organization shall report the following information:<br>
<ul style='list-style-type:lower-alpha;padding-left:40px'>
<li>Number of legal actions pending or completed during the reporting period regarding anti-competitive behavior and violations of anti-trust and monopoly legislation in which the organization has been identified as a participant.</li>
<li>Main outcomes of completed legal actions, including any decisions or judgements.</li>
</ul>
   </strong>
  <div><strong>GUIDANCE</strong></div>
  <strong>Background</strong>
  <p style="text-indent:30px;">
      This disclosure pertains to legal actions initiated under national or international laws designed primarily for the purpose of regulating anti-competitive behavior, anti-trust, or monopoly practices.
  </p>
  <p style="text-indent:30px;">
      Anti-competitive behavior, anti-trust, and monopoly practices can affect consumer choice, pricing, and other factors that are essential to efficient markets. Legislation introduced in many countries seeks to control or prevent monopolies, with the underlying assumption that competition between enterprises also promotes economic efficiency and sustainable growth.
  </p>
  <p style="text-indent:30px;">
      Legal action indicates a situation in which the market actions or status of an organization have reached a sufficient scale to merit concern by a third party. Legal decisions arising from these situations can carry the risk of significant disruption of market activities for the organization as well as punitive measures.
  </p>`,
  "207-1": `
  <div>
      <strong>Disclosure 207-1  Approach to tax</strong>
  </div>
    <div><strong>REQUIREMENTS</strong></div><br>
    <strong>
    The reporting organization shall report the following information:<br>
<ul style='list-style-type:lower-alpha;padding-left:40px'>
<li>A description of the approach to tax, including:
<ul style='list-style-type:lower-roman;padding-left:40px'>
<li>
whether the organization has a tax strategy and, if so, a link to this strategy if publicly available;</li>
<li>
the governance body or executive-level position within the organization that formally reviews and approves the tax strategy, and the frequency of this review;</li>
<li>
the approach to regulatory compliance;</li>
<li>how the approach to tax is linked to the business and sustainable development strategies of the organization.</li>
</ul>
</li>
</ul>
    </strong><br><hr>
  <div><strong>GUIDANCE</strong></div>
  <strong>Background</strong>
  <p style="text-indent:30px; list-style-type:disc;">
      An organization’s approach to tax defines how the organization balances tax compliance with business activities and ethical, societal, and sustainable development-related expectations. It can include the organization’s tax principles, its attitude to tax planning, the degree of risk the organization is willing to accept, and the organization’s approach to engaging with tax authorities.
      
      An organization’s approach to tax is often described in a tax strategy, but it could also be described in equivalent documents, such as policies, standards, principles, or codes of conduct.

      <ul style="padding-left:40px;list-style-type:disc">
          <li> <strong>Guidance for Disclosure 207-1-a</strong></li>
          <p style="text-indent:40px; text-align:justify">    
              The reporting organization can illustrate its approach to tax by providing examples drawn from its tax practices. For example, the organization can provide an overview of its use of tax havens, the types of tax incentive it uses, or its approach to transfer pricing. These examples help demonstrate the organization’s risk appetite and the tax practices deemed acceptable and unacceptable by the organization and its highest governance body
          </p>
          <li> <strong>Guidance for Disclosure 207-1-a-i</strong></li>
          <ul style="padding-left:40px;list-style-type:disc">
              <li>
                  If the organization has a tax strategy but the strategy is not publicly available, the organization can provide an abstract or summary of the strategy.
              </li>
              <li>
                  If the organization has a tax strategy that applies to a smaller number of entities or tax jurisdictions than reported in Disclosure 207-4, the organization may report this strategy and list the entities or tax jurisdictions to which the strategy applies.
              </li>
              <li>
                  In addition to the overall strategy, if the organization has tax strategies that apply to individual entities or tax jurisdictions, the organization can explain any relevant differences between these strategies.
              </li>
          </ul>
          <li> <strong>Guidance for Disclosure 207-1-a-iii</strong></li>
          <p style="text-indent:40px; text-align:justify">
              When describing its approach to regulatory compliance, the organization can describe any statements in its tax strategy or equivalent documents regarding its intention with respect to the tax laws in the jurisdictions in which it operates. For example, the organization can describe whether it seeks to comply with the letter and the spirit of the law. That is, whether the organization takes reasonable steps to determine and follow the intention of the legislature
          </p>
          <li> <strong>Guidance for Disclosure 207-1-a-iv</strong></li>
          <p style="text-indent:40px; text-align:justify">
              When describing how its approach to tax is linked to its business strategy, the organization can explain how its tax planning is aligned with its commercial activities. The description can include any relevant statements from its tax strategy or equivalent documents.
          </p>
          <p style="text-indent:40px; text-align:justify">
              When describing how its approach to tax is linked to its sustainable development strategy, the organization can explain the following:
          </p>
          <ul style="padding-left:40px;list-style-type:disc">
              <li>
                  Whether it considered the economic and social impacts of its approach to tax when developing its tax strategy.
              </li>
              <li>
                  Any organizational commitments to sustainable development in the jurisdictions in which it operates and whether its approach to tax is aligned with these commitments
              </li>
          </ul>
      </ul>
  </p>`,
  "207-2": `
  <div>
      <strong>Disclosure 207-2  Tax governance, control, and risk management</strong>
  </div>
    <div><strong>REQUIREMENTS</strong></div><br>
    <strong>
    The reporting organization shall report the following information:<br>
<ul style='list-style-type:lower-alpha;padding-left:40px'>
<li>A description of the tax governance and control framework, including:<br>
<ul style='list-style-type:lower-roman;padding-left:40px'>
<li>
the governance body or executive-level position within the organization accountable for compliance with the tax strategy;</li>
<li>
how the approach to tax is embedded within the organization;</li>
<li>
the approach to tax risks, including how risks are identified, managed, and monitored;</li>
<li>
how compliance with the tax governance and control framework is evaluated.</li>
</ul>
</li>
<li>
A description of the mechanisms to raise concerns about the organization’s business conduct and the organization’s integrity in relation to tax.</li>
<li>
A description of the assurance process for disclosures on tax including, if applicable, a link or reference to the external assurance report(s) or assurance statement(s).</li>
</ul>

    </strong><br><hr>
  <div><strong>GUIDANCE</strong></div>
  <strong>Background</strong>
  <p style="text-indent:30px; list-style-type:disc;">
     Having robust governance, control, and risk management systems in place for tax can be an indication that the reported approach to tax and tax strategy are well embedded in an organization and that the organization is effectively monitoring its compliance obligations.
      Reporting this information reassures stakeholders that the organization’s practices reflect the statements it has made about its approach to tax in its tax strategy or equivalent documents.

      <ul style="padding-left:40px;list-style-type:disc">
          <li> <strong>Guidance for Disclosure 207-2-a</strong></li>
          <p style="text-indent:40px; text-align:justify">    
              When describing the tax governance and control framework, the reporting organization can provide examples of effective implementation of its tax governance, control, and risk management systems.            
          </p>
          <li> <strong>Guidance for Disclosure 207-2-a-i</strong></li>
          <p style="text-indent:40px; text-align:justify">
              If the highest governance body in an organization is accountable for compliance with the tax strategy, the organization can specify the degree to which the highest governance body has oversight of compliance. The organization can also specify any accountability for compliance delegated to executive-level positions within the organization.
          </p>
          <li> <strong>Guidance for Disclosure 207-2-a-ii</strong></li>
          <p style="text-indent:40px; text-align:justify">
              When reporting how the approach to tax is embedded within the organization, the organization can describe processes, projects, programs, and initiatives that support adherence to the approach to tax and tax strategy.
              Examples of such initiatives can include:
          </p>
          <ul style="padding-left:40px;list-style-type:disc">
              <li>
                training and guidance provided to relevant employees on the link between tax strategy, business strategy, and sustainable development;                
              </li>
              <li>
                  remuneration or incentive schemes for the person(s) responsible for implementing the tax strategy;
              </li>
              <li>
                  succession-planning for positions within the organization that are responsible for tax;
              </li>
              <li>
                  participation in tax transparency initiatives or representative associations that seek to develop best practice around disclosures on tax or educate stakeholders on tax-related issues.
              </li>
          </ul>
          <li> <strong>Guidance for Disclosure 207-2-a-iii</strong></li>
          <p style="text-indent:40px; text-align:justify">
              Tax risks are risks associated with the organization’s tax practices that might lead to a negative effect on the goals of the organization, or to financial or reputational damage. These include compliance risks or risks such as those related to uncertain tax positions, changes in legislation, or a perception of aggressive tax practices.
          </p>
          <p style="text-indent:40px; text-align:justify">
              When reporting on the approach to tax risks, the organization can describe its risk appetite and tolerance and provide examples of tax practices it has avoided because they are misaligned with its approach to tax and tax strategy. Risk appetite and tolerance indicate the degree of risk the organization is willing to accept in determining its tax positions.
          </p>
          <p style="text-align:justify">
              When reporting how tax risks are identified, managed, and monitored, the organization can:
          </p>
          <ul style="padding-left:40px;list-style-type:disc">
              <li>
                  describe the role of the highest governance body in the tax risk management process;
              </li>
              <li>
                  describe how the tax risk management process is communicated and embedded across the organization;
              </li>
              <li>
                  refer to any internal control frameworks or generally accepted risk management principles that are applied to tax.
              </li>
          </ul>
          <li> <strong>Guidance for Disclosure 207-2-a-iv</strong></li>
          <p style="text-indent:40px; text-align:justify">    
              When reporting how compliance with the tax governance and control framework is evaluated, the organization can describe the process through which the tax governance and control framework is monitored, tested, and maintained. An example of this is giving an internal auditor accountability for undertaking annual reviews of the tax department’s compliance with the tax governance and control framework.
          </p>
          <p style="text-indent:40px; text-align:justify">    
          The organization can also specify the degree to which the highest governance body has oversight of the design, implementation, and effectiveness of the tax governance and control framework.
          </p>
          <li> <strong>Guidance for Disclosure 207-2-b</strong></li>
          <p style="text-indent:40px; text-align:justify">    
              One example of a mechanism for individuals to raise concerns about the organization’s business conduct, or about activities that compromise the organization’s integrity in relation to tax, is whistleblowing.
          </p>
          <p style="text-indent:40px; text-align:justify">    
              Disclosure 207-2-b is related to Disclosure 2-26 in GRI 2: General Disclosures 2021. If the information reported by the organization in Disclosure 2-26 covers mechanisms used to raise concerns about the organization’s business conduct in relation to tax, the organization can provide a reference to this information.
          </p>
          <li> <strong>Guidance for Disclosure 207-2-c</strong></li>
          <p style="text-indent:40px; text-align:justify">    
              Disclosure 207-2-c is related to Disclosure 2-5 in GRI 2: General Disclosures 2021. If the assurance process for disclosures on tax has been completed as part of another assurance process, the organization can provide a reference to this information reported in Disclosure 2-5 or elsewhere
          </p>
      </ul>
  </p>`,
  "207-3": `
  <div>
      <strong>Disclosure 207-3  Stakeholder engagement and management of concerns related to tax</strong>
  </div>
    <div><strong>REQUIREMENTS</strong></div>
    The reporting organization shall report the following information:<br>

<li>A description of the approach to stakeholder engagement and management of stakeholder concerns related to tax, including:
<ul style='list-style-type:lower-roman;padding-left:20px'>
<li>the approach to engagement with tax authorities;</li>
<li>the approach to public policy advocacy on tax;</li>
<li>the processes for collecting and considering the views and concerns of stakeholders, including external stakeholders.</li>
</ul>
</li>
</ul>
  <div><strong>GUIDANCE</strong></div>
  <b>Background</b>
  <p style="text-indent:30px; list-style-type:disc;">
      Organizations’ tax practices are of interest to various stakeholders. The approach an organization takes to engaging with stakeholders has the potential to influence its reputation and position of trust. This includes how the organization engages with tax authorities in the development of tax systems, legislation, and administration.

      Stakeholder engagement can enable the organization to understand evolving expectations related to tax. It can give the organization insight into potential future regulatory changes and enable the organization to better manage its risks and impacts
      <ul style="padding-left:40px;list-style-type:disc">
          <li> <strong>Guidance for Disclosure 207-3-a-i</strong></li>
          <p style="text-indent:40px; text-align:justify">    
              The approach to engagement with tax authorities can include participating in cooperative compliance agreements, seeking active real-time audit, seeking clearance for all significant transactions, engaging on tax risks, and seeking advance pricing agreements
          </p>
          <li> <strong>Guidance for Disclosure 207-3-a-ii</strong></li>
          <p style="text-indent:30px; text-align:justify">
              When reporting the approach to public policy advocacy on tax, the reporting organization can describe:
          </p>           
          <ul style="padding-left:60px;list-style-type:disc">
              <li>
                  its lobbying activities related to tax;
              </li>
              <li>
                its stance on significant issues related to tax that it addresses in its public policy advocacy, and any differences between its advocacy positions and its stated policies, goals, or other public positions;
              </li>
              <li>
                whether it is a member of, or contributes to, any representative associations or committees that participate in public policy advocacy on tax, including:
              </li>
              <li>
                the nature of this contribution;
              </li>
              <li>
                any differences between the organization’s stated policies, goals, or other public positions on significant issues related to tax, and the positions of the representative associations or committees
              </li>
          </ul>
          <p style="text-indent:30px; text-align:justify">
              Disclosure 207-3-a-ii is related to the requirements in GRI 415: Public Policy 2016. If the organization has determined public policy to be a material topic and has reported information in GRI 415 that covers the organization’s public policy advocacy on tax, the organization can provide a reference to this information.
          </p>
          <li> <strong>Guidance for Disclosure 207-2-a-iii</strong></li>
          <p style="text-indent:40px; text-align:justify">
              When reporting the processes for collecting and considering the views and concerns of stakeholders, the organization can describe how the processes enable stakeholders to participate in this engagement. The organization can also provide examples of how stakeholder feedback has influenced the approach to tax, the tax strategy, or the tax practices of the organization.            
          </p>
      </ul>
  </p>`,
  "207-4": `
  <div>
      <strong>Disclosure 207-4 Country-by-country reporting</strong>
  </div>
       <div><strong>REQUIREMENTS</strong></div><br>
       <strong>
       The reporting organization shall report the following information:<br>
<ul style='list-style-type:lower-alpha;padding-left:40px'>
<li>
All tax jurisdictions where the entities included in the organization’s audited consolidated financial statements, or in the financial information filed on public record, are resident for tax purposes.</li>
<li>For each tax jurisdiction reported in Disclosure 207-4-a:
<ul style='list-style-type:lower-romanl;padding-left:40px'>
<li>
Names of the resident entities;</li>
<li>
Primary activities of the organization;</li>
<li>
Number of employees, and the basis of calculation of this number;</li>
<li>
Revenues from third-party sales;</li>
<li>
Revenues from intra-group transactions with other tax jurisdictions;</li>
<li>
Profit/loss before tax;</li>
<li>
Tangible assets other than cash and cash equivalents;</li>
<li>
Corporate income tax paid on a cash basis;</li>
<li>
Corporate income tax accrued on profit/loss;</li>
<li>
Reasons for the difference between corporate income tax accrued on profit/loss and the tax due if the statutory tax rate is applied to profit/loss before tax.</li>
</ul>
</li>
<li>
The time period covered by the information reported in Disclosure 207-4.</li>
</ul>
Compilation requirements:<br>

2.1. When compiling the information specified in Disclosure 207-4, the reporting organization shall report information for the time period covered by the most recent audited consolidated financial statements or financial information filed on public record. If information is not available for this time period, the organization may report information for the time period covered by the audited consolidated financial statements, or the financial information filed on public record, immediately preceding the most recent ones.<br>
2.2. When compiling the information specified in Disclosure 207-4-b, the reporting organization shall:<br>
<ul style='list-style-type:none;padding-left:40px'>
<li>
2.2.1. reconcile the data reported for Disclosures 207-4-b-iv, vi, vii, and viii with the data stated in its audited consolidated financial statements, or the financial information filed on public record, for the time period reported in Disclosure 207-4-c. Where the data reported does not reconcile with the audited consolidated financial statements, or the financial information filed on public record, the organization shall provide an explanation for this difference;
</li>
<li>
2.2.2. for Disclosure 207-4-b-ix, include corporate income tax accrued in the time period reported in Disclosure 207-4-c and exclude deferred corporate income tax and provisions for uncertain tax positions;</li>
<li>
2.2.3. in cases where an entity is deemed not to be resident in any tax jurisdiction, provide the information for this stateless entity separately.</li>
</ul>
       </strong><br><hr>

  <p style="text-indent:30px; list-style-type:disc;">
      <div><strong>RECOMMENDATIONS</strong></div>
      The reporting organization should report the following additional information for each tax jurisdiction reported in Disclosure 207-4-a:
      <ul style="padding-left:40px">
          <li>
              2.3.1	Total employee remuneration;
          </li>
          <li>
              2.3.2	Taxes withheld and paid on behalf of employees;
          <li>
          <li>
              2.3.3	Taxes collected from customers on behalf of a tax authority;
          </li>
          <li>
              2.3.4	Industry-related and other taxes or payments to governments;
          <li>
          <li>
              2.3.5	Significant uncertain tax positions;
          </li>
          <li>
              2.3.6	Balance of intra-company debt held by entities in the tax jurisdiction, and the basis of calculation of the interest rate paid on the debt
          <li>
      </ul>
      <br>
      <b>Background</b><br>
      <div><strong>GUIDANCE</strong></div>
      <p style="text-indent:30px;">
          Country-by-country reporting is the reporting of financial, economic, and tax-related information for each jurisdiction in which the organization operates.
      </p> 
      <ul style="padding-left:40px;list-style-type:disc">
          <li> <strong>Guidance for Disclosure 207-4-a</strong></li>
          <p style="text-indent:40px; text-align:justify">    
              In the context of this Standard, tax jurisdictions are identified according to where the entities included in the organization’s audited consolidated financial statements, or in the financial information filed on public record, are resident for tax purposes. These entities include permanent establishments and dormant entities.
          </p>
          <li> <strong>Guidance for Disclosure 207-4-b</strong></li>
          <p style="text-indent:30px; text-align:justify">
              Unless otherwise stated, country-by-country information is to be reported at the level of tax jurisdictions and not at the level of individual entities.
          </p>    
          <p style="text-indent:30px; text-align:justify">
              Number of employees, revenues, profit/loss before tax, and tangible assets other than cash and cash equivalents are indicators of the organization’s scale of activity within a tax jurisdiction.
          </p>
          <p style="text-indent:30px; text-align:justify">
              When considered in conjunction with the other required and recommended information, they can inform assessments about the level of taxes being paid in a jurisdiction.
          </p>
          <p style="text-indent:30px; text-align:justify">
              In addition to this information, the organization can report any other information relevant for understanding the scale of its activity within a jurisdiction.
          </p>
          <p style="text-indent:30px; text-align:justify">
              If the reporting organization cannot report all required information for all the tax jurisdictions reported in Disclosure 207-4-a, it may use reasons for omission as set out in GRI 1: Foundation 2021. The organization is required to specify the requirement it cannot comply with, and to provide a reason for omission and required explanation, as set out in GRI 1. See Requirement 6 in GRI 1 for more information on reasons for omission.
          </p>
          <p style="text-indent:30px; text-align:justify">   
              If complete reporting for a tax jurisdiction is not possible because the organization holds a minority shareholding or is the non-operating joint venture partner in an entity, the organization may provide ‘information unavailable / incomplete’ as the reason for omission and specify the majority shareholder or operating partner.
          </p>
          <p style="text-indent:30px; text-align:justify">
              The organization can also report any contextual information necessary to understand how data has been compiled, such as any standards, methodologies, and assumptions used.
          </p> 
          <ul style="padding-left:20px;list-style-type:disc">
              <li> <strong>Guidance for Disclosure 207-4-b-i</strong></li>
              <p style="text-indent:40px; text-align:justify">    
                  Disclosure 207-4-b-i requires the organization to report a list of entities by tax jurisdiction.
              </p>
              <p style="text-indent:40px; text-align:justify">    
                  If the organization’s publicly available audited consolidated financial statements, or the financial information filed on public record, include a list of all its entities by tax jurisdiction, the organization can provide a reference to this information.
                  When reporting the names of the resident entities for a tax jurisdiction, the organization can specify if any of the entities are dormant.
              </p>
          </ul>
          <ul style="padding-left:20px;list-style-type:disc">
              <li> <strong>Guidance for Disclosure 207-4-b-ii</strong></li>
              <p style="text-indent:40px; text-align:justify">    
                  When reporting its primary activities in a tax jurisdiction, the organization can provide a general description such that a report reader can clearly identify the organization’s main activities in the jurisdiction, for example, sales, marketing, manufacturing, or distribution. The organization is not required to list the activities of each entity in the jurisdiction.    
              </p>
          </ul>
          <ul style="padding-left:20px;list-style-type:disc">
              <li> <strong>Guidance for Disclosure 207-4-b-iii</strong></li>
              <p style="text-indent:40px; text-align:justify">    
                  Employee numbers can be reported using an appropriate calculation, such as head count at the end of the time period reported in Disclosure 207-4-c or a full-time equivalent (FTE) calculation. To enable comparability, it is important that the organization applies the approach consistently across all tax jurisdictions and between time periods.
              </p>
              <p style="text-indent:40px; text-align:justify">    
                  If the organization is unable to report exact figures, it can report the number of employees to the nearest ten or, where the number of employees is greater than 1000, to the nearest 100.
              </p>
              <p style="text-indent:40px; text-align:justify">
                  The number of employees is one indicator of the organization’s scale of activity in a tax jurisdiction. In addition to the number of employees, the organization may report the number of workers (excluding employees) performing the organization’s activities, if this helps explain the
                  organization’s scale of activity in the jurisdiction. It is important that the organization reports the number of employees and/or the number of workers consistently across all jurisdictions and between time periods.
              </p>
          </ul>
          <ul style="padding-left:20px;list-style-type:disc">
              <li> <strong>Guidance for Disclosure 207-4-b-iv & 207-4-b-v</strong></li>
              <p style="text-indent:40px; text-align:justify">    
                  These disclosures require the organization to report revenues from third-party sales for each tax jurisdiction and from intra-group transactions between that jurisdiction and other tax jurisdictions. Intra-group transactions within the same tax jurisdiction are not required, but the organization can report this information separately.
              </p>
              <p style="text-indent:40px; text-align:justify">    
                  Intra-group transactions between jurisdictions can influence the tax bases of the organization in the jurisdictions involved in these transactions. Intra-group transactions within the same tax jurisdiction do not affect the tax base of the organization within that jurisdiction.
              </p>
              <p style="text-indent:40px; text-align:justify">
                  For this reason, revenues from third-party sales and intra-group transactions with other jurisdictions are a more appropriate indicator of an organization’s scale of activity in a tax jurisdiction than aggregated revenues. Aggregated revenues could result in local revenues being double-counted, which might create a misleading impression about the organization's scale of activity in a jurisdiction.
                  The organization can also report other sources of revenue, for example, dividends, interest, and royalties, where this is standard practice in the sector of the organization.
              </p>
          </ul>
          <ul style="padding-left:20px;list-style-type:disc">
              <li> <strong>Guidance for Disclosure 207-4-b-vi</strong></li>
              <p style="text-indent:40px; text-align:justify">    
                  When reporting profit/loss before tax for a tax jurisdiction, the organization can calculate the consolidated profit/loss before tax for all its resident entities in the jurisdiction.
              </p>
          </ul>
          <ul style="padding-left:20px;list-style-type:disc">
              <li> <strong>Guidance for Disclosure 207-4-b-vii</strong></li>
              <p style="text-indent:40px; text-align:justify">    
                  When reporting tangible assets for a tax jurisdiction, the organization can calculate the consolidated total of the net book values of tangible assets for all its resident entities in the jurisdiction.
              </p>
          </ul>
          <ul style="padding-left:20px;list-style-type:disc">
              <li> <strong>Guidance for Disclosure 207-4-b-viii</strong></li>
              <p style="text-indent:40px; text-align:justify">    
                  When reporting corporate income tax paid on a cash basis for a tax jurisdiction, the organization can calculate the total actual corporate income tax paid during the time period reported in Disclosure 207-4-c by all its resident entities in the jurisdiction. This includes cash taxes paid by entities to the jurisdiction of residence and to all other jurisdictions (e.g., withholding taxes incurred in other tax jurisdictions).
              </p>
              <p style="text-indent:40px; text-align:justify">
                  If the tax paid includes a significant amount of withholding tax, the organization can explain this. If taxes are incurred in other tax jurisdictions, the organization can report the amount of tax paid to the other tax jurisdictions separately and identify the jurisdictions where the tax was paid.
              </p>
          </ul>
          <ul style="padding-left:20px;list-style-type:disc">
              <li> <strong>Guidance for Disclosure 207-4-b-x</strong></li>
              <p style="text-indent:40px; text-align:justify">    
                  When reporting the reasons for the difference between corporate income tax accrued on profit/loss and the tax due if the statutory tax rate is applied to profit/loss before tax, the organization can describe items that explain the difference, such as tax reliefs, allowances, incentives, or any special tax provisions where an entity benefits from preferential tax treatment.
              </p>
              <p style="text-indent:40px; text-align:justify">
                  The organization can also report the expiration date, investment requirements, and likely long- term continuity of tax reliefs or incentives for a jurisdiction.
              </p>
              <p style="text-indent:40px; text-align:justify">
                  The organization can also report the expiration date, investment requirements, and likely long- term continuity of tax reliefs or incentives for a jurisdiction.
              </p>
              <p style="text-indent:40px; text-align:justify">
                  In addition to providing a qualitative explanation as required by this disclosure, the organization can also report a quantitative corporate tax reconciliation.
              </p>
          </ul>
          <li> <strong>Guidance for Disclosure 207-4-c</strong></li>
          <p style="text-indent:40px; text-align:justify">    
              The organization is required to report information on a regular schedule and make it available in time for information users to make decisions (see the Timeliness principle in GRI 1: Foundation 2021 for more information). The organization is also recommended to report the information for the same reporting period and publish the information at the same time as its financial reporting, where this is possible (see section 5.1 in GRI 1 for more information). However, the
              information required in Disclosure 207-4 might not be available for reporting until a later point in time.
          </p>
          <p style="text-indent:40px; text-align:justify">
              If the information required in Disclosure 207-4 is not available for the time period covered by the most recent audited consolidated financial statements or financial information filed on public record, the organization may report information for the time period covered by the audited consolidated financial statements, or the financial information filed on public record, immediately preceding the most recent ones.
          </p>
          <ul style="padding-left:20px;list-style-type:disc">
              <li><strong>Guidance for clause 2.2.1</strong></li>
                  For each of the disclosures specified in clause 2.2.1, the data is considered to be reconciled when the sum of this data for all tax jurisdictions equals the amount reported in the organization’s audited consolidated financial statements or in the financial information filed on public record.
              <li><strong>Guidance for clause 2.2.3</strong></li>
                  When providing information for stateless entities, the organization can also include their jurisdiction of incorporation.
              <li><strong>Guidance for clause 2.3.1</strong></li>
                  Total employee remuneration in a tax jurisdiction can reflect the business value provided by the entities in that jurisdiction to the organization as a whole.
                  Total employee remuneration also represents the basis for calculating taxes withheld and paid on behalf of employees, covered under clause 2.3.2.
              <li><strong>Guidance for clause 2.3.2</strong></li>
                  Taxes withheld and paid on behalf of employees refer to taxes withheld by the organization from employee remuneration to be paid to the tax authorities. These can include income taxes, payroll taxes, and social security contributions.
              <li><strong>Guidance for clause 2.3.3</strong></li>
                  Taxes collected from customers refer to taxes and duties charged on and collected on the sales of certain products and services. These are paid by the organization to the tax authorities on behalf of customers.
              <li><strong>Guidance for clause 2.3.4</strong></li>
                  Examples of industry-related and other taxes or payments to governments include:
                  <ul style="padding-left:20px;list-style-type:disc">
                      <li>industry taxes (e.g., energy tax, airline tax);</li>
                      <li>property taxes (e.g., land tax);</li>
                      <li>product taxes (e.g., customs duties, alcohol and tobacco duties);</li>
                      <li>taxes and duties levied on the supply, use, or consumption of goods and services considered to be harmful to the environment (e.g., vehicle excise duties).</li>
                  </ul>
              <li><strong>Guidance for clause 2.3.5</strong></li>
                  When reporting significant uncertain tax positions for a tax jurisdiction, the organization can report the value of the tax positions in line with its audited consolidated financial statements or the financial information filed on public record.
                  The organization can provide a description of tax positions that have not been agreed with the relevant tax authorities at the end of the time period reported in Disclosure 207-4-c. The description can include the nature of the disagreement and the reasons for any change in tax positions that occurred during the time period, where relevant.
          </ul>
      </ul>
  </p>`,
  "301-1": `
    <div>
  <strong>Disclosure 301-1 Materials used by weight or volume</strong></div>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The reporting organization shall report the following information:<br>
<ul style='list-style-type:lower-alpha;padding-left:40px'>
    <li>Total weight or volume of materials that are used to produce and package the organization’s primary products and services during the reporting period, by:
    <ul style='list-style-type:lower-roman'>
        <li>non-renewable materials used;</li>
        <li>renewable materials used.</li>
    </ul>
    </li>
</ul>
    </strong><br><hr>
  <div><strong>RECOMMENDATIONS</strong></div>
  <br>
  When compiling the information specified in Disclosure 301-1, the reporting organization
should:
<ul >
  <li>
 2.1.1  include the following material types in the calculation of total materials used:
<ul style="padding-left:40px">
      <li>
      2.1.1.1 raw materials, i.e., natural resources used for conversion to products or
services, such as ores, minerals, and wood;
      </li>
      <li>
        2.1.1.2 associated process materials, i.e., materials that are needed for the
manufacturing process but are not part of the final product, such as
lubricants for manufacturing machinery;
      </li>
      <li>
          2.1.1.3 semi-manufactured goods or parts, including all forms of materials and
components other than raw materials that are part of the final product;
      </li>    
      <li>
          2.1.1.4 materials for packaging purposes, including paper, cardboard and
plastics;
      </li>
    </ul>
  </li>

  <li>
2.1.2 report, for each material type, whether it was purchased from external suppliers or
sourced internally (such as by captive production and extraction activities);
  </li>
  <li>
 2.1.3 report whether these data are estimated or sourced from direct measurements;
</li>
<li>
2.1.4 if estimation is required, report the methods used.
  </li>
</ul>
<br>
<div><b>GUIDANCE</b></div>
<p style="text-indent:30px;">
The reported usage data are to reflect the material in its original state, and not to be presented
with further data manipulation, such as reporting it as ‘dry weight’.
</p>
  `,
  "301-2": `
    <div>
  <strong>Disclosure 301-2 Recycled input materials used</strong></div>
  <br>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
        The reporting organization shall report the following information:<br>
    <ul style="list-style-type:lower-alpha;padding-left:40px">
    <li>
        Percentage of recycled input materials used to manufacture the organization's primary products and services.

    </li>
   </ul>
    Compilation requirements
    2.2 When compiling the information specified in Disclosure 301-2, the reporting organization shall:
        <ul style="list-style-type:none;padding-left:40px">
        <li>2.2.1. use the total weight or volume of materials used as specified in Disclosure 301-1;</li>

        <li>2.2.2 calculate the percentage of recycled input materials used by applying the following formula:</li>
        </ul>
    <div style="width:100%;border:1px solid black;display:flex;justify-content:center;align-items:center;margin-top:10px;margin-bottom:10px">
        <div style='width:25%'>
        Percentage of recycled input materials used
        </div>
                <div style='width:10%'>
      =
        </div>
        <div style='display:flex;flex-direction:column'>
            <div style='border-bottom:1px solid black'>Total recycled input materials used</div>
            <div>Total input materials used</div>
        </div>
        <div style='margin-left:20px'>x100
        </div>
    </div>
    </strong>
  <div><strong>RECOMMENDATIONS</strong></div>
  <p style="text-indent:30px;">
    When compiling the information specified in Disclosure 301-2, the reporting organization
should, if estimation is required, report the methods used.
  </p>
  <br>
    <div><strong>GUIDANCE</strong></div>
    <p style="text-indent:30px;">
    If material weight and volume measurements are stated as different units, the organization can
convert measurements to standardized units.
    </p>
  `,
  "301-3": `
  <div>
  <strong>Disclosure 301-3 Reclaimed products and their packaging materials</strong></div>
  <br>
  <div><b>REQUIREMENTS</b></div>
<strong>
  The reporting organization shall report the following information:<br>
<ul style="list-style-type:lower-alpha;padding-left:40px">
<li>Percentage of reclaimed products and their packaging materials for each product category.</li>
<li>How the data for this disclosure have been collected.</li>
</ul>

Compilation requirements

2.4 When compiling the information specified in Disclosure 301-3, the reporting organization shall:
<ul style="list-style-type:lower-alpha;padding-left:40px">
    2.4.1 exclude rejects and recalls of products;
    2.4.2 calculate the percentage of reclaimed products and their packaging materials for each product category using the following formula:
</ul>
    <div style="width:100%;border:1px solid black;display:flex;justify-content:center;align-items:center;margin-top:10px;margin-bottom:10px">
        <div style='width:25%'>
       Percentage of reclaimed products and their packaging materials
        </div>
                <div style='width:10%'>
      =
        </div>
        <div style='display:flex;flex-direction:column;width:50%'>
            <div style='border-bottom:1px solid black'>Products and their packaging materials reclaimed within the reporting period
</div>
            <div>Products sold within the reporting
period</div>
        </div>
        <div style='margin-left:20px'>x100
        </div>
    </div>
</strong>

<b>GUIDANCE</b>

<p style="text-indent:30px;">
The reporting organization can also report recycling or reuse of packaging separately.
</p>
  `,

  "302-1": `
      <div>
  <strong>Disclosure 302-1 Energy consumption within the organization</strong></div>
  <br>
    <b>REQUIREMENTS</b><br>
<strong>
The reporting organization shall report the following information:<br>
<ul style="list-style-type:lower-alpha;padding-left:40px">
<li>Total fuel consumption within the organization from non-renewable sources, in joules or multiples, and including fuel types used.</li>
<li>Total fuel consumption within the organization from renewable sources, in joules or multiples, and including fuel types used.</li>
<li>In joules, watt-hours or multiples, the total:
<ul style="list-style-type:lower-roman;padding-left:40px">
<li>electricity consumption</li>
<li>heating consumption</li>
<li>cooling consumption</li>
<li>steam consumption</li>
</ul>
</li>
<li>In joules, watt-hours or multiples, the total:
<ul style="list-style-type:lower-roman;padding-left:40px">
<li>electricity sold</li>
<li>heating sold</li>
<li>cooling sold</li>
<li>steam sold</li>
</ul>
</li>
<li>Total energy consumption within the organization, in joules or multiples.</li>
<li>Standards, methodologies, assumptions, and/or calculation tools used.</li>
<li>Source of the conversion factors used.</li>
</ul>
<br>
Compilation requirements
2.1 When compiling the information specified in Disclosure 302-1, the reporting organization shall:
<ul style="list-style-type:none;padding-left:40px">
<li>2.1.1. avoid the double-counting of fuel consumption, when reporting self-generated energy consumption. If the organization generates electricity from a non- renewable or renewable fuel source and then consumes the generated electricity, the energy consumption shall be counted once under fuel consumption;</li>
<li>2.1.2. report fuel consumption separately for non-renewable and renewable fuel sources;</li>
<li>2.1.3. only report energy consumed by entities owned or controlled by the organization;</li>
<li>2.1.4. calculate the total energy consumption within the organization in joules or multiples using the following formula:</li>
</ul>
<div style="border:1px solid black;display:flex;flex-direction:column;align-items:center">
<div>Total energy consumption within the organization
</div>
<div> =
</div>
<div> Non-renewable fuel consumed
</div>
<div> +
</div>
<div>Renewable fuel consumed</div>
<div>+</div>
<div>Electricity, heating, cooling, and steam purchased for consumption</div>
<div>+</div>
<div>Self-generated electricity, heating, cooling, and steam, which are not consumed (see clause 2.1.1)</div>
<div> - </div>
<div>Electricity, heating, cooling, and steam sold</div>

</div>
</strong>
<br><hr>

  <b>RECOMMENDATIONS</b>
<ul>
<li>
2.2 When compiling the information specified in Disclosure 302-1, the reporting
organization should:
<ul style="padding-left:40px">
<li>
2.2.1
apply conversion factors consistently for the data disclosed;</li>
<li>
2.2.2
use local conversion factors to convert fuel to joules, or multiples, when
possible;
</li>
<li>
2.2.3
use the generic conversion factors, when local conversion factors are
unavailable;
</li>
<li>
2.2.4
if subject to different standards and methodologies, describe the
approach to selecting them;
</li>
<li>
2.2.5
report energy consumption for a consistent group of entities. When
possible, the group of entities should also be consistent with the group of
entities used in Disclosures 305-1 and 305-2 of GRI 305: Emissions 2016;
</li>
<li>
2.2.6
where it aids transparency or comparability over time, provide a
breakdown of energy consumption data by:
business unit or facility;
country;type of source (see definitions for the
listing of non-renewable sources and
renewable sources);
type of activity.
</li>
</ul>
</li>
</ul>
<br>

<b>GUIDANCE</b>
<p style="text-indent:30px;">
For some organizations, electricity is the only significant form of energy they consume. For
others, energy sources such as steam or water provided from a district heating plant or chilled
water plant can also be important.
</p>
<p style="text-indent:30px;">
Energy can be purchased from sources external to the organization or produced by the
organization itself (self-generated).
</p>
<p style="text-indent:30px;">
Non-renewable fuel sources can include fuel for combustion in boilers, furnaces, heaters,
turbines, flares, incinerators, generators and vehicles that are owned or controlled by the
organization. Non-renewable fuel sources cover fuels purchased by the organization. They also
include fuel generated by the organization’s activities – such as mined coal, or gas from oil and
gas extraction.
</p>
<p style="text-indent:30px;">
Renewable fuel sources can include biofuels, when purchased for direct use, and biomass in
sources owned or controlled by the organization.
</p>
<p style="text-indent:30px;">
Consuming non-renewable fuels is usually the main contributor to direct (Scope 1) GHG
emissions, which are reported in Disclosure 305-1 of GRI 305: Emissions 2016. Consuming purchased electricity, heating, cooling, and steam contributes to the organization’s energy
indirect (Scope 2) GHG emissions, which are reported in Disclosure 305-2 of GRI 305: Emissions 2016.
</p>
  `,
  "302-2": `
      <div>
  <strong>Disclosure 302-2 Energy consumption outside of the
organization</strong></div>
  <br>
  <b>REQUIREMENTS</b><br>
  <strong>
  The reporting organization shall report the following information:<br>
<ul style='list-style-type:lower-alpha;padding-left:40px'>
 <li>Energy consumption outside of the organization, in joules or multiples.</li>
<li>Standards, methodologies, assumptions, and/or calculation tools used.</li>
<li>Source of the conversion factors used.</li>
</ul><br>
Compilation requirements<br>
2.3 When compiling the information specified in Disclosure 302-2, the reporting organization shall exclude energy consumption reported in Disclosure 302-1.
</strong><br><hr>
  <b>RECOMMENDATIONS</b>
  <ul>
  <li>
    2.4
When compiling the information specified in Disclosure 302-2, the reporting organization
should:
<li>
</li>
</li>
<ul style='padding-left:40px'>
<li>
    2.4.1 if subject to different standards and methodologies, describe the approach to selecting them;
    </li>
    <li>
    2.4.2 list energy consumption outside of the organization, with a breakdown by
    upstream and downstream categories and activities.
    </li>
    </ul>
  </ul>

<br>
<b>GUIDANCE</b>
<p style="text-indent:30px;">
The reporting organization can identify energy consumption outside of the organization by
assessing whether an activity’s energy consumption:
<ul style='list-style-type:disc;padding-left:40px'>
<li>
 contributes significantly to the organization’s total anticipated energy consumption outside of
the organization;
</li>
<li>
offers potential for reductions the organization can undertake or influence;
</li>
<li>
 contributes to climate change-related risks, such as financial, regulatory, supply chain,
product and customer, litigation, and reputational risks;
</li>
<li>
 is deemed material by stakeholders, such as customers, suppliers, investors, or civil
society;
</li>
<li>
 results from outsourced activities previously performed in-house, or that are typically
performed in-house by other organizations in the same sector;
</li>
</li>
has been identified as significant for the organization’s sector;
</li>
<li>
 meets any additional criteria for determining relevance, developed by the organization or by
organizations in its sector.
</li>
</ul>
The organization can use the following upstream and downstream categories and activities
from the ‘GHG Protocol Corporate Value Chain (Scope 3) Accounting and Reporting Standard’
for identifying relevant energy consumption outside of the organization:<br>
Upstream categories
<br>1. Purchased goods and services
<br>2. Capital goods
<br>3. Fuel- and energy-related activities (not included in Disclosure 302-1)
<br>4. Upstream transportation and distribution
<br>5. Waste generated in operations
<br>6. Business travel
<br>7. Employee commuting
<br>8. Upstream leased assets
<br>Other upstream
<br>Downstream categories
<br>1. Downstream transportation and distribution
<br>2. Processing of sold products
<br>3. Use of sold products
<br>4. End-of-life treatment of sold products
<br>5. Downstream leased assets
<br>6. Franchises
<br>7. Investments
<br>Other downstream
</p>
<p style="text-indent:30px;">
For each of these categories and activities, the organization can calculate or estimate the
amount of energy consumed.
</p>
<p style="text-indent:30px;">
The organization can report energy consumption separately for non-renewable sources and
renewable sources.
</p>
<b>Background</b><br>
<p style="text-indent:30px;">
Energy consumption can occur outside an organization, i.e., throughout the organization’s
upstream and downstream activities associated with its operations.
</p>
<p style="text-indent:30px;">
This can include consumers’ use of products the organization sells, and the end-of-life
treatment of products.
</p>
<p style="text-indent:30px;">
Quantifying energy consumption outside of the organization can provide a basis for calculating
some of the relevant other indirect (Scope 3) GHG emissions in Disclosure 305-3 of GRI 305:
Emissions 2016.
</p>
  `,
  "302-3": `
      <div>
  <strong>Disclosure 302-3 Energy intensity</strong></div>
  <br>
    <div><strong>REQUITEMENTS</strong></div><br>
    <strong>
    The reporting organization shall report the following information:<br>
<ul style='list-style-type:lower-alpha;padding-left:40px'>
<li>Energy intensity ratio for the organization.</li>
<li>Organization-specific metric (the denominator) chosen to calculate the ratio.</li>
<li>Types of energy included in the intensity ratio; whether fuel, electricity, heating, cooling, steam, or all.</li>
<li>Whether the ratio uses energy consumption within the organization, outside of it, or both.</li>
</ul><br>
Compilation requirements
2.5 When compiling the information specified in Disclosure 302-3, the reporting organization shall:<br>
<ul style='list-style-type:none;padding-left:40px'>
    <li>2.5.1. calculate the ratio by dividing the absolute energy consumption (the numerator) by the organization-specific metric (the denominator);</li>
    <li>2.5.2. if reporting an intensity ratio both for the energy consumed within the organization and outside of it, report these intensity ratios separately.</li>
</ul>
    </strong><br><hr>

  <div><strong>RECOMMENDATIONS</strong></div>
  <ul>
  <li>
  2.6 When compiling the information specified in Disclosure 302-3, the reporting organization
should, where it aids transparency or comparability over time, provide a breakdown of the
energy intensity ratio by:
<ul style='padding-left:40px'>
<li>
  2.6.1 business unit or facility;</li>
  <li>
  2.6.2 country;</li>
  <li>
  2.6.3 type of source (see definitions for the listing of non-renewable sources and
  renewable sources);</li>
  <li>
  2.6.4 type of activity.</li>
</ul>
</li>
</ul>
<br>
<b>GUIDANCE</b>
<p style="text-indent:30px;">
Intensity ratios can be provided for, among others:
<ul style='list-style-type:disc;padding-left:40px'>
<li>
products (such as energy consumed per unit produced);</li>
<li>services (such as energy consumed per function or per service);</li>
<li>sales (such as energy consumed per monetary unit of sales).</li>
</ul>
Organization-specific metrics (denominators) can include:
<ul style='list-style-type:disc;padding-left:40px'>
<li>
units of product;</li>
<li> production volume (such as metric tons, liters, or MWh);</li>
<li>
 size (such as m2 floor space);</li>
<li>number of full-time employees;</li>
<li> monetary units (such as revenue or sales).</li>
</ul>
<br>
<b>Background</b><br>
<p style="text-indent:30px;">
Energy intensity ratios define energy consumption in the context of an organization-specific metric.
</p>
<p style="text-indent:30px;">
These ratios express the energy required per unit of activity, output, or any other organization-
specific metric. Intensity ratios are often called normalized environmental impact data.</p>
<p style="text-indent:30px;">
In combination with the organization’s total energy consumption, reported in Disclosures 302-1
and 302-2, energy intensity helps to contextualize the organization’s efficiency, including in
relation to other organizations.</p>
</p>
  `,
  "302-4": `
  <div>
  <strong>Disclosure 302-4 Reduction of energy consumption</strong></div>
  <br>
  <div><strong>REQUIREMENTS</strong></div><br>
  <strong>
  The reporting organization shall report the following information:<br>
<ul style='list-style-type:lower-alpha;padding-left:40px'>
<li>Amount of reductions in energy consumption achieved as a direct result of conservation and efficiency initiatives, in joules or multiples.</li>
<li>Types of energy included in the reductions; whether fuel, electricity, heating, cooling, steam, or all.</li>
<li>Basis for calculating reductions in energy consumption, such as base year or baseline, including the rationale for choosing it.</li>
<li>Standards, methodologies, assumptions, and/or calculation tools used.</li>
</ul><br>
Compilation requirements

2.7 When compiling the information specified in Disclosure 302-4, the reporting organization shall:<br>
<ul style="list-style-type:none;padding-left:40px">
<li>2.7.1. exclude reductions resulting from reduced production capacity or outsourcing;</li>
<li>2.7.2. describe whether energy reduction is estimated, modeled, or sourced from direct measurements. If estimation or modeling is used, the organization shall disclose the methods used.</li>
</ul>
  </strong><br><hr>

  <div><strong>RECOMMENDATIONS</strong></div>

  2.8 When compiling the information specified in Disclosure 302-4, the reporting organization
should, if subject to different standards and methodologies, describe the approach to
selecting them.
  <br>
  <b>GUIDANCE</b>
  <p style="text-indent:30px;">
  The reporting organization can prioritize disclosing reduction initiatives that were implemented
in the reporting period, and that have the potential to contribute significantly to reductions. The
organization can describe reduction initiatives and their targets when reporting how it manages
this topic.</p>
Reduction initiatives can include:
<ul style='list-style-type:disc;padding-left:40px'>
  <li> process redesign;</li>
  <li> conversion and retrofitting of equipment;</li>
  <li>changes in behavior;</li>
  <li> operational changes.</li>
</ul>
<p style="text-indent:30px;">
The organization can report reductions in energy consumption by combining energy types, or
separately for fuel, electricity, heating, cooling, and steam.
</p>
<p style="text-indent:30px;">
The organization can also provide a breakdown of reductions in energy consumption by
individual initiatives or groups of initiatives
</p>
  `,
  "302-5": `
      <div>
  <strong>Disclosure 302-5 Reductions in energy requirements of
products and services</strong></div>
  <br>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The reporting organization shall report the following information:<br>
<ul style='list-style-type:lower-alpha;padding-left:40px'>
    <li>Reductions in energy requirements of sold products and services achieved during the reporting period, in joules or multiples.</li>
    <li>Basis for calculating reductions in energy consumption, such as base year or baseline, including the rationale for choosing it.</li>
    <li>Standards, methodologies, assumptions, and/or calculation tools used.</li>
</ul>
    </strong><br><hr>
  <div><strong>RECOMMENDATIONS</strong></div>
  <ul>
  <li>
      2.9 When compiling the information specified in Disclosure 302-5, the reporting organization
should:
  <ul style='list-style-type:disc;padding-left:40px'>
    <li>
    2.9.1 if subject to different standards and methodologies, describe the approach to
 selecting them;
    </li>
    <li>
   2.9.2  refer to industry use standards to obtain this information, where available (such
as fuel consumption of cars for 100 km at 90 km/h).
    </li>
  </ul>
  </li>
  </ul>
  <br>
  <b>GUIDANCE</b>
  <p style="text-indent:30px;">
  Use-oriented figures can include, for example, the energy requirements of a car or a computer.
Consumption patterns can include, for example, 10 percent less energy use per 100 km
travelled or per time unit (hour, average working day).
</p>
  `,
  "303-1": `
    <div>
  <strong>Disclosure 303-1 Interactions with water as a shared
resource</strong></div>
  <br>
   <div><strong>REQUIREMENTS</strong></div>
   <strong>
   The reporting organization shall report the following information:<br>
<ul style='list-style-type:lower-alpha;padding-left:40px'> 
<li>A description of how the organization interacts with water, including how and where water is withdrawn, consumed, and discharged, and the water-related impacts the organization has caused or contributed to, or that are directly linked to its operations, products, or services by its business relationships (e.g., impacts caused by runoff).</li>
<li>A description of the approach used to identify water-related impacts, including the scope of assessments, their timeframe, and any tools or methodologies used.</li>
<li>A description of how water-related impacts are addressed, including how the organization works with stakeholders to steward water as a shared resource, and how it engages with suppliers or customers with significant water-related impacts.</li>
<li>
An explanation of the process for setting any water-related goals and targets that are part of the organization’s approach to managing water and effluents, and how they relate to public policy and the local context of each area with water stress.</li>
</ul>
</strong><br><hr>

  <div><strong>RECOMMENDATIONS</strong></div>

  <ul>
  <li>
  The reporting organization should report the following additional information:
  <ul style='padding-left:40px'>
<li>
1.2.1 An overview of water use across the organization’s value chain;
</li>
<li>
1.2.2 A list of specific catchments where the organization causes significant water-
related impacts.
</li>
</ul>
</li>
  </ul>
  <br>
  <b>GUIDANCE</b>
  <p>
  Through its value chain, an organization can affect both the quality as well as the availability of
water. If the reporting organization has identified significant water-related impacts in the value
chain, which includes activities carried out by the organization, and by entities upstream and
downstream from the organization, it is required to report information about these impacts. See
Guidance to 3-3-b in GRI 3: Material Topics 2021 for more information about reporting an
organization’s involvement with negative impacts.
</p>
<p>
The description of how the organization interacts with water can include information on specific
catchments where water is withdrawn, consumed, and discharged, and information on what the
water is used for in activities carried out by the organization and by entities upstream and
downstream from the organization (e.g., for cooling, storage, incorporating in products, growing
crops).
</p>
<p>
In the context of this Standard, suppliers with significant water-related impacts may include
suppliers of water-intensive commodities or services, suppliers located in areas with water
stress, and/or suppliers with significant impacts on the local water environment and the related
local communities.
</p>
<p>
If applicable, the organization can describe its environmental impacts caused by runoff, and how
they are addressed. For example, runoff can carry high-nutrient and pollution loads due to the
organization’s activities, leading to eutrophication and other negative impacts on local
waterbodies.
</p>

<b>Guidance for Disclosure 303-1-b</b>
<br>
When assessing impacts, it is important that the organization consider its future impacts on
water quality and availability, as these factors can change over time.
<br>
Tools and methodologies for identifying impacts can include life cycle assessments,
environmental impact assessments, water footprint assessments, scenario analysis, and
stakeholder engagement. If information is estimated or modeled, rather than sourced from
direct measurements, the organization can explain its estimation or modeling methods.
<br>

<b>Guidance for Disclosure 303-1-c</b>
<br>
Working with stakeholders is critical for an organization to steward water as a shared resource
and account for the needs of other water users of the catchment. An organization’s stakeholders
can include:
<ul style='list-style-type:disc;padding-left:40px'>
<li> suppliers with significant water-related impacts;</li>
<li> users of its products and services;</li>
<li> local communities and action groups;</li>
<li> employees and other workers;</li>
<li> other water users in its sector or industry;</li>
<li> governments, regulators, and civil society organizations;</li>
<li> global initiatives, trade associations, and partnerships.</li>
</ul>
<br>
The organization can describe how it participates in discussions with stakeholders, the
frequency of this engagement, and its role in these discussions. Outcomes of working with
stakeholders can include, for example, collective target-setting for water use, increased
investment in infrastructure, policy advocacy, and capacity building and awareness raising.

<br>
When reporting on its engagement with suppliers, the organization can describe:
<ul style='list-style-type:disc;padding-left:40px'>
<li> how the organization engages with its suppliers to help them improve their water
management practices;</li>
<li> the number of suppliers engaged;</li>
<li> the outcomes of this engagement;</li>
<li> the amount of procurement that the proportion of engaged suppliers represents;</li>
<li> why information is not requested from suppliers with significant water-related impacts;</li>
<li> future plans and goals for working with suppliers to reduce water-related impacts.</li>
</ul>
<br>
Water impacts related to products and services might be addressed by, for example, improving
product design, providing information and advice on the responsible use of products and
services, and holding regular consultations with users.
<br>
<b>Guidance for Disclosure 303-1-d</b><br>
Meaningful targets for managing water-related impacts:
<ul style='list-style-type:disc;padding-left:40px'>
<li> account for the local context where water is withdrawn and discharged;</li>
<li> are scientifically informed by sustainable thresholds and the social context of a given
catchment;</li>
<li> align with public sector efforts, such as the water- related targets of the UN Sustainable
Development Goals, in particular Goal 6, or targets set by national and local government
institutions;</li>
<li> are informed by the advocacy of other stakeholders, such as civil society organizations, trade
associations, and action groups.</li>
</ul>

The organization can report its progress toward the goals and targets using 3-3-e-iii in GRI 3:
Material Topics 2021.
<br>
<b>Guidance for clause 1.2.1</b>
The organization can present the overview of water use across its value chain as a breakdown,
in graphic or written form, showing, for example, parts of the value chain where water
consumption is significant and the commodities to which it is related, or the percentage of
commodity sourcing that comes from catchments located in areas with water stress. The
organization is encouraged to include information about upstream as well as downstream water
use (e.g., use of water for consumer products, such as soaps, shampoos, and cleaning
solutions).
<br>
<b>Guidance for clause 1.2.2<b><br>
To identify catchments where it causes water-related impacts, the organization can use global
catchment data sets. These include the CEO Water Mandate 'Interactive Database of the World's
River Basins'1 , and the WWF 'HydroSHEDS'2
.
  `,
  "303-2": `
      <div>
  <strong>Disclosure 303-2 Management of water discharge-related impacts</strong></div>
  <br>
  <div><strong>REQUIREMENTS</strong></div>
  <strong>
  The reporting organization shall report the following information:<br>
<ul style='list-style-type:lower-alpha;padding-left:40px'>
<li>A description of any minimum standards set for the quality of effluent discharge, and how these minimum standards were determined, including:</li>
<ul style='list-style-type:lower-roman;padding-left:40px'>
<li>how standards for facilities operating in locations with no local discharge requirements were determined;</li>
<li>any internally developed water quality standards or guidelines;</li>
<li>any sector-specific standards considered;</li>
<li>whether the profile of the receiving waterbody was considered.</li>
</ul>
</ul>
</strong><br><hr>
  <div><strong>GUIDANCE</strong></div>
  <br>
  Minimum standards are those that go beyond regulatory requirements in controlling the quality
of effluent discharge.<br>
Water quality refers to the physical, chemical, biological, and taste-related characteristics of
water. It is a measure of water suitability for a given purpose or function, including its use as a
human right. Water quality standards help uphold water quality in order to protect ecosystems,
wildlife, and human health and welfare, and can be based on water properties, such as
temperature or pH value.<br>
The specific choice of water quality standards and parameters can vary depending on an
organization’s procucts, services, and facility locations, and can depend on national and/or
regional regulations, as well as the profile of the receiving waterbody.
  `,
  "303-3": `
    <div>
  <strong>Disclosure 303-3 Water withdrawal</strong></div>
  <br>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The reporting organization shall report the following information:<br>
<ul style='list-style-type:lower-alpha;padding-left:40px'>
<li>Total water withdrawal from all areas in megaliters, and a breakdown of this total by the following sources, if applicable:<br>
<ul style='list-style-type:lower-roman;padding-left:40px'>
    <li>Surface water;</li>
    <li>Groundwater;</li>
    <li>Seawater;</li>
    <li>Produced water;</li>
    <li>Third-party water.</li>
</ul>
</li>
<li>
Total water withdrawal from all areas with water stress in megaliters, and a breakdown of this total by the following sources, if applicable:<br>
<ul style='list-style-type:lower-roman;padding-left:40px'>
    <li>Surface water;</li>
    <li>Groundwater;</li>
    <li>Seawater;</li>
    <li>Produced water;</li>
    <li>Third-party water, and a breakdown of this total by the withdrawal sources listed in i-iv.</li>
</ul>
</li>
<li>
A breakdown of total water withdrawal from each of the sources listed in Disclosures 303-3-a and 303-3-b in megaliters by the following categories:
<ul style='list-style-type:lower-roman;padding-left:40px'>
<li>Freshwater (≤1,000 mg/L Total Dissolved Solids);</li>
<li>Other water (>1,000 mg/L Total Dissolved Solids).</li>
</ul>
</li>
<li>
Any contextual information necessary to understand how the data have been compiled, such as any standards, methodologies, and assumptions used.</li>
</ul>
<br>
Compilation requirements<br>
2.1 When compiling the information specified in Disclosure 303-3, the reporting organization shall use publicly available and credible tools and methodologies for assessing water stress in an area.
    </strong><br><hr>
  <div><strong>RECOMMENDATIONS</strong></div>
  <ul>
  <li>
  2.2 The reporting organization should report the following additional information:
  <ul style='list-style-type:disc;padding-left:40px'>
  <li>
2.2.1 A breakdown of total water withdrawal in megaliters by withdrawal source
categories listed in Disclosure 303-3, at each facility in areas with water stress;
</li>
<li>
2.2.2 Total water withdrawal in megaliters by suppliers with significant water-related
impacts in areas with water stress.
</li>
</ul>
</ul>
<br>
<b>GUIDANCE</b>
<b>Background</b>
<br>
The volume of water withdrawal from areas with water stress can indicate an organization’s
impacts in sensitive locations.
<br>
To learn more about locations where water-related impacts might be significant, and where
actions to address them are most needed, the reporting organization can also report the
information requested in Disclosure 303-3 for each facility in areas with water stress. This can
give stakeholders more confidence in the organization’s water stewardship efforts and
practices.<br>
<b>Guidance for Disclosure 303-3</b><br>
Surface water includes collected or harvested rainwater. Third-party water includes water
supplied by municipal water networks or other organizations.
<b>Guidance for Disclosure 303-3-b</b><br>
Water stress refers to the ability, or lack thereof, to meet the human and ecological demand for
water. Water stress can refer to the availability, quality, or accessibility of water.<br>
Publicly available and credible tools for assessing areas with water stress include the World
Resources Institute ‘Aqueduct Water Risk Atlas,' and the WWF 'Water Risk Filter'.<br>
Based on these tools, water stress in an area may be assessed using either of the following
indicators and their thresholds:<br>
<ul style='list-style-type:disc;padding-left:40px'>
<li> 
The ratio of total annual water withdrawal to total available annual renewable water supply
(i.e., baseline water stress) is high (40-80%) or extremely high (>80%)3 ;
</li>
<li>
The ratio of water consumption-to-availability (i.e., water depletion) is moderate (dry-year
depletion, where for at least 10% of the time, the monthly depletion ratio is >75%), high
(seasonal depletion, where for one month of the year on average, the depletion ratio is
>75%), or very high (ongoing depletion, where the depletion ratio on average is >75%).
</li>
</ul>
The organization may use these indicators even though they account only for quantity and not the
quality or accessibility of water as per the inclusive approach to the definition of water stress.<br>
The organization can complement the results from these tools with their own assessments, to
provide more granular local-level data. Water stress in an area may be measured at catchment
level at a minimum.<br>
<b>Guidance for Disclosure 303-3-b-v</b><br>
If water is supplied by a third party, the organization is required to request information about its
withdrawal sources, listed in Disclosures 303-3-b-i to 303-3-b-iv, from the third-party water
supplier. The organization can report any additional information about third-party water, such as
who the third-party water suppliers are and the volume of water supplied by them.<br>
<b>Guidance for Disclosure 303-3-c<b></br>
The organization is required to provide a breakdown of the water withdrawn from each of the
sources listed in Disclosures 303-3-a and 303-3-b (surface water, groundwater, seawater,
produced water, third-party water) by the categories freshwater and other water. The organization
is only required to provide this breakdown for the sources it has withdrawn water from. If all
water withdrawn from a source belongs only to one category (i.e., to freshwater or to other
water), the organization can report the volume for the remaining category as zero. For example, if
all the withdrawn seawater belongs to the other water category, the organization can report the
volume of freshwater under this source as zero.<br>

Other water constitutes any water that has a concentration of total dissolved solids higher than
1,000 mg/L. Other water is therefore all water that does not fall into the freshwater category.<br>
The organization is, at a minimum, required to report a figure for other water withdrawal for each of
the sources listed in Disclosures 303-3-a and 303-3-b.<br>
The organization can additionally report any further breakdowns for other water withdrawal
based on its water management and reporting practices, as long as it explains the approach
used to define water quality using Disclosure 303-3-d. The organization can report additional
information on how water quality has been determined, including consideration of the potential
value of water to its users, as well as any absolute physical and/or chemical criteria used.<br>
<b>Guidance for clause 2.2.1</b><br>
To compile this information, the organization can use the following approach: (a) determine
which facilities are located in areas with water stress, (b) for each of these facilities, report a
breakdown of the total water withdrawal by surface water, groundwater, seawater, produced
water, and third-party water.<br>
<b>Guidance for clause 2.2.2</b><br>
To compile this information, the organization can use the following approach: (a) determine
which suppliers are located in areas with water stress, (b) determine which of these suppliers
cause significant water-related impacts, (c) add up the total water withdrawal of each of these
suppliers, (d) report the sum.
  `,
  "303-4": `
    <div>
  <strong>Disclosure 303-4 Water discharges</strong></div>
  <br>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The reporting organization shall report the following information:<br>
<ul style='list-style-type:lower-alpha;padding-left:40px'>
<li>
Total water discharge to all areas in megaliters, and a breakdown of this total by the following types of destination, if applicable:
<ul style='list-style-type:lower-roman;padding-left:40px'>
Surface water;
Groundwater;
Seawater;
Third-party water, and the volume of this total sent for use to other organizations, if applicable.</ul></li>
<li>
A breakdown of total water discharge to all areas in megaliters by the following categories:
<ul style='list-style-type:lower-roman;padding-left:40px'>
Freshwater (≤1,000 mg/L Total Dissolved Solids);
Other water (>1,000 mg/L Total Dissolved Solids).
</ul>
</li>
<li>
Total water discharge to all areas with water stress in megaliters, and a breakdown of this total by the following categories:
<ul style='list-style-type:lower-roman;padding-left:40px'>
Freshwater (≤1,000 mg/L Total Dissolved Solids);
Other water (>1,000 mg/L Total Dissolved Solids).
</ul>
</li>
<li>
Priority substances of concern for which discharges are treated, including:<br>
<ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>
how priority substances of concern were defined, and any international standard, authoritative list, or criteria used;
the approach for setting discharge limits for priority substances of concern;</li>
<li>
number of incidents of non-compliance with discharge limits.</li>
</ul>
</li>
<li>
Any contextual information necessary to understand how the data have been compiled, such as any standards, methodologies, and assumptions used.</li>
</ul><br>
Compilation requirements<br>
2.3 When compiling the information specified in Disclosure 303-4, the reporting organization shall use publicly available and credible tools and methodologies for assessing water stress in an area.

    </strong>
  <div><strong>RECOMMENDATIONS</strong></div>
  <ul><li>
  2.4 The reporting organization should report the following additional information:
<ul  style='padding-left:40px'>
<li>
2.4.1 The number of occasions on which discharge limits were exceeded;</li>
<li>
2.4.2 A breakdown of total water discharge to all areas in megaliters by level of
treatment, and how the treatment levels were determined;</li>
<li>
2.4.3 Percentage of suppliers with significant water-related impacts from water
discharge that have set minimum standards for the quality of their effluent
discharge.</li>
</ul>
</li>
  </ul>

  <b>GUIDANCE</b><br>
  <b>Background</b>
  Quantifying the volume of water discharge can help an organization understand its negative
impacts on the receiving waterbody.<br>
The relationship between water discharge and negative impacts is not linear. An increase in the
total volume of water discharge does not necessarily correspond to greater negative impacts,
since these impacts depend on the quality of the water discharge and the sensitivity of the
receiving waterbody. An organization with a high volume of water discharge, but also a high level
of treatment and strict quality standards, can have positive impacts on the receiving waterbody.
<br>
To learn more about locations where water-related impacts might be significant, and where
actions to address them are most needed, the reporting organization can also report the
information requested in Disclosure 303-4 for each facility in areas with water stress.<br>
<b>Guidance for Disclosure 303-4</b><br>
See Guidance for Disclosure 303-3-b for how to assess areas with water stress.
<b>Guidance for Disclosure 303-4-a-iv</b><br>
An example of third-party water discharge is when an organization sends water and effluents to
other organizations for use. In these instances, the organization is required to report the volume of
this water discharge separately.
<b>Guidance for Disclosures 303-4-b and 303-4-c</b><br>
The organization is required to provide a breakdown of the water discharged to all areas and to
all areas with water stress by the categories freshwater and other water. Other water constitutes
any water that has a concentration of total dissolved solids higher than 1,000 mg/L. Other water
is therefore all water that does not fall into the freshwater category.<br>
The organization is, at a minimum, required to report a figure for other water discharged. The
organization can additionally report any further breakdowns for other water discharge based on
its water management and reporting practices, as long as it explains the approach used to
define water quality using Disclosure 303-4-e. The organization can report additional
information on how water quality has been determined, including consideration of the potential
value of water to its users, as well as any absolute physical and/or chemical criteria used.<br>
<b>Guidance for Disclosure 303-4-d</b><br>
In the context of this Standard, substances of concern are those that cause irreversible damage
to the waterbody, ecosystem, or human health.<br>
Discharge limits for substances of concern can be based on regulation and/or other factors
determined by an organization. In countries where no regulations for discharge limits are
available, the organization can develop its own discharge limits.<br>
‘Discharge consent’ is the permission granted to an organization, allowing it to discharge a set
amount of a substance. The organization can report any unauthorized discharges that exceed
these limits using Disclosure 303-4-d. The organization can also describe any plans to reduce
unauthorized discharges in the future.<br>
<b>Guidance for clause 2.4.2</b>
Reporting water discharge by level of treatment can provide insight into the effort an organization
is making to improve the quality of its water discharge. When reporting how the treatment levels
were determined, the organization is expected to include the reasons why a certain level of
treatment was set.<br>
The level of treatment can be reported for any water or effluents at the point of discharge,
whether treated by the organization onsite or sent to a third party for treatment.<br>
Water treatment involves physical, chemical or biological processes that improve water quality
by removing solids, pollutants, and organic matter from water and effluents. Minimum
requirements for treatment might be specified in national, state, or local legislation; however, the
organization is expected to consider its overall water discharge impacts and the needs of other
water users in setting treatment levels.<br>
The organization can break down its water discharge by the following treatment levels:
<ul style='list-style-type:disc;padding-left:40px'>
<li>
 Primary treatment, which aims to remove solid substances that settle or float on the water
surface;</li>
<li>
Secondary treatment, which aims to remove substances and materials that have remained
in the water, or are dissolved or suspended in it;</li>
<li>
 Tertiary treatment, which aims to upgrade water to a higher level of quality before it is
discharged. It includes processes that remove, for example, heavy metals, nitrogen, and
phosphorus.</li>
</ul>
An organization might withdraw and discharge water of good quality that does not require
treatment. If so, the organization can explain this in its reported information.<br>
<b>Guidance for clause 2.4.3</b>
Minimum standards are those that go beyond regulatory requirements in controlling the quality
of effluent discharge. For more information on water quality standards, see Disclosure 303-2 in
the Topic management disclosures section.<br>
To compile this information, the organization can use the following approach: (a) determine the
number of suppliers with significant water-related impacts from water discharge, (b) determine
how many of these suppliers have set minimum standards for the quality of their effluent
discharge, (c) calculate the percentage using the following formula:<br>
Percentage of suppliers with significant water-related impacts from water
discharge that have set minimum standards for the quality of their effluent
discharge = <br>
(Number of suppliers that have set minimum standards for the quality of their
effluent discharge/Number of suppliers with significant water-related impacts from water discharge)*100<br>
  `,
  "303-5": `
    <div>
  <strong>Disclosure 303-5 Water consumption</strong></div>
  <br>
  <div><strong>REQUIREMENTS </strong></div>
  <strong>
  The reporting organization shall report the following information:<br>
<ul style='list-style-type:lower-alpha;padding-left:40px'>
<li>Total water consumption from all areas in megaliters.</li>
<li>Total water consumption from all areas with water stress in megaliters.</li>
<li>Change in water storage in megaliters, if water storage has been identified as having a significant water-related impact.</li>
<li>Any contextual information necessary to understand how the data have been compiled, such as any standards, methodologies, and assumptions used, including whether the information is calculated, estimated, modeled, or sourced from direct measurements, and the approach taken for this, such as the use of any sector-specific factors.</li>
</ul>
  </strong><br><hr>
  <div><strong>RECOMMENDATIONS</strong></div>
  2.5 The reporting organization should report the following additional information:
    <ul style='padding-left:40px'>
    <li>2.5.1 total water consumption in megaliters at each facility in areas with water stress;</li>
    <li>2.5.2 Total water consumption in megaliters by suppliers with significant water-related
impacts in areas with water stress.</li>
    </ul>
    <br>
    <b>GUIDANCE</b><br>
    <b>Background</b>
    Water consumption measures water used by an organization such that it is no longer available
for use by the ecosystem or local community in the reporting period. Reporting the volume of
water consumption can help the organization understand the overall scale of its impact due to
water withdrawal on downstream water availability.<br>
<b>Guidance for Disclosure 303-5<b>
See Guidance for Disclosure 303-3-b for how to assess areas with water stress.
If the reporting organization cannot directly measure water consumption, it may calculate this
using the following formula:
<div style='padding:5px 0px;border:1px solid black'>
Water consumption = Total water withdrawal - Total water discharge
</div>
<b>Guidance for Disclosure 303-5-c</b>
If the water in storage has been identified as having a significant water-related impact, the
organization is required to report change in water storage. The organization may calculate
change in water storage using the following formula:<br>
<div style='padding:10px 0px;border:1px solid black'>
Change in water storage =
Total water storage at the end of the reporting period -
Total water storage at the beginning of the reporting period
</div>

<b>Guidance for clause 2.5.1</b><br>
To compile this information, the organization can use the following approach: (a) determine
which facilities are located in areas with water stress, (b) for each of these facilities, report the
total water consumption.<br>
<b>Guidance for clause 2.5.2</b><br>
To compile this information, the organization can use the following approach: (a) determine
which suppliers are located in areas with water stress, (b) determine which of these suppliers
cause significant water-related impacts, (c) add up the total water consumption of each of these
suppliers, (d) report the sum
  `,
  "305-1": `
  <div>
  <strong>Disclosure 305-1 Direct (Scope 1) GHG emissions</strong></div>
  <br>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The reporting organization shall report the following information:
<ul style='list-style-type:lower-alpha;padding-left:40px'>
<li>Gross direct (Scope 1) GHG emissions in metric tons of CO2 equivalent.</li>
<li>Gases included in the calculation; whether CO2 , CH4 , N2 O, HFCs, PFCs, SF6 , NF3 , or all.</li>
<li>Biogenic CO2 emissions in metric tons of CO2 equivalent.</li>
<li>Base year for the calculation, if applicable, including:
<ul style='list-style-type:lower-roman;padding-left:40px'>
<li>the rationale for choosing it;</li>
<li>emissions in the base year;</li>
<li>the context for any significant changes in emissions that triggered recalculations of base year emissions.</li>
</ul>
</li>
<li>
Source of the emission factors and the global warming potential (GWP) rates used, or a reference to the GWP source.</li>
<li>
Consolidation approach for emissions; whether equity share, financial control, or operational control.</li>
<li>
Standards, methodologies, assumptions, and/or calculation tools used.</li>
</ul>
Compilation requirements

2.1 When compiling the information specified in Disclosure 305-1, the reporting organization shall:
<ul  style='list-style-type:none;padding-left:40px'>
<li>
2.1.1. exclude any GHG trades from the calculation of gross direct (Scope 1) GHG emissions;
</li>
</li>
2.1.2. report biogenic emissions of CO2 from the combustion or biodegradation of biomass separately from the gross direct (Scope 1) GHG emissions. Exclude biogenic emissions of other types of GHG (such as CH4 and N2 O), and biogenic emissions of CO2 that occur in the life cycle of biomass other than from combustion or biodegradation (such as GHG emissions from processing or transporting biomass).</li>
</ul>

    </strong>
  <div><strong>RECOMMENDATIONS</strong></div>
When compiling the information specified in Disclosure 305-1, the reporting organization should:
<ul style="padding-left:40px">
<li>
2.2.1  apply emission factors and GWP rates consistently for the data disclosed; 2.2.2  use the GWP rates from the IPCC assessment reports based on a 100-year timeframe; </li>
<li>
 2.2.3  select a consistent approach for consolidating direct (Scope 1) and energy indirect (Scope 2) GHG emissions; 
 choosing from the equity share, financial control, or operational control methods outlined in the ‘GHG Protocol Corporate Standard’; 
 </li>
 <li>
2.2.4 if subject to different standards and methodologies, describe the approach to selecting them; 
</li>
<li>
2.2.5 where it aids transparency or comparability over time, provide a breakdown of the direct (Scope 1) GHG emissions by: 
<ul  style="padding-left:40px">
<li>
2.2.5.1 business unit or facility; 
</li>
<li>
2.2.5.2 country; </li>
<li>
2.2.5.3 type of source (stationary combustion, process, fugitive); </li>
<li>
2.2.5.4 type of activity.
</li>
</ul>
</li>
</ul>
<br>
<div><strong>GUIDANCE</strong></div>
<p style="text-indent:30px;">
Direct (Scope 1) GHG emissions can come from the following sources owned or controlled by an organization:</p>
<ul style="padding-left:40px;list-style-type:disc">
<li>
Generation of electricity, heating, cooling and steam: these emissions result from combustion of fuels in stationary sources, such as boilers, furnaces, and turbines – and from other combustion processes such as flaring;
Physical or chemical processing: most of these emissions result from the manufacturing or processing of chemicals and materials, such as cement, steel, aluminum, ammonia, and waste processing;
</li>
<li>
Transportation of materials, products, waste, workers, and passengers: these emissions result from the combustion of fuels in mobile combustion sources owned or controlled by the organization, such as trucks, trains, ships, airplanes, buses, and cars;
</li>
<li>
Fugitive emissions: these are emissions that are not physically controlled but result from intentional or unintentional releases of GHGs. These can include equipment leaks from joints, seals, packing, and gaskets; methane emissions (e.g., from coal mines) and venting; HFC emissions from refrigeration and air conditioning equipment; and methane leakages (e.g., from gas transport).
</li>
</ul>
<p style="text-indent:30px;">
Methodologies used to calculate the direct (Scope I) GHG emissions can include:

</p>
<ul style="padding-left:40px;list-style-type:disc">
<li>
direct measurement of energy source consumed (coal, gas) or losses (refills) of cooling systems and conversion to GHG (CO2 equivalents);
</li>
<li>
mass balance calculations;
</li>
<li>
calculations based on site-specific data, such as for fuel composition analysis;
</li>
<li>
calculations based on published criteria, such as emission factors and GWP rates;</li>
<li>
direct measurements of GHG emissions, such as continuous online analyzers;</li>
<li>
estimations.</li>
</ul>
<p style="text-indent:30px;">
If estimations are used due to a lack of default figures, the reporting organization can indicate the basis and assumptions on which figures were estimated.
</p>
<p style="text-indent:30px;">
For recalculations of prior year emissions, the organization can follow the approach in the ‘GHG Protocol Corporate Standard’.
</p>
<p style="text-indent:30px;">
The chosen emission factors can originate from mandatory reporting requirements, voluntary reporting frameworks, or industry groups.
</p>
<p style="text-indent:30px;">
Estimates of GWP rates change over time as scientific research develops. GWP rates from the Second Assessment Report of the Intergovernmental Panel on Climate Change (IPCC) are used as the basis for international negotiations under the ‘Kyoto Protocol’. Thus, such rates can be used for disclosing GHG emissions where it does not conflict with national or regional reporting requirements. The organization can also use the latest GWP rates from the most recent IPCC assessment report.
</p>
<p style="text-indent:30px;">
The organization can combine Disclosure 305-1 with Disclosures 305-2 (energy indirect/Scope 2 GHG emissions) and 305-3 (other indirect/Scope 3 GHG emissions) to disclose total GHG emissions.
Further details and guidance are available in the ‘GHG Protocol Corporate Standard’.</p>`,
  "305-2": `
  <div>
 <strong> Disclosure 305-2 Energy indirect (Scope 2) GHG emissions</strong></div>
  <br>
   <strong>
REQUIREMENTS
</strong></div>
<strong>
The reporting organization shall report the following information:<br>
<ul style='list-style-type:lower-alpha;padding-left:40px'>
<li>
Gross location-based energy indirect (Scope 2) GHG emissions in metric tons of CO2 equivalent.</li>
<li>
If applicable, gross market-based energy indirect (Scope 2) GHG emissions in metric tons of CO2 equivalent.</li>
<li>
If available, the gases included in the calculation; whether CO2 , CH4 , N2 O, HFCs, PFCs, SF6 , NF3 , or all.</li>
<li>
Base year for the calculation, if applicable, including:
<ul style='list-style-type:lower-roman;padding-left:40px'>
<li>the rationale for choosing it;</li>
<li>emissions in the base year;</li>
<li>the context for any significant changes in emissions that triggered recalculations of base year emissions.</li>
</ul>
</li>
<li>
Source of the emission factors and the global warming potential (GWP) rates used, or a reference to the GWP source.</li>
<li>
Consolidation approach for emissions; whether equity share, financial control, or operational control.</li>
<li>
Standards, methodologies, assumptions, and/or calculation tools used.</li>
</ul>
Compilation requirements<br>
2.3 When compiling the information specified in Disclosure 305-2, the reporting organization shall:<br>
<ul style='list-style-type:none;padding-left:40px'>
<li>2.3.1. exclude any GHG trades from the calculation of gross energy indirect (Scope 2) GHG emissions;</li>
<li>2.3.2. exclude other indirect (Scope 3) GHG emissions that are disclosed as specified in Disclosure 305-3;</li>
<li>2.3.3. account and report energy indirect (Scope 2) GHG emissions based on the location- based method, if it has operations in markets without product or supplier-specific data;</li>
<li>2.3.4 account and report energy indirect (Scope 2) GHG emissions based on both the location-based and market-based methods, if it has any operations in markets providing product or supplier-specific data in the form of contractual instruments.</li>
</ul>
</strong><br><hr>

  <div>
 <strong>
RECOMMENDATIONS
</strong></div>
<p style="text-indent:30px;">
When compiling the information specified in Disclosure 305-2, the reporting organization
should:
</p>
<ul style="padding-left:40px">
<li>
2.4.1 apply emission factors and GWP rates consistently for the data disclosed;
use the GWP rates from the IPCC assessment reports based on a 100-year
timeframe;
</li>
<li>
2.4.2 select a consistent approach for consolidating direct (Scope 1) and energy
indirect (Scope 2) GHG emissions, choosing from the equity share, financial
control, or operational control methods outlined in the ‘GHG Protocol Corporate
Standard’;
</li>
<li>
2.4.3 select a consistent approach for consolidating direct (Scope 1) and energy
indirect (Scope 2) GHG emissions, choosing from the equity share, financial
control, or operational control methods outlined in the ‘GHG Protocol Corporate
Standard’;
</li>
<li>
2.4.4 if subject to different standards and methodologies, describe the approach to
selecting them;
</li>
<li>
2.4.5 where it aids transparency or comparability over time, provide a breakdown of the
energy indirect (Scope 2) GHG emissions by:
<ul style="padding-left:40px">
    <li>2.4.5.1 business unit or facility </li>
        <li>2.4.5.2 country;</li>
        <li>2.4.5.3 type of source (electricity, heating, cooling, and steam);</li>
        <li>2.4.5.4 type of activity.</li>
</ul>
</li>
</ul>
<div> <strong>
GUIDANCE</strong></div>
<p style="text-indent:30px;">
Energy indirect (Scope 2) GHG emissions include, but are not limited to, the CO2 emissions
from the generation of purchased or acquired electricity, heating, cooling, and steam consumed
by an organization – disclosed as specified in Disclosure 302-1 of GRI 302: Energy 2016. For
many organizations, the energy indirect (Scope 2) GHG emissions that result from the
generation of purchased electricity can be much greater than their direct (Scope 1) GHG
emissions.
</p>
<p style="text-indent:30px;">
The ‘GHG Protocol Scope 2 Guidance’ requires organizations to provide two distinct Scope 2
values: a location-based and a market-based value. A location- based method reflects the
average GHG emissions intensity of grids on which energy consumption occurs, using mostly
grid-average emission factor data. A market-based method reflects emissions from electricity
that an organization has purposefully chosen (or its lack of choice). It derives emission factors
from contractual instruments, which include any type of contract between two parties for the sale
and purchase of energy bundled with attributes about the energy generation, or for unbundled
attribute claims.
</p>
<p style="text-indent:30px;">
The market-based method calculation also includes the use of a residual mix, if the organization
does not have specified emissions-intensity from its contractual instruments. This helps prevent
double counting between consumers’ market-based method figures. If a residual mix is
unavailable, the organization can disclose this and use grid-average emission factors as a
proxy (which can mean that the location-based and market- based are the same number until
information on the residual mix is available).
</p>
<p style="text-indent:30px;">
The reporting organization can apply the Quality Criteria in the ‘GHG Protocol Scope 2 Guidance’
so that contractual instruments convey GHG emission rate claims and to prevent double
counting.
</p>
<p style="text-indent:30px;">
For recalculations of prior year emissions, the organization can follow the approach in the ‘GHG
Protocol Corporate Standard’.
</p>
<p style="text-indent:30px;">
The chosen emission factors can originate from mandatory reporting requirements, voluntary
reporting frameworks, or industry groups.
</p>
<p style="text-indent:30px;">
Estimates of GWP rates change over time as scientific research develops. GWP rates from the
Second Assessment Report of the IPCC are used as the basis for international negotiations
under the ‘Kyoto Protocol’. Thus, such rates can be used for disclosing GHG emissions where it
does not conflict with national or regional reporting requirements. The organization can also use
the latest GWP rates from the most recent IPCC assessment report.
</p>
<p style="text-indent:30px;">
The organization can combine Disclosure 305-2 with Disclosures 305-1 (direct/Scope 1 GHG
emissions) and 305-3 (other indirect/Scope 3 GHG emissions) to disclose total GHG
emissions.
</p>
<p style="text-indent:30px;">
Further details and guidance are available in the ‘GHG Protocol Corporate Standard’. Details on
the location-based and market-based methods are available in the ‘GHG Protocol Scope 2
Guidance</p>’.
  `,
  "305-3": `
  <div><strong>
  Disclosure 305-3 Other indirect (Scope 3) GHG emissions</strong></div>
  <br>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The reporting organization shall report the following information:<br>
<ul style='list-style-type:lower-alpha;padding-left:40px'>
<li>Gross other indirect (Scope 3) GHG emissions in metric tons of CO2 equivalent.</li>
<li>If available, the gases included in the calculation; whether CO2 , CH4 , N2 O, HFCs, PFCs, SF6 , NF3 , or all.</li>
<li>Biogenic CO2 emissions in metric tons of CO2 equivalent.</li>
<li>Other indirect (Scope 3) GHG emissions categories and activities included in the calculation.</li>
<li>
Base year for the calculation, if applicable, including:<br>
<ul  style='list-style-type:lower-roman;padding-left:40px'>
    <li>the rationale for choosing it;</li>
    <li>emissions in the base year;</li>

<li>the context for any significant changes in emissions that triggered recalculations of base year emissions.</li>
</ul>
</li>
Source of the emission factors and the global warming potential (GWP) rates used, or a reference to the GWP source.
Standards, methodologies, assumptions, and/or calculation tools used.
</ul>
<br>
Compilation requirements<br>
2.5 When compiling the information specified in Disclosure 305-3, the reporting organization shall:<br>
<ul style='list-style-type:none;padding-left:40px'>
<li>2.5.1. exclude any GHG trades from the calculation of gross other indirect (Scope 3) GHG emissions;</li>
<li>2.5.2. exclude energy indirect (Scope 2) GHG emissions from this disclosure. Energy indirect (Scope 2) GHG emissions are disclosed as specified in Disclosure 305-2;</li>
<li>2.5.3 report biogenic emissions of CO2 from the combustion or biodegradation of biomass that occur in its value chain separately from the gross other indirect (Scope 3) GHG emissions. Exclude biogenic emissions of other types of GHG (such as CH4 and N2 O), and biogenic emissions of CO2 that occur in the life cycle of biomass other than from combustion or biodegradation (such as GHG emissions from processing or transporting biomass).</li>
</ul>
    </strong><br><hr>
  <div><strong>
  RECOMMENDATIONS</strong></div>

<p style="text-indent:30px;">When compiling the information specified in Disclosure 305-3, the reporting organization
should:</p>
<ul style="padding-left:40px">
<li>
2.6.1 apply emission factors and GWP rates consistently for the data disclosed;
</li>
<li>

2.6.2 use the GWP rates from the IPCC assessment reports based on a 100-year
timeframe;</li>
<li>
2.6.3 if subject to different standards and methodologies, describe the approach to
selecting them;
</li>
<li>
2.6.4 list other indirect (Scope 3) GHG emissions, with a breakdown by upstream and
downstream categories and activities;
</li>
<li>
2.6.5 where it aids transparency or comparability over time, provide a breakdown of the other indirect (Scope 3)
GHG emissions by:
<ul style="padding-left:40px">
<li>
2.6.5.1 business unit or facility;
</li>
<li>
2.6.5.2 country;
</li>
<li>
2.6.5.3 type of source;
</li>
<li>
2.6.5.4 type of activity.
</li>
</ul>
</ul>
<div><strong>
GUIDANCE</strong></div>
<p style="text-indent:30px;">
Other indirect (Scope 3) GHG emissions are a consequence of an organization’s activities, but
occur from sources not owned or controlled by the organization. Other indirect (Scope 3) GHG
emissions include both upstream and downstream emissions. Some examples of Scope 3
activities include extracting and producing purchased materials; transporting purchased fuels in
vehicles not owned or controlled by the organization; and the end use of products and services.
</p>
<p style="text-indent:30px;">
Other indirect emissions can also come from the decomposing of the organization’s waste.
Process-related emissions during the manufacture of purchased goods and fugitive emissions
in facilities not owned by the organization can also produce indirect emissions.
For some organizations, GHG emissions that result from energy consumption outside of the
organization can be much greater than their direct (Scope 1) or energy indirect (Scope 2) GHG
emissions.
</p>
<p style="text-indent:30px;">
The reporting organization can identify other indirect (Scope 3) GHG emissions by assessing
which of its activities’ emissions:
</p>
<ul style="padding-left:40px;list-style-type:disc">
<li>
contribute significantly to the organization’s total anticipated other indirect (Scope 3) GHG
emissions;
</li>
<li>
offer potential for reductions the organization can undertake or influence;</li>
<li>
contribute to climate change-related risks, such as financial, regulatory, supply chain,
product and customer, litigation, and reputational risks;
</li>
<li>

are deemed material by stakeholders, such as customers, suppliers, investors, or civil
society;
</li>
<li>
result from outsourced activities previously performed in-house, or that are typically
performed in-house by other organizations in the same sector;
</li>
<li>
have been identified as significant for the organization’s sector;</li>
<li>
meet any additional criteria for determining relevance, developed by the organization or by
organizations in its sector.</li>
</ul>
<p style="text-indent:30px;">
The organization can use the following upstream and downstream categories and activities
from the ‘GHG Protocol Corporate Value Chain Standard’.</p>

<b>Upstream categories</b><br>
1. Purchased goods and services<br>
2. Capital goods<br>
3. Fuel- and energy-related activities (not included in Scope 1 or Scope 2)<br>
4. Upstream transportation and distribution<br>
5. Waste generated in operations<br>
6. Business travel<br>
7. Employee commuting<br>
8. Upstream leased assets<br>
Other upstream
<br>
<b>Downstream categories</b><br>
9. Downstream transportation and distribution<br>
10. Processing of sold products<br>
11. Use of sold products<br>
12. End-of-life treatment of sold products<br>
13. Downstream leased assets<br>
14. Franchises<br>
15. Investments<br>
Other downstream<br>
<p style="text-indent:30px;">
For each of these categories and activities, the organization can provide a figure in CO2 equivalent or explain why certain data are not included.</p>
<p style="text-indent:30px;">
For recalculations of prior year emissions, the organization can follow the approach in the ‘GHG
Protocol Corporate Value Chain Standard’.</p>
<p style="text-indent:30px;">
The chosen emission factors can originate from mandatory reporting requirements, voluntary
reporting frameworks, or industry groups.</p>
<p style="text-indent:30px;">
Estimates of GWP rates change over time as scientific research develops. GWP rates from the
Second Assessment Report of the IPCC are used as the basis for international negotiations
under the ‘Kyoto Protocol’. Thus, such rates can be used for disclosing GHG emissions where it
does not conflict with national or regional reporting requirements. The organization can also use
the latest GWP rates from the most recent IPCC assessment report.
</p>
<p style="text-indent:30px;">
The organization can combine Disclosure 305-3 with Disclosures 305-1 (direct/Scope 1 GHG
emissions) and 305-2 (energy indirect/Scope 2 GHG emissions) to disclose total GHG
emissions.</p>

  `,
  "305-4": `
    <div><strong>Disclosure 305-4 GHG emissions intensity</strong></div>
  <br>
    <div><strong>REQUIREMENTS</strong></div>
  <strong>  The reporting organization shall report the following information:<br>
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
    <li>GHG emissions intensity ratio for the organization.</li>
    <li>Organization-specific metric (the denominator) chosen to calculate the ratio.</li>
    <li>Types of GHG emissions included in the intensity ratio; whether direct (Scope 1), energy indirect (Scope 2), and/or other indirect (Scope 3).</li>
    <li>Gases included in the calculation; whether CO2 , CH4 , N2 O, HFCs, PFCs, SF6 , NF3 , or all.</li>
</ul><br>
Compilation requirements<br>
2.7 When compiling the information specified in Disclosure 305-4, the reporting organization shall:
<ul style='list-style-type:none;padding-left:40px'>
<li>calculate the ratio by dividing the absolute GHG emissions (the numerator) by the organization-specific metric (the denominator);</li>
<li>
if reporting an intensity ratio for other indirect (Scope 3) GHG emissions, report this intensity ratio separately from the intensity ratios for direct (Scope 1) and energy indirect (Scope 2) emissions.</li>
</ul><br></strong>
    <div><strong>RECOMMENDATIONS</strong></div>
2.8 When compiling the information specified in Disclosure 305-4, the reporting organization should, where it aids transparency or comparability over time, provide a breakdown of the GHG emissions intensity ratio by:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
    <li>2.8.1. business unit or facility;</li>
    <li>2.8.2. country;</li>
    <li>2.8.3. type of source;</li>
    <li>2.8.4. type of activity.</li>
</ul>
    <div><strong>GUIDANCE</strong></div>
    <strong>Guidance for Disclosure 305-4</strong>
Intensity ratios can be provided for, among others:<br>
<ul  style='list-style-type:disc;padding-left:40px'>
<li>products (such as metric tons of CO2 emissions per unit produced);</li>
<li>services (such as metric tons of CO2 emissions per function or per service);</li>
<li>sales (such as metric tons of CO2 emissions per sales).</li>
</ul>
Organization-specific metrics (denominators) can include:<br>
<ul  style='list-style-type:disc;padding-left:40px'>
<li>units of product;</li>
<li>production volume (such as metric tons, liters, or MWh);</li>
<li>size (such as m2 floor space);</li>
<li>number of full-time employees;</li>
<li>monetary units (such as revenue or sales).</li>
</ul>
The reporting organization can report an intensity ratio for direct (Scope 1) and energy indirect (Scope 2) GHG emissions combined, using the figures reported in Disclosures 305-1 and 305- 2.<br>
<b>Background</b><br>
Intensity ratios define GHG emissions in the context of an organization-specific metric. Many organizations track environmental performance with intensity ratios, which are often called normalized environmental impact data.<br>
GHG emissions intensity expresses the amount of GHG emissions per unit of activity, output, or any other organization-specific metric. In combination with an organization’s absolute GHG emissions, reported in Disclosures 305-1, 305-2, and 305-3, GHG emissions intensity helps to contextualize the organization’s efficiency, including in relation to other organizations.
  `,
  "305-5": `
     <div><strong>Disclosure 305-5 Reduction of GHG emissions
</strong></div>
  <br>
  <div><strong>REQUIREMENTS</strong></div>
 <strong> The reporting organization shall report the following information:<br>
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>GHG emissions reduced as a direct result of reduction initiatives, in metric tons of CO2 equivalent.</li>
<li>Gases included in the calculation; whether CO2 , CH4 , N2 O, HFCs, PFCs, SF6 , NF3 , or all.</li>
<li>Base year or baseline, including the rationale for choosing it.</li>
<li>Scopes in which reductions took place; whether direct (Scope 1), energy indirect (Scope 2), and/or other indirect (Scope 3).</li>
<li>Standards, methodologies, assumptions, and/or calculation tools used.</li>
</ul><br>
Compilation requirements<br>
2.9 When compiling the information specified in Disclosure 305-5, the reporting organization shall:
<ul style='list-style-type:none;padding-left:40px'>
<li>2.9.1. exclude reductions resulting from reduced production capacity or outsourcing;</li>
<li>2.9.2. use the inventory or project method to account for reductions;</li>
<li>2.9.3. calculate an initiative’s total reductions of GHG emissions as the sum of its associated primary effects and any significant secondary effects;</li>
<li>2.9.4. if reporting two or more Scope types, report the reductions for each separately;</li>
<li>2.9.5. report reductions from offsets separately.</li>
</ul>
</strong><br>
  <div><strong>RECOMMENDATIONS</strong></div>
2.10 When compiling the information specified in Disclosure 305-5, the reporting organization should, if subject to different standards and methodologies, describe the approach to selecting them.<br>
  <div><strong>GUIDANCE</strong></div>
<b>Guidance for Disclosure 305-5</b>
The reporting organization can prioritize disclosing reduction initiatives that were implemented in the reporting period, and that have the potential to contribute significantly to reductions. The organization can describe reduction initiatives and their targets when reporting how it manages this topic.<br>
Reduction initiatives can include:
<ul style='list-style-type:disc;padding-left:40px'>
<li>process redesign;</li>
<li>conversion and retrofitting of equipment;</li>
<li>fuel switching;</li>
<li>changes in behavior;</li>
<li>offsets.</li>
</ul><br>
The organization can report reductions disaggregated by initiatives or groups of initiatives. <br>
This disclosure can be used in combination with Disclosures 305-1, 305-2, and 305-3 of this standard to monitor the reduction of GHG emissions with reference to the organization’s targets, or to regulations and trading systems at international or national level.<br>

Guidance for clause 2.9.2<br>
The inventory method compares reductions to a base year. The project method compares reductions to a baseline.<br>

Guidance for clause 2.9.3<br>
Primary effects are the elements or activities designed to reduce GHG emissions, such as carbon storage. Secondary effects are smaller, unintended consequences of a reduction initiative, including changes to production or manufacture, which result in changes to GHG emissions elsewhere. <br>
  `,

  "305-6": `
     <div><strong>Disclosure 305-6 Emissions of ozone-depleting substances (ODS)</strong></div>
  <br>
  <div><strong>REQUIREMENTS</strong></div>
 <strong> The reporting organization shall report the following information:<br>
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>Production, imports, and exports of ODS in metric tons of CFC-11 (trichlorofluoromethane) equivalent.</li>
<li>Substances included in the calculation.</li>
<li>Source of the emission factors used.</li>
<li>Standards, methodologies, assumptions, and/or calculation tools used.</li>
</ul>
Compilation requirements<br>

2.11 When compiling the information specified in Disclosure 305-6, the reporting organization shall:<br>
<ul  style='list-style-type:none;padding-left:40px'>
<li>
2.11.1 calculate the production of ODS as the amount of ODS produced, minus the amount destroyed by approved technologies, and minus the amount entirely used as feedstock in the manufacture of other chemicals;

<div style="border:1px solid black;display:flex;flex-direction:column;align-items:center">
<div>Production of ODS</div>
<div> = </div>
<div> ODS produced </div>
<div> - <div>
<div>ODS destroyed by approved technologies</div>
<div> - </div>
<div> ODS entirely used as feedstock in the manufacture of other chemicals </div>
</div>
</li>
<li>2.11.2 exclude ODS recycled and reused.</li>
</ul>
</strong><br>
  <div><strong>RECOMMENDATIONS</strong></div>
  2.12. When compiling the information specified in Disclosure 305-6, the reporting organization should:<br>
<ul style='list-style-type:none;padding-left:40px'>
<li>2.12.1. if subject to different standards and methodologies, describe the approach to selecting them;</li>
<li>2.12.2. where it aids transparency or comparability over time, provide a breakdown of the ODS data by:<br>
<ul style='list-style-type:none;padding-left:40px'>
<li>
business unit or facility;</li>
<li>country;</li>
<li>type of source;</li>
<li>type of activity.</li>
</ul>
</li>
</ul>
  <div><strong>GUIDANCE</strong></div>
  <b>Guidance for Disclosure 305-6</b>
The reporting organization can report separate or combined data for the substances included in the calculation.<br>
<b>Background</b><br>
Measuring ODS production, imports, and exports helps to indicate how an organization complies with legislation. This is particularly relevant if the organization produces or uses ODS in its processes, products and services and is subject to phase-out commitments. Results on ODS phase-out help to indicate the organization’s position in any markets affected by regulation on ODS.<br>
This disclosure covers the substances included in Annexes A, B, C, and E of the ‘Montreal Protocol’ as well as any other ODS produced, imported, or exported by an organization. <br>
  `,
  "305-7": `
   <div><strong>Disclosure 306-1 Water discharge by quality and destination</strong></div>
  <br>
  <div><strong>REQUIREMENTS</strong></div><strong>
  The reporting organization shall report the following information:<br>
<ul  style='list-style-type:lower-alpha;padding-left:40px' >
<li>
Significant air emissions, in kilograms or multiples, for each of the following:
<ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>NOx</li>
<li>SOx</li>
<li>Persistent organic pollutants (POP)</li>
<li>Volatile organic compounds (VOC)</li>
<li>Hazardous air pollutants (HAP)</li>
<li>Particulate matter (PM)</li>
<li>Other standard categories of air emissions identified in relevant regulations</li>
</ul>
</li>
<li>
Source of the emission factors used.</li>
<li>
Standards, methodologies, assumptions, and/or calculation tools used.</li>
</ul>
<br>
Compilation requirements<br>
2.13 When compiling the information specified in Disclosure 305-7, the reporting organization shall select one of the following approaches for calculating significant air emissions:
<ul style='list-style-type:none;padding-left:40px'>
<li>1. Direct measurement of emissions (such as online analyzers);</li>
<li>2. Calculation based on site-specific data;</li>
<li>3. Calculation based on published emission factors;</li>
<li>4. Estimation. If estimations are used due to a lack of default figures, the organization shall indicate the basis on which figures were estimated.</li>
</ul>
</strong><br>
  <div><strong>RECOMMENDATIONS</strong></div>
2.14 When compiling the information specified in Disclosure 305-7, the reporting organization should:
<ul  style='list-style-type:none;padding-left:40px'>
<li>
2.14.1. if subject to different standards and methodologies, describe the approach to selecting them;</li>
<li>2.14.2. where it aids transparency or comparability over time, provide a breakdown of the air emissions data by:
<ul  style='list-style-type:none;padding-left:40px'>
<li>2.14.2.1. business unit or facility;</li>
<li>2.14.2.2. country;</li>
<li>2.14.2.3. type of source;</li>
<li>2.14.2.4. type of activity.</li>
</ul>
</li>
</ul>
  `,
  "306-1-2016": `
   <div><strong>
  Disclosure 306-1 Water discharge by quality and destination</strong></div>
  <br>
  <div><strong>REQUIREMENTS</strong></div>
 <strong> The reporting organization shall report the following information:<br>
  <ul  style='list-style-type:lower-alpha;padding-left:40px'>
        <li> Total volume of planned and unplanned water discharges by:
        <ul  style='list-style-type:lower-roman;padding-left:40px'>
            <li> destination;</li>
            <li>quality of the water, including treatment method;</li>
            <li> whether the water was reused by another organization.</li>
        </ul>
        </li>
        <li>Standards, methodologies, and assumptions used.</li>
  </ul><br>

  Compilation requirements<br>
 2.1 When compiling the information specified in Disclosure 306-1, the reporting organization shall:
 <ul style='list-style-type:none;padding-left:40px'>
<li>2.1.1 exclude collected rainwater and domestic sewage from the volume of planned and unplanned water discharges;</li>
<li>2.1.2  if it does not have a meter to measure water discharges, estimate the volume of planned
and unplanned water discharges by subtracting the approximate volume consumed on-site
from the volume withdrawn as specified in Disclosure 303-1 of GRI 303: Water.</li>
</ul>
  </strong>
  
  <div><strong>RECOMMENDATIONS</strong></div>
  2.2 When compiling the information specified in Disclosure 306-1, the reporting organization should:
  <ul style='padding-left:40px'>
<li>2.2.1 if it discharges effluents or process water, report water quality in terms of total volumes of effluent
using standard effluent parameters, such as Biological Oxygen Demand (BOD) or Total Suspended
Solids (TSS);</li>
<li>2.2.2 select parameters that are consistent with those used in the organization’s sector.</li>
</ul><br>
<b>GUIDANCE</b><br>

In the context of this Standard, ‘water discharge’
includes water effluents discharged over the course
of the reporting period. These effluents can be
discharged to subsurface waters, surface waters,
sewers that lead to rivers, oceans, lakes, wetlands,
treatment facilities, and ground water, either:
<ul style='list-style-type:disc;padding-left:40px'>
<li> through a defined discharge point (point source
discharge);</li>
<li> over land in a dispersed or undefined manner
(non-point source discharge);</li>
<li> as wastewater removed from the organization
via truck.</li>
</ul>
Discharge of collected rainwater and domestic sewage
is not considered to be water discharge.<br>
<b>Guidance for clause 2.2</b><br>
The specific choice of water quality parameters can vary
depending on the organization’s products, services, and
operations.<br>
Water quality metrics can vary depending on national
or regional regulations.
`,
  "306-2-2016": `
   <div><strong>
  Disclosure 306-2 Waste by type and disposal method</strong></div>
  <br>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The reporting organization shall report the following information:<br>
    <ul style='list-style-type:lower-alpha;padding-left:40px'>
<li>Total weight of hazardous waste, with a breakdown by the following disposal methods
where applicable:
<ul style='list-style-type:lower-roman;padding-left:40px'>
<li> Reuse</li>
<li> Recycling</li>
<li> Composting</li>
<li> Recovery, including energy recovery</li>
<li> Incineration (mass burn)</li>
<li> Deep well injection</li>
<li> Landfill</li>
<li> On-site storage</li>
<li> Other (to be specified by the organization)</li>
</ul>
</li>
<li>Total weight of non-hazardous waste, with a breakdown by the following disposal methods
where applicable:
<ul style='list-style-type:lower-roman;padding-left:40px'>
<li> Reuse</li>
<li> Recycling</li>
<li> Composting</li>
<li> Recovery, including energy recovery</li>
<li> Incineration (mass burn)</li>
<li> Deep well injection</li>
<li> Landfill</li>
<li> On-site storage</li>
<li> Other (to be specified by the organization)</li>
</ul>
</li>
<li>
How the waste disposal method has been determined:
<ul style='list-style-type:lower-roman;padding-left:40px'>
 <li>Disposed of directly by the organization, or otherwise directly confirmed</li>
<li>Information provided by the waste disposal contractor</li>
<li>Organizational defaults of the waste disposal contractor</li>
</ul>
</li>
</ul><br>
Compilation requirements<br>
2.3 When compiling the information specified in Disclosure 306-2, the reporting organization shall:
<ul style='list-style-type:none;padding-left:40px'>
<li>2.3.1 identify hazardous waste as defined by national legislation at the point of generation;</li>
<li>2.3.2 exclude non-hazardous wastewater from the calculation of non-hazardous waste;</li>
<li>2.3.3 if no weight data are available, estimate the weight using available information on waste
density and volume collected, mass balances, or similar information.</li>
</ul>

    </strong>
  <div><strong>
  GUIDANCE</strong></div>
  Information about waste disposal methods reveals
the extent to which an organization has managed
the balance between disposal options and uneven
environmental impacts. For example, land filling and
recycling create very different types of environmental
impacts and residual effects. Most waste minimization
strategies emphasize prioritizing options for reuse,
recycling, and then recovery over other disposal
options to minimize ecological impacts.
`,
  "306-3-2016": `
   <div><strong>Disclosure 306-3 Significant spills</strong></div>
  <br>
  <div>
  <strong>REQUIREMENTS</strong></div>
  <strong>
The reporting organization shall report the following information:<br>
<ul style='list-style-type:lower-alpha;padding-left:40px'>
<li> Total number and total volume of recorded significant spills.<li>
<li> The following additional information for each spill that was reported in the organization’s
financial statements:
<ul style='list-style-type:lower-roman;padding-left:40px'>
<li> Location of spill;</li>
<li> Volume of spill;</li>
<li> Material of spill, categorized by: oil spills (soil or water surfaces), fuel spills (soil or
water surfaces), spills of wastes (soil or water surfaces), spills of chemicals (mostly
soil or water surfaces), and other (to be specified by the organization).</li>
</ul>
</li>
<li> Impacts of significant spills.</li>
</ul>
</strong>
`,
  "306-4-2016": `

   <div><strong>
 Disclosure 306-4 Transport of hazardous waste</strong></div>
  <br>

    <div><strong>REQUIREMENTS</strong></div>
    <strong>
        The reporting organization shall report the following information:
        <ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li> Total weight for each of the following:
<ul  style='list-style-type:lower-roman;padding-left:40px'>
<li> Hazardous waste transported</li>
<li> Hazardous waste imported</li>
<li> Hazardous waste exported</li>
<li> Hazardous waste treated</li>
</ul>
</li>
<li>Percentage of hazardous waste shipped internationally.</li>
<li>Standards, methodologies, and assumptions used.</li>
</ul><br>
    Compilation requirements<br>
2.4 When compiling the information specified in Disclosure 306-4, the reporting organization shall:<br>
<ul  style='list-style-type:none;padding-left:40px'>
 <li>2.4.1 convert volumes to an estimate of weight;</li>
<li>in response to Disclosure 306-4-c, provide a brief explanation of the methodology used
for making these conversions.</li>
</ul>
    </strong><br><hr>

  <div><strong>
  GUIDANCE</strong></div>
  This disclosure covers waste deemed hazardous under
the terms of the Basel Convention Annex I, II, III, and
VIII (see reference 1 in the References section). It covers
hazardous waste transported by or on behalf of the
reporting organization within the reporting period
by destination, including transport across operational
boundaries and within operations.<br>
The organization can calculate the total weight of
transported hazardous waste using the following
equation:<br>

Total weight of hazardous waste
transported by destination = <br>
Weight of hazardous waste transported to the
organization by destination from external sources/
suppliers not owned by the organization + <br>
Weight of hazardous waste transported from the
organization by destination to external sources/
suppliers not owned by the organization + <br>
Weight of hazardous waste transported nationally
and internationally by destination between locations
owned, leased, or managed by the organization <br>

Imported hazardous waste can be calculated as the
total weight of hazardous waste transported across
international borders and which enters the boundaries
of the organization, by destination, excluding waste
transported between different locations of the
organization<br>

Exported hazardous waste can be calculated as
the proportion of the total amount of transported
hazardous waste by destination that is transported
from the organization to locations abroad, including all
waste that leaves the boundaries of the organization to
cross international borders and excluding transportation
between different locations of the organization.<br>

For treated waste, the organization can identify:
<ul style='list-style-type:disc;padding-left:40px'>
<li> the portion of the total amount of transported
and exported waste that the organization has
treated, by destination;</li>
<li> the portion of the total amount of waste, by
destination, that is treated by external sources/
suppliers, that has been transported, exported,
or imported by the organization.</li>
</ul>
`,
  "306-5-2016": `
     <div><strong>
 Disclosure 306-5 Water bodies affected by water discharges and/or runoff</strong></div>
  <br>
  <div><strong>REQUIREMENTS</strong></div>  
  <strong>
  The reporting organization shall report the following information:<br>
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
Water bodies and related habitats that are significantly affected by water discharges and/or
runoff, including information on:<br>
<ul  style='list-style-type:lower-roman;padding-left:40px'>
    <li> the size of the water body and related habitat;</li>
    <li> whether the water body and related habitat is designated as a nationally
    or internationally protected area;</li>
    <li>the biodiversity value, such as total number of protected species.</li>
    </ul>
    </li>
</ul>
<br>

Compilation requirements<br>
2.5  When compiling the information in Disclosure 306-5, the reporting organization shall report
water bodies and related habitats significantly affected by water discharges and/or runoff that
meet one or more of the following criteria:<br>
<ul  style='list-style-type:none;padding-left:40px'>
<li>
2.5.1 Discharges account for an average of five percent or more of the annual average volume
of the water body;
</li>
<li>
2.5.2 Discharges that, on the advice of appropriate professionals, such as municipal authorities,
are known to have or are highly likely to have significant impacts on the water body and
associated habitats;</li>
<li>
2.5.3 Discharges to water bodies that are recognized by professionals to be particularly sensitive
due to their relative size, function, or status as a rare, threatened, or endangered system,
or that support a particular endangered species of plant or animal;</li>
<li>
2.5.4 Any discharge to a wetland listed in the Ramsar Convention or any other nationally
or internationally proclaimed conservation area regardless of the rate of discharge;</li>
<li>
2.5.5 The water body has been identified as having a high biodiversity value, such as species
diversity and endemism, or total number of protected species;</li>
<li>
2.5.6 The water body has been identified as having a high value or importance to local
communities.
</li>
</ul>
  </strong>

  <strong>Background</strong>
  This disclosure is a qualitative counterpart to the
quantitative disclosures of water discharge, and helps to
describe the impact of these discharges. Discharges and
runoff affecting aquatic habitats can have a significant
impact on the availability of water resources.
`,
  "306-1-2020": `
   <div><strong>
  Disclosure 306-1 Waste generation and significant waste-related impacts</strong></div>
  <br>
  <div><strong>REQUIREMENTS</strong></div>
The reporting organization shall report the following information:<br>
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>
For the organization’s significant actual and potential waste-related impacts, a description of:
<ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>
the inputs, activities, and outputs that lead or could lead to these impacts;</li>
<li>
 whether these impacts relate to waste generated in the organization’s own activities or to waste generated upstream or downstream in its value chain.</li>
 </li>
 </ul>
 </li>
   </ul>
  <div><strong>
  RECOMMENDATIONS</strong></div>
  1.2 The reporting organization should report a process flow of inputs, activities, and outputs that lead or could lead to significant waste-related impacts.
  <br>
  <b>GUIDANCE</b><br>
  <strong>Background</strong><br>
  The quantity, type, and quality of waste generated by an organization is a consequence of the
activities involved in the production of its products and services (e.g., extraction, processing,
procurement of materials, product or service design, production, distribution) and their
subsequent consumption. An assessment of how materials move into, through, and out of the
organization can help understand where in the organization’s value chain these materials
eventually become waste. This provides a holistic overview of waste generation and its causes,
which in turn can support the organization in identifying opportunities for waste prevention and
for adopting circularity measures. In this way, the organization can go beyond mitigating and
remediating negative impacts once waste has been generated and move towards managing
waste as a resource.<br>
<b>Guidance for Disclosure 306-1</b><br>
When reporting on this disclosure, the organization can specify the types of inputs and outputs.
The types of inputs and outputs can include raw materials, process and manufacturing
materials, leaks and losses, waste, by-products, products, or packaging.
<br>

The organization can assess and report whether inputs, activities, and outputs lead or could
lead to significant waste-related impacts using the following criteria:
<ul style='list-style-type:disc;padding-left:40px'>
<li>
 Quantity of inputs used to produce the organization’s products or services, which will
become waste after they are used for production.
</li>
<li>
 Quantity of waste outputs generated in the organization’s own activities, or quantity of outputs
it provides to entities downstream that will eventually become waste when they reach their
end of life.
</li>
<li>
 Hazardous characteristics of inputs and outputs.
</li>
<li>
 Properties of input materials or design characteristics of outputs that limit or prevent their
recovery or limit the length of their life.</li>
<li>
 Known potential negative threats associated with specific materials when they are
discarded. For example, the potential threat of marine pollution resulting from leakage of
discarded plastic packaging into waterbodies.</li>
<li>
Types of activities that lead to significant quantities of waste generation or to generation of
hazardous waste.</li>
</ul>
The organization is required to report on inputs that it receives from entities upstream in its value
chain, as well as outputs it provides to entities downstream in its value chain. For example, if an
organization procures components with hazardous characteristics from a supplier and uses
these in a product that will continue to carry these components and their hazardous
characteristics, the organization is required to report these components under inputs that lead or
could lead to significant waste-related impacts. Similarly, if an organization sells to consumers
products that generate large quantities of packaging waste, it is required to report this packaging
under outputs that lead or could lead to significant waste-related impacts.<br>

If the organization has identified many inputs and outputs or many activities that lead or could
lead to significant waste-related impacts, it may group these by:<br>
<ul style='list-style-type:disc;padding-left:40px'>
<li>
  product or service category that the inputs and outputs relate to;</li>
<li>
 business units or facilities that procure the inputs, or whose activities produce the outputs;</li>
 <li>
 categories of upstream and downstream activities that produce the outputs (for examples of
upstream and downstream categories, see the guidance for Disclosure 302-2 in GRI 302:Energy 2016).</li>
</ul>
<b>Guidance for clause 1.2</b><br>
A process flow is a tool to visualize the information required to be reported under Disclosure
306-1. A graphic illustration of the process flow can help the organization and its stakeholders
understand how inputs and outputs move through the organization’s own activities as well as
through the activities of entities upstream and downstream in its value chain. It shows where
waste is generated in the value chain or where outputs become waste.<br>
The organization can also use the process flow to illustrate information that is required under
other disclosures of this Standard, such as:,
<ul style='list-style-type:disc;padding-left:40px'>
<li> actions taken to prevent waste generation (Disclosure 306-2);</li>
<li> composition of waste generated (Disclosure 306-3);</li>
<li> recovery operations used to divert waste from disposal (Disclosure 306-4);</li>
<li> disposal operations (Disclosure 306-5).</li>
</ul>
The organization can include estimates of the weight of inputs and the weight of outputs in
metric tons or the ratio of inputs to outputs.
 `,
  "306-2-2020": `
   <div><strong>
  Disclosure 306-2 Management of significant waste-related impacts</strong></div>
  <br>
  <div><strong>REQUIREMENTS</strong></div>
<strong>  The reporting organization shall report the following information:<br>
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>
Actions, including circularity measures, taken to prevent waste generation in the organization’s own activities and upstream and downstream in its value chain, and to manage significant impacts from waste generated.</li>
<li>
If the waste generated by the organization in its own activities is managed by a third party, a description of the processes used to determine whether the third party manages the waste in line with contractual or legislative obligations.
</li>
<li>
The processes used to collect and monitor waste-related data.</li>
</ul></strong><br><hr>
  <div><strong>
  GUIDANCE</strong></div>
  <b>Background</b><br>
  An organization can cause waste-related impacts through its own activities. For example, when
its operations generate waste outputs. It can also contribute to waste-related impacts through
activities carried out in its value chain upstream or downstream. For example, through criteria in
its procurement policies that lead to waste generation upstream, or through management
decisions that limit the life of its products and therefore contribute to waste generation
downstream.<br>
Even when the organization has not contributed to waste-related impacts upstream or
downstream in its value chain, its operations, products, or services could be directly linked to
waste-related impacts by its business relationships with the entities in its value chain. For
example, when third parties hired by the organization carry out inadequate recovery or disposal
operations.<br>
The way an organization is involved with negative impacts is important for determining the
organization’s response to an impact.<br>
<b>Guidance for Disclosure 306-2-a</b><br>
Actions, including circularity measures, to prevent waste generation and to manage significant
impacts from waste generated can include:<br>
<ul>
<li> Input material choices and product design:
<ul style='list-style-type:disc;padding-left:40px'>
<li>Improving materials selection and product design through consideration for longevity and
durability, repairability, modularity and disassembly, and recyclability.</li>
<li> Reducing the use of raw and finite materials by procuring secondary materials (e.g., used
or recycled input materials) or renewable materials.</li>
<li> Substituting inputs that have hazardous characteristics with inputs that are non-
hazardous.</li>
</ul>
</li>
<li>
 Collaboration in the value chain and business model innovation:
 <ul style='list-style-type:disc;padding-left:40px'>
<li> Setting policies for procurement from suppliers that have sound waste prevention and
waste management criteria.</li>
<li> Engaging in or setting up industrial symbiosis as a result of which the organization’s
waste or other outputs (e.g., by-products from production) become inputs for another
organization.</li>
<li> Participating in a collective or individual extended producer responsibility scheme or
applying product stewardship, which extends the producer’s responsibility for a product
or service to its end of life.</li>
<li> Transitioning to and applying new business models, such as product service systems
that use services instead of products to meet consumer demand.</li>
<li> Engaging in or setting up product take-back schemes and reverse logistics processes to
divert products and materials from disposal.</li>
</ul>
</li>
<li>
 End-of-life interventions:
 <ul style='list-style-type:disc;padding-left:40px'>
<li> Establishing and improving facilities for waste management, including facilities for the
collection and sorting of waste.</li>
<li> Recovering products, components, and materials from waste through preparation for
reuse and recycling.</li>
<li> Engaging with consumers to raise awareness about sustainable consumption practices,
such as reduced purchasing of products, product sharing, exchange, reuse, and
recycling.</li>
</ul>
</li>
</ul>

<b>Guidance for Disclosure 306-2-b</b><br>
This disclosure can provide insight into the level of control the organization assumes for waste
management outsourced to a third party. In the context of this Standard, a third party includes a
public or private waste management organization, or any other entity or group of individuals
formally or informally involved in handling the reporting organization’s waste. Waste
management by third parties can include the collection, transportation, recovery, and disposal of
waste, as well as the supervision of such operations and the aftercare of disposal sites. The
organization may specify agreements in a contract for the third party to follow when managing its
waste, or rely on existing legislative obligations, such as local environmental laws and
regulations, to ensure that the third party manages the waste adequately.<br>
<b>Guidance for Disclosure 306-2-c</b><br>
The processes that the organization has in place for collecting and monitoring waste-related
data can reflect its commitment to managing waste-related impacts. Such processes can
include online data entry, maintaining a centralized database, real-time weighbridge
measurement, and annual external data validation.
<br>
The organization can specify whether the data collection and monitoring processes extend
beyond waste generated in its own activities to include waste generated upstream and
downstream in its value chain.
 `,
  "306-3-2020": `
    <div><strong>Disclosure 306-3 Waste generated</strong></div>
  <br>
<div><strong>REQUIREMENTS</strong></div><br>
<strong>
  The reporting organization shall report the following information:<br>
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>
Total weight of waste generated in metric tons, and a breakdown of this total by composition of the waste.</li>
<li>
Contextual information necessary to understand the data and how the data has been compiled.</li>
</ul><br>

Compilation requirements<br>

2.1 When compiling the information specified in Disclosure 306-3-a, the reporting organization shall:<br>
<ul  style='list-style-type:none;padding-left:40px'>
<li>
2.1.1 exclude effluent, unless required by national legislation to be reported under total waste;
</li>
<li>
2.1.1 use 1000 kilograms as the measure for a metric ton.</li>
</ul><br>
  <div><strong>
  GUIDANCE</strong></div>
  <strong>Background</strong><br>
The total weight of waste generated, when contrasted with the weight of waste that the
organization directs to recovery and disposal, can show the extent to which the organization
manages its waste-related impacts.<br>
The composition of the waste generated can help identify recovery or disposal operations
appropriate to the type of waste and to the specific materials present in the waste.<br>
<b>Guidance for Disclosure 306-3</b><br>
This disclosure covers waste generated in the organization’s own activities. The organization
can separately report waste generated upstream and downstream in its value chain, if this
information is available.<br>
<b>Guidance for Disclosure 306-3-a</b><br>
When reporting composition of the waste, the organization can describe:
<ul style='list-style-type:disc;padding-left:40px'>
<li>
 the type of waste, such as hazardous waste or non-hazardous waste;</li>
<li>
 the waste streams, relevant to its sector or activities (e.g., tailings for an organization in the
mining sector, electronic waste for an organization in the consumer electronics sector, or
food waste for an organization in the agriculture or in the hospitality sector);</li>
<li>
 the materials that are present in the waste (e.g., biomass, metals, non-metallic minerals,
plastics, textiles).</li>
</ul> 

<b>Guidance for Disclosure 306-3-b</b><br>
To help understand the data, the organization can explain the reasons for the difference between
the weight of waste generated and the weight of waste directed to recovery or disposal. This
difference can be a result of precipitation or evaporation, leaks or losses, or other modifications
to the waste. In the context of this Standard, leaks result from physical or technical failures (e.g.,
a trail of waste from a waste collection truck), while losses result from inadequate security
measures or administrative failures (e.g., theft or lost records).<br>
To help understand how the data has been compiled, the organization can specify whether the
data has been modeled or sourced from direct measurements, such as waste transfer notes
from contracted waste collectors, external assurance, or audits of waste-related data.
 `,
  "306-4-2020": `
      <div><strong>Disclosure 306-4 Waste diverted from disposal</strong></div>
  <br>
  <div>
     <strong>REQUIREMENTS</strong>
  </div><br>
 <strong> The reporting organization shall report the following information:<br>
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>
Total weight of waste diverted from disposal in metric tons, and a breakdown of this total by composition of the waste.
</li>
<li>
Total weight of hazardous waste diverted from disposal in metric tons, and a breakdown of this total by the following recovery operations<br>
<ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>Preparation for reuse;</li>
<li>Recycling;</li>
<li>Other recovery operations.</li>
</ul>
</li>
<li>
Total weight of non-hazardous waste diverted from disposal in metric tons, and a breakdown of this total by the following recovery operations:
<ul style='list-style-type:lower-roman;padding-left:40px'>
<li>Preparation for reuse;</li>
<li>Recycling;</li>
<li>Other recovery operations.</li>
</ul>
</li>
<li>
For each recovery operation listed in Disclosures 306-4-b and 306-4-c, a breakdown of the total weight in metric tons of hazardous waste and of non-hazardous waste diverted from disposal:
<ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>onsite;</li>
<li>offsite.</li>
</ul>
</li>
<li>
Contextual information necessary to understand the data and how the data has been compiled.</li>
</ul><br>
Compilation requirements<br>
2.2 When compiling the information specified in Disclosure 306-4, the reporting organization shall:<br>
<ul  style='list-style-type:none;padding-left:40px'>
<li>
2.2.1 exclude effluent, unless required by national legislation to be reported under total waste;</li>
<li>
use 1000 kilograms as the measure for a metric ton.</li>
</ul>
</strong>
<br><hr>
  <div><strong>RECOMMENDATIONS</strong></div>
  2.3 The reporting organization should report the total weight of waste prevented, and the
baseline and methodology for this calculation.
<br>
<b>GUIDANCE</b><br>
<b>Background</b>
An organization’s choice of operations to manage waste shows how it addresses significant
waste-related impacts. The options to manage waste can be informed by the waste
management hierarchy, which orders operations to manage waste from the most preferable to
the least preferable. The waste management hierarchy prioritizes waste prevention, followed by
recovery operations that divert waste from being sent to disposal, such as preparation for reuse,
recycling, and other recovery operations.
<br>
<b>Guidance for Disclosure 306-4-a</b><br>
When reporting composition of the waste, the organization can describe:
<ul>
<li> the type of waste, such as hazardous waste or non-hazardous waste;</li>
<li> the waste streams, relevant to its sector or activities (e.g., tailings for an organization in the
mining sector, electronic waste for an organization in the consumer electronics sector, or
food waste for an organization in the agriculture or in the hospitality sector);</li>
<li> the materials that are present in the waste (e.g., biomass, metals, non-metallic minerals,
plastics, textiles).</li>
</ul>
<b>Guidance for Disclosures 306-4-b and 306-4-c</b>
When reporting on Disclosures 306-4-b-ii and 306-4-c-ii, the organization can specify the type of
recycling operations, such as downcycling, upcycling, composting, or anaerobic digestion.
Besides preparation for reuse and recycling, the organization can report the other types of
recovery operations it uses under Disclosures 306-4-b-iii and 306-4-c-iii, such as repurposing
or refurbishment.<br>
<b>Guidance for Disclosure 306-4-d</b><br>
Reporting on the quantity and type of waste diverted from disposal onsite and offsite shows the
extent to which the organization knows how its waste is managed. In the context of this Standard,
‘onsite’ means within the physical boundary or administrative control of the reporting
organization, and ‘offsite’ means outside the physical boundary or administrative control of the
reporting organization.<br>
<b>Guidance for Disclosure 306-4-e</b><br>
To help understand the data, the organization can explain the reasons for the difference between
the weights of waste diverted from disposal onsite and offsite (e.g., lack of infrastructure onsite
to recover materials from waste). It can also describe sector practices, sector standards, or
external regulations that mandate a specific recovery operation.<br>
To help understand how the data has been compiled, the organization can specify whether the
data has been modeled or sourced from direct measurements, such as waste transfer notes
from contracted waste collectors, external assurance, or audits of waste-related data.
<br>
<b>Guidance for clause 2.3</b><br>
Waste prevention is the most preferable option in the waste management hierarchy, as it
prevents the resulting impacts on the environment and human health. The organization can
calculate waste prevented as the reduction in waste generation resulting from the actions
reported under Disclosure 306-2-a. Reductions in waste generation resulting from reduced
production capacity are not considered waste prevention. The organization can report waste
prevented in its own activities as well as waste prevented in entities upstream and downstream
in its value chain.
 `,
  "306-5-2020": `
       <div><strong>Disclosure 306-5 Waste directed to disposal</strong></div>
  <br>
  <div><strong>REQUIREMENTS</strong></div>
<strong>
  The reporting organization shall report the following information:<br>
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>Total weight of waste directed to disposal in metric tons, and a breakdown of this total by composition of the waste.</li>
<li>
Total weight of hazardous waste directed to disposal in metric tons, and a breakdown of this total by the following disposal operations:<br>
<ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>Incineration (with energy recovery);</li>
<li>Incineration (without energy recovery);</li>
<li>Landfilling;</li>
<li>Other disposal operations.</li>
</ul>
</li>
Total weight of non-hazardous waste directed to disposal in metric tons, and a breakdown of this total by the following disposal operations:<br>
<ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>Incineration (with energy recovery);</li>
<li>Incineration (without energy recovery);</li>
<li>Landfilling;</li>
<li>Other disposal operations.</li>
</ul>
<li>
For each disposal operation listed in Disclosures 306-5-b and 306-5-c, a breakdown of the total weight in metric tons of hazardous waste and of non-hazardous waste directed to disposal:
<ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>onsite;</li>
<li>offsite.</li>
</ul>
</li>
<li>
Contextual information necessary to understand the data and how the data has been compiled.</li>
</ul><br>
Compilation requirements<br>
2.4 When compiling the information specified in Disclosure 306-5, the reporting organization shall:<br>
<ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>2.4.1 exclude effluent, unless required by national legislation to be reported under total waste;</li>
<li>2.4.2 use 1000 kilograms as the measure for a metric ton.</li>
</ul>
</strong><br><hr>
  <div><strong>GUIDANCE</strong></div><br>
  <strong>Background</strong>
  Disposal is the least preferable option in the waste management hierarchy because of its
negative impacts on the environment and human health. Leachate from landfills can
contaminate land and water, methane released from the decay of organic waste in landfills
contributes to climate change, and uncontrolled burning of waste contributes to air pollution.
Disposal prevents the materials present in the waste from being recirculated in the environment
and economy, making them unavailable for future use.<br>
<b>Guidance for Disclosure 306-5-a</b>
<br>
When reporting composition of the waste, the organization can describe:
<ul style='list-style-type:disc;padding-left:40px'>
<li> the type of waste, such as hazardous waste or non-hazardous waste;</li>
<li> the waste streams, relevant to its sector or activities (e.g., tailings for an organization in the
mining sector, electronic waste for an organization in the consumer electronics sector, or
food waste for an organization in the agriculture or in the hospitality sector);</li>
<li> the materials that are present in the waste (e.g., biomass, metals, non-metallic minerals,
plastics, textiles).</li>
</ul>
<b>Guidance for Disclosures 306-5-b and 306-5-c</b><br>
Besides incineration and landfilling, the organization can specify the other types of disposal
operations it uses under Disclosures 306-5-b-iv and 306-5-c-iv, such as dumping, open
burning, or deep well injection.<br>
<b>Guidance for Disclosure 306-5-d</b><br>
Reporting the quantity and type of waste directed to disposal onsite and offsite shows the extent
to which the organization knows how its waste is managed. In the context of this Standard,
‘onsite’ means within the physical boundary or administrative control of the reporting
organization, and ‘offsite’ means outside the physical boundary or administrative control of the
reporting organization.
<br>
<b>Guidance for Disclosure 306-5-e</b><br>
To help understand the data, the organization can explain the reasons for the difference between
the weights of waste directed to disposal onsite and offsite (e.g., local regulations that prohibit
landfilling of specific types of waste). It can also describe sector practices, sector standards, or
external regulations that mandate a specific disposal operation.<br>
To help understand how the data has been compiled, the organization can specify whether the
data has been modeled or sourced from direct measurements, such as waste transfer notes
from contracted waste collectors, external assurance, or audits of waste-related data.
  `,
  "308-1": `
         <div><strong>Disclosure 306-5 Waste directed to disposal</strong></div>
  <br>
  <div><b>REQUIREMENTS</b></div>
  <div><strong>
  The reporting organization shall report the following information:<br>
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
    <li>Percentage of new suppliers that were screened using environmental criteria.</li>
</ul>
</strong></div>
  <div><strong>GUIDANCE</strong></div><br>
Environmental criteria can include the topics covered in other GRI Topic Standards (e.g., GRI
302: Energy 2016, GRI 303: Water and Effluents 2018, GRI 305: Emissions 2016)<br>
<b>Background</b><br>
This disclosure informs stakeholders about the percentage of suppliers selected or contracted
subject to due diligence processes for environmental impacts.<br>
An organization is expected to initiate due diligence as early as possible in the development of a
new relationship with a supplier.<br>
Impacts may be prevented or mitigated at the stage of structuring contracts or other agreements,
as well as via ongoing collaboration with suppliers.
  `,
  "308-2": `
           <div><strong>Disclosure 308-2 Negative environmental impacts in
the supply chain and actions taken</strong></div>
  <br>
  <div><strong>REQUIREMENTS</strong></div>
<strong>  The reporting organization shall report the following information:<br>
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>
Number of suppliers assessed for environmental impacts.</li>
<li>
Number of suppliers identified as having significant actual and potential negative environmental impacts.</li>
<li>
Significant actual and potential negative environmental impacts identified in the supply chain.</li>
<li>
Percentage of suppliers identified as having significant actual and potential negative environmental impacts with which improvements were agreed upon as a result of assessment.</li>
<li>
Percentage of suppliers identified as having significant actual and potential negative environmental impacts with which relationships were terminated as a result of assessment, and why.</li>
</ul></strong>
<br><hr>

  <div><strong>RECOMMENDATIONS</strong></div><br>
 2.1 When compiling the information specified in Disclosure 308-2, the reporting organization
should, where it provides appropriate context on significant impacts, provide a
breakdown of the information by:<br>
<ul style='padding-left:40px'>
<li>
2.1.1 the location of the supplier;</li>
<li>
2.1.2 the significant actual and potential negative environmental impact.</li>
</ul>
<br>
<b>GUIDANCE</b><br>
Negative impacts include those that are either caused or contributed to by an organization, or
that are directly linked to its operations, products, or services by its relationship with a supplier.<br>
Assessments for environmental impacts can include the topics covered in other GRI Topic
Standards (e.g., GRI 302: Energy 2016, GRI 303: Water and Effluents 2018, GRI 305: Emissions
2016).<br>
Assessments can be made against agreed performance expectations that are set and
communicated to the suppliers prior to the assessment.<br>
Assessments can be informed by audits, contractual reviews, two-way engagement, and
complaint and grievance mechanisms.<br>
Improvements can include changing an organization’s procurement practices, the adjustment of
performance expectations, capacity building, training, and changes to processes.<br>
<b>Background</b>
This disclosure informs stakeholders about an organization’s awareness of significant actual
and potential negative environmental impacts in the supply chain.

  `,

  "401-1": `
  <div>
      <strong>Disclosure 401-1 New employee hires and employee turnover</strong>
  </div>
  <div><strong>REQUIREMENTS</strong></div>
 <strong> The reporting organization shall report the following information:<br>
  <ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>
Total number and rate of new employee hires during the reporting period, by age group,
gender and region.
</li>
<li>
Total number and rate of employee turnover during the reporting period, by age group,
gender and region.</li>
</ul><br></strong><br><hr>
  <div><strong>RECOMMENDATIONS</strong></div>
  When compiling the information specified in Disclosure 401-1, the reporting organization should use data from Disclosure 2-7 in GRI 2: General Disclosures 2021 to identify the total number of employees.
  <div><strong>GUIDANCE</strong></div>
  An organization can use the following age groups:
  <p style="text-indent:30px; text-align:justify">
      <ul style="padding-left:40px;list-style-type:disc">
          <li>
              Under 30 years old;
          </li>
          <li>
              30-50 years old;
          </li>
          <li>
            Over 50 years old.
          </li>
      </ul>
      <b>Background</b><br>
      <p style="text-indent:40px; text-align:justify">
          The number, age, gender, and region of an organization’s new employee hires can indicate its strategy and ability to attract diverse, qualified employees. This information can signify the organization’s efforts to implement inclusive recruitment practices based on age and gender. It can also signify the optimal use of available labor and talent in different regions.
      </p>
      <p style="text-indent:40px; text-align:justify">    
          A high rate of employee turnover can indicate levels of uncertainty and dissatisfaction among employees. It can also signal a fundamental change in the structure of an organization’s core operations. An uneven pattern of turnover by age or gender can indicate incompatibility or potential inequity in the workplace. Turnover results in changes to the human and intellectual capital of the organization and can impact productivity. Turnover has direct cost implications either in terms of reduced payroll or greater expenses for the recruitment of employees
      </p>
  </p>`,
  "401-2": `
  <div>
      <strong>Disclosure 401-2 Benefits provided to full-time employees that are not provided to temporary or part- time employees</strong>
  </div>
  <div><b>REQUIREMENTS</b></div>
  <strong>The reporting organization shall report the following information:<br>
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>
Benefits which are standard for full-time employees of the organization but are not
provided to temporary or part-time employees, by significant locations of operation.
These include, as a minimum:<br>
<ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>life insurance;</li>
<li>health care;</li>
<li>disability and invalidity coverage;</li>
<li>parental leave;</li>
<li>retirement provision;</li>
<li>stock ownership;</li>
<li>others.</li>
</ul>
</li>
<li>
The definition used for ‘significant locations of operation’.</li>
</ul>
Compilation requirements<br>
2.3 When compiling the information specified in Disclosure 401-2, the reporting
organization shall exclude in-kind benefits such as provision of sports or child day
care facilities, free meals during working time, and similar general employee welfare
programs.
</strong><br><hr>
  <div><strong>GUIDANCE</strong></div> 
  <b>Background</b><br>
      <p style="text-indent:40px; text-align:justify">    
          Data reported under this disclosure provide a measure of an organization’s investment in human resources and the minimum benefits it offers its full-time employees. The quality of benefits for full-time employees is a key factor in retaining employees. 
      </p>
  </p>`,
  "401-3": `
  <div>
      <strong>Disclosure 401-3 Parental leave</strong>
  </div>
  <div><strong>RECOMMENDATIONS</strong></div>
  2.4 When compiling the information specified in Disclosure 401-3, the reporting organization should use the following formulas to calculate the return to work and retention rates:
<div>
<div style='border:1px solid black;display:flex;'>
    <div>Return to work rate</div>
        <div> = </div>
    <div style='display:flex;flex-direction:column'>
        <div style='border-bottom:1px solid black'>Total number of employees that did return to work after
parental leave</div>
        <div>Total number of employees that did return to work after
parental leave</div>
    </div>
    <div>x</div>
        <div>100</div>
</div>
<div style='border:1px solid black;display:flex;'>
    <div>Retention rate</div>
        <div> = </div>
    <div style='display:flex;flex-direction:column'>
        <div style='border-bottom:1px solid black'>Total number of employees retained 12 months after
returning to work following a period of parental leave</div>
        <div>Total number of employees returning from parental
leave in the prior reporting period(s)</div>
    </div>
    <div>x</div>
        <div>100</div>
</div>
</div>
  <div><strong>GUIDANCE</strong></div>
  <p style="text-indent:30px; text-align:justify">
      Employees entitled to parental leave means those employees that are covered by organizational policies, agreements or contracts that contain parental leave entitlements.
  </p>
  <p style="text-indent:30px; text-align:justify">
      To determine who returned to work after parental leave ended and were still employed 12 months later, an organization can consult records from the prior reporting periods.
  </p>
  <b>Background</b><br>
  <p style="text-indent:30px; text-align:justify">
      Many countries have introduced legislation to provide parental leave. The aim of the legislation is to allow employees to take leave and return to work in the same or a comparable position.
  </p>
  <p style="text-indent:30px; text-align:justify">
      The application of legislation varies according to interpretation by government, employers and employees. Many women are discouraged from taking leave and returning to work by employer practices that affect their employment security, remuneration and career path. Many men are not encouraged to take the leave to which they are entitled.
  </p>
  <p style="text-indent:30px; text-align:justify">
      Equitable gender choice for maternity and paternity leave, and other leave entitlements, can lead to the greater recruitment and retention of qualified employees. It can also boost employee morale and productivity. Men’s uptake of paternity leave options can indicate the degree to which an organization encourages fathers to take such leave. Men taking advantage of leave entitlements positively impacts women to take such leave without prejudicing their career path.
  </p>`,
  "402-1": `
  <div><strong>Disclosure 402-1 Minimum notice periods regarding operational changes</strong></div>
  <div><strong>REQUIREMENTS</strong></div>
 <strong>   The reporting organization shall report the following information:<br>
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>Minimum number of weeks’ notice typically provided to employees and their
representatives prior to the implementation of significant operational
changes that could substantially affect them.</li>
<li>
For organizations with collective bargaining agreements, report whether the
notice period and provisions for consultation and negotiation are specified in
collective agreements.
</li>
</ul></strong>
<div>
<strong>GUIDANCE</strong>
<b>Guidance for Disclosure 402-1</b>
Minimum notice periods can be found in corporate policies and standard employment contracts.
Different policy statements can exist at a regional level.<br>
An organization can identify the collective bargaining agreements referred to in Disclosure 2-30
of GRI 2: General Disclosures 2021, and review the notice period clauses within these
documents.<br>
<b>Background</b><br>
Organizations are expected to provide reasonable notice of significant operational changes to
employees and their representatives, as well as to appropriate government authorities.<br>
Minimum notice periods are a measure of an organization’s ability to maintain employee
satisfaction and motivation while implementing significant changes to operations.<br>
This disclosure provides insight into an organization’s practice of ensuring timely discussion of
significant operational changes, and engaging with its employees and their representatives to
negotiate and implement these changes, which can have positive or negative implications for
workers.<br>
This disclosure also allows an assessment of an organization’s consultation practices in
relation to expectations expressed in relevant international norms.<br>
The essence of consultation is that management takes the views of workers into account when
making specific decisions. Therefore, it is important that consultation takes place before a
decision is made. Meaningful consultation includes the timely provision of all information
needed to make an informed decision to workers or their representatives. Genuine consultation
involves dialogue; opinion surveys and questionnaires are not considered consultation.<br>
Timely and meaningful consultation allows the affected parties to understand the impacts of the
changes, such as possible loss of employment. It also gives an opportunity for them to work
collectively to prevent or mitigate negative impacts as much as possible (see references [11]
and [12] in the Bibliography). Consultative practices that result in good industrial relations help
to provide positive working environments, reduce turnover, and minimize operational
disruptions.
</div>
  `,
  "403-1": `
  <div>
      <strong>Disclosure 403-1 Occupational health and safety management system</strong>
  </div> 
<div><strong>REQUIREMENTS</strong></div>
<strong>
The reporting organization shall report the following information for employees and for
workers who are not employees but whose work and/or workplace is controlled by the
organization:<br>
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
A statement of whether an occupational health and safety management system has
been implemented, including whether:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>
the system has been implemented because of legal requirements and, if so, a list
of the requirements;</li>
<li>the system has been implemented based on recognized risk management and/or
management system standards/guidelines and, if so, a list of the
standards/guidelines.</li>
</ul>
<li>
A description of the scope of workers, activities, and workplaces covered by the
occupational health and safety management system, and an explanation of whether
and, if so, why any workers, activities, or workplaces are not covered.</li>
</ul>
</strong>
  <div><strong>GUIDANCE</strong></div>
  <p style="text-indent:30px; text-align:justify">
      Disclosure 403-1 requires the reporting organization to list any legal requirements it has followed in implementing the occupational health and safety management system.
  </p>
  <p style="text-indent:30px; text-align:justify">
      Recognized standards/guidelines for occupational health and safety management systems include international, national, and industry-specific standards.
  </p>
  <p style="text-indent:30px; text-align:justify">
      When reporting on the occupational health and safety management system, the organization can also describe:
      <ul style="padding-left:40px;list-style-type:disc">
          <li>
            the type of occupational health and safety professionals responsible for the management system, and whether these individuals are employed by the organization or engaged as consultants;
          </li>
          <li>
            how the continual improvement of the management system is achieved, i.e., the iterative process of enhancing the management system to achieve improvements in overall occupational health and safety performance.
          </li>
      </ul>
  </p>`,
  "403-2": `
  <div>
      <strong>Disclosure 403-2 Hazard identification, risk assessment, and incident investigation</strong>
  </div> 
  <div><strong>REQUIREMENTS</strong></div>
  <strong>
  The reporting organization shall report the following information for employees and for
workers who are not employees but whose work and/or workplace is controlled by the
organization:<br>
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>
A description of the processes used to identify work-related hazards and assess risks
on a routine and non-routine basis, and to apply the hierarchy of controls in order to
eliminate hazards and minimize risks, including:
<ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>how the organization ensures the quality of these processes, including the
competency of persons who carry them out;</li>
<li>how the results of these processes are used to evaluate and continually improve
the occupational health and safety management system.</li>
</ul>
</li>
<li>
A description of the processes for workers to report work-related hazards and
hazardous situations, and an explanation of how workers are protected against
reprisals.</li>
<li>
A description of the policies and processes for workers to remove themselves from
work situations that they believe could cause injury or ill health, and an explanation of
how workers are protected against reprisals.</li>
<li>
A description of the processes used to investigate work-related incidents, including the
processes to identify hazards and assess risks relating to the incidents, to determine
corrective actions using the hierarchy of controls, and to determine improvements
needed in the occupational health and safety management system.</li>
</ul>
  </strong><br><hr>
  <div><strong>GUIDANCE</strong></div>
  <div><strong>Guidance for Disclosure 403-2-a</strong></div>
  <p style="text-indent:30px; text-align:justify">
      When describing the processes used to identify hazards and assess risks on a routine and non-routine basis, and to apply the hierarchy of controls, the reporting organization can:
      <ul style="padding-left:40px;list-style-type:disc">
          <li>
            specify whether these processes are based on legal requirements and/or recognized standards/guidelines;
          </li>
          <li>
            describe the frequency and scope of processes undertaken on a routine basis;
          </li>
          <li>
            describe the triggers for processes undertaken on a non-routine basis, such as changes in operating procedures or equipment; incident investigations; worker complaints or referrals; changes in workers or workflow; results of surveillance of work environment and worker health, including exposure monitoring (e.g., exposure to noise, vibration, dust);
          </li>
          <li>
            explain how obstacles to the implementation of these processes are removed for workers who might be more vulnerable to the risk of work-related injury or ill health, such as workers facing language barriers or having visual or hearing impairments (e.g., by providing occupational health and safety training and information in a language easily understood by workers).
          </li>
      </ul>
  </p>
  <div><strong>Guidance for Disclosure 403-2-b and Disclosure 403-2-c</strong></div>
  <p style="text-indent:30px; text-align:justify">
      Protecting workers against reprisals involves putting policies and processes in place that provide them with protection against intimidation, threats, or acts that could have a negative impact on their employment or work engagement, including termination, demotion, loss of compensation, discipline, and any other unfavorable treatment. Workers might face reprisals on account of their decision to either remove themselves from work situations that they believe could cause injury or ill health, or for reporting hazards or hazardous situations to their workers’ representatives, to their employer, or to regulatory authorities.
  </p>
  <p style="text-indent:30px; text-align:justify">
      Disclosure 403-2-c covers the right of workers to refuse or stop unsafe or unhealthy work. Workers have the right to remove themselves from work situations that they believe could cause them or another person injury or ill health.
  </p>`,
  "403-3": `
  <div>
      <strong>Disclosure 403-3 Occupational health services</strong>
  </div>
  <div>REQUIREMENTS</div>
  <strong>
  The reporting organization shall report the following information for employees and for
workers who are not employees but whose work and/or workplace is controlled by the
organization:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>
A description of the occupational health services’ functions that contribute to the
identification and elimination of hazards and minimization of risks, and an explanation
of how the organization ensures the quality of these services and facilitates workers’
access to them.</li>
</ul>
  </strong><br><hr>
  <div><strong>RECOMMENDATIONS</strong></div>
  The reporting organization should report the following additional information:
  <ul style="padding-left:40px">
      <li>
          1.3.1	How the organization maintains the confidentiality of workers’ personal health- related information;
      </li>
      <li>
          1.3.2	How the organization ensures that workers’ personal health-related information and their participation in any occupational health services is not used for any favorable or unfavorable treatment of workers
      <li>
  </ul>
  <br>
  <div><strong>GUIDANCE</strong></div>
  <p style="text-indent:30px; text-align:justify">
      Occupational health services aim to protect the health of workers in relation to their work environment.
      When describing how the quality of occupational health services is ensured, the reporting organization can explain whether the services are provided by competent individuals with recognized qualifications and accreditations, and whether it complies with legal requirements and/or recognized standards/guidelines.
  </p>
  <p style="text-indent:30px; text-align:justify">
      When describing how it facilitates workers’ access to occupational health services, the organization can describe whether it provides these services at the workplace and during working hours; whether it arranges transport to health clinics or expedites service there; whether it provides information about the services, including in a language easily understood by workers; and whether it adjusts workloads to allow workers to make use of these services.
      The organization can also report the metrics used to evaluate the effectiveness of these services, and the approaches used to raise awareness about them and encourage participation.
  </p>
  <div><strong>Guidance for clauses 1.3.1 and 1.3.2</strong></div>
  <p style="text-indent:30px; text-align:justify">
      Occupational health services are expected to respect workers’ right to privacy. Organizations are expected not to use workers' participation in such services and programs, or the health data derived therefrom, as criteria for their decisions regarding the employment or engagement of workers, including termination, demotion, promotion or offering of prospects, compensation, or any other favorable or unfavorable treatment.
  </p>`,
  "403-4": `
  <div>
      <strong>Disclosure 403-4 Worker participation, consultation, and communication on occupational health and safety</strong>
  </div>
  <div><strong>REQUIREMENTS</strong></div>
  <strong>
  The reporting organization shall report the following information for employees and for
workers who are not employees but whose work and/or workplace is controlled by the
organization:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>
A description of the processes for worker participation and consultation in the
development, implementation, and evaluation of the occupational health and safety
management system, and for providing access to and communicating relevant
information on occupational health and safety to workers.</li>
<li>
Where formal joint management-worker health and safety committees exist, a
description of their responsibilities, meeting frequency, decision-making authority, and
whether and, if so, why any workers are not represented by these committees.</li>
</ul>
  </strong><br><hr>
  <div><strong>RECOMMENDATIONS</strong></div>
  The reporting organization should report whether and, if so, which occupational health and safety topics are covered in local or global formal agreements with trade unions.    <div><strong>GUIDANCE</strong></div>
  <div><strong>Guidance for Disclosure 403-4-a</strong></div>
  <p style="text-indent:30px; text-align:justify">
      When describing the processes for worker participation in occupational health and safety, the reporting organization can include information on:
  </p>
  <p style="text-indent:30px; text-align:justify">
      When describing the processes for providing access to and communicating relevant information on occupational health and safety to workers, the organization can report whether it provides information about work-related incidents and the actions taken in response.
      <ul style="padding-left:40px;list-style-type:disc">
          <li>
            formal participation, based on legal requirements;
          </li>
          <li>
            participation through engagement with formally recognized workers’ representatives;
          </li>
          <li>
            direct participation, particularly by affected workers (e.g., the direct involvement of all workers in occupational health and safety decisions in small organizations);
          </li>
          <li>
            the use of committees, and how these committees are established and operated;
          </li>
          <li>
            participation in the occupational health and safety management system (e.g., participation in identification of hazards, assessment of risks, application of the hierarchy of controls, investigation of incidents, audits, decision-making about the use of contractors and outsourcing);
          </li>
          <li>
            how obstacles to participation are identified and removed (e.g., by providing training, by protecting workers against reprisals).
          </li>
      </ul>
      <p style="text-indent:40px; text-align:justify">
          The number, age, gender, and region of an organization’s new employee hires can indicate its strategy and ability to attract diverse, qualified employees. This information can signify the organization’s efforts to implement inclusive recruitment practices based on age and gender. It can also signify the optimal use of available labor and talent in different regions.
      </p>
      <p style="text-indent:40px; text-align:justify">    
          A high rate of employee turnover can indicate levels of uncertainty and dissatisfaction among employees. It can also signal a fundamental change in the structure of an organization’s core operations. An uneven pattern of turnover by age or gender can indicate incompatibility or potential inequity in the workplace. Turnover results in changes to the human and intellectual capital of the organization and can impact productivity. Turnover has direct cost implications either in terms of reduced payroll or greater expenses for the recruitment of employees
      </p>
  </p>`,
  "403-5": `
  <div>
      <strong>Disclosure 403-5 Worker training on occupational health and safety</strong>
  </div> 
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The reporting organization shall report the following information for employees and for
workers who are not employees but whose work and/or workplace is controlled by the
organization:<br>
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>A description of any occupational health and safety training provided to workers,
including generic training as well as training on specific work-related hazards,
hazardous activities, or hazardous situations.</li>
</ul>
    </strong>
  <div><strong>GUIDANCE</strong></div>
  <p style="text-indent:30px; text-align:justify">
      When describing the occupational health and safety training provided, the reporting organization can include information on:
  </p>
  <p style="text-indent:30px; text-align:justify">
      When reporting on the occupational health and safety management system, the organization can also describe:
      <ul style="padding-left:40px;list-style-type:disc">
          <li>
              how training needs are assessed;
          </li>
          <li>
            how the training is designed and delivered, including the content or topics addressed, the competency of trainers, which workers receive the training, the frequency of the training, and whether the training is provided in a language easily understood by workers;
          </li>
          <li>
              whether the training is provided free of charge and during paid working hours – if not, whether it is mandatory for workers to attend, and whether they are compensated for this;
          </li>
          <li>
              how the effectiveness of the training is evaluated
          </li>
      </ul>
  </p>`,
  "403-6": `
  <div>
      <strong>Disclosure 403-6 Promotion of worker health</strong>
  </div>
  <div><strong>REQUIREMENTS</strong></div>
  <strong>
  The reporting organization shall report the following information for employees and for
workers who are not employees but whose work and/or workplace is controlled by the
organization:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>
An explanation of how the organization facilitates workers’ access to non-occupational
medical and healthcare services, and the scope of access provided.</li>
<li>
A description of any voluntary health promotion services and programs offered to
workers to address major non-work-related health risks, including the specific health
risks addressed, and how the organization facilitates workers’ access to these
services and programs.</li>
</ul>
  </strong><br><hr>
  <div><strong>RECOMMENDATIONS</strong></div>
  The reporting organization should report the following additional information:
  <ul style="padding-left:40px;list-style-type:disc">
      <li>
          1.5.1	How the organization maintains the confidentiality of workers’ personal health- related information;
      </li>
      <li>
          1.5.2	How the organization ensures that workers’ personal health-related information and their participation in any services or programs is not used for any favorable or unfavorable treatment of workers
      </li>
  </ul>
  <div><strong>GUIDANCE</strong></div>
  <div><strong>Guidance for Disclosure 403-6-a</strong></div>
  <p style="text-indent:30px; text-align:justify">
      Achieving universal health coverage, including financial risk protection; access to quality essential healthcare services; and access to safe, effective, quality and affordable essential medicines and vaccines for all, is one of the targets of the UN Sustainable Development Goals (Target 3.8).
      Workers’ access to non-occupational medical and healthcare services might be facilitated, for example, through company clinics or disease treatment programs, referral systems, or health insurance or financial contributions.
  </p>
  <p style="text-indent:30px; text-align:justify">
      When describing the scope of access to non-occupational medical and healthcare services provided, the reporting organization can specify the types of service to which access is facilitated and the types of worker that have access to them.
      If the organization does not facilitate workers’ access to non-occupational medical and healthcare services because it operates in a country where the population already has access to high-quality and accessible services (e.g., through financial or other support), the organization can state this in its reported information.<br>
  </p>
  <p style="text-indent:30px; text-align:justify">
      If the organization does not facilitate access to non-occupational medical and healthcare services for workers who are not employees because the employer of those workers facilitates their access to these services, the organization can state this in its reported information.
  </p>
  <div><strong>Guidance for Disclosure 403-6-b</strong></div>
  <p style="text-indent:30px; text-align:justify">
      Ensuring healthy lives and promoting wellbeing for all at all ages is one of the UN Sustainable Development Goals (Goal 3). This goal includes targets such as reducing premature mortality from non-communicable diseases through prevention and treatment, and promoting mental health and wellbeing; strengthening the prevention and treatment of substance abuse, including narcotic drug abuse and harmful consumption of alcohol; ensuring universal access to sexual and reproductive healthcare services; and ending epidemics of AIDS, tuberculosis, malaria, and neglected tropical diseases, and combating hepatitis, water-borne diseases, and other communicable diseases.
  </p>
  <p style="text-indent:30px; text-align:justify">
      Disclosure 403-6-b covers voluntary services and programs aimed at addressing major non- work-related health risks among workers, including both physical and mental health-related risks. Examples of these risks include smoking, drug and alcohol abuse, physical inactivity, unhealthy diets, HIV, and psychosocial factors.
  </p>
  <p style="text-indent:30px; text-align:justify">
      Voluntary health promotion programs and services might include smoking cessation programs, dietary advice, offering of healthy food in the canteen, stress-reducing programs, provision of a gym, or fitness programs. A program or service is voluntary when it does not set mandatory
      personal targets, and if incentives are provided, these are not associated with the organization’s decisions regarding employment or engagement of workers.
  </p>
  <p style="text-indent:30px; text-align:justify">
      Voluntary health promotion services and programs complement but cannot be a substitute for occupational health and safety services, programs and systems that prevent harm and protect workers from work-related injuries and ill health. Voluntary health promotion and occupational health and safety may be managed jointly by the organization, as part of an overall approach to ensuring the health and safety of workers.
  </p>
  <p style="text-indent:30px; text-align:justify">
      When describing how it facilitates workers’ access to voluntary health promotion services and programs, the organization can explain whether it allows workers to make use of these during paid working hours. The organization can also report if these services and programs are available for family members of workers.
  </p>
  When describing its voluntary health promotion services and programs, the organization can also report:
  <ul style="padding-left:40px;list-style-type:disc">
      <li>
        how the topics covered in these services and programs are selected, including how workers are engaged in the selection of topics;
      </li>
      <li>
          the extent to which these services and programs include proven effective interventions;
      </li>
      <li>
          the metrics used to evaluate the effectiveness of these services and programs;
      </li>
      <li>
        the approaches used to raise awareness about these services and programs and encourage participation.
      </li>   
  </ul>
  <div><strong>Guidance for clauses 1.5.1 and 1.5.2</strong></div>
  <p style="text-indent:30px; text-align:justify">
      Non-occupational health services and programs are expected to respect workers’ right to privacy. Organizations are expected not to use workers' participation in such services and programs, or the health data derived therefrom, as criteria for their decisions regarding the employment or engagement of workers, including termination, demotion, promotion or offering of prospects, compensation, or any other favorable or unfavorable treatment.
  </p>
  `,
  "403-7": `
  <div>
      <strong>Disclosure 403-7 Prevention and mitigation of occupational health and safety impacts directly linked by business relationships</strong>
  </div> 
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The reporting organization shall report the following information:
    <ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>
A description of the organization’s approach to preventing or mitigating significant
negative occupational health and safety impacts that are directly linked to its
operations, products, or services by its business relationships, and the related hazards
and risks.</li>
</ul>
    </strong>
  <div><strong>GUIDANCE</strong></div>
    <div><strong>Background</strong></div>
  <p style="text-indent:30px; text-align:justify">
      In cases where an organization has no control over both the work and workplace, it still has a responsibility to make efforts, including exercising any leverage it might have, to prevent and mitigate negative occupational health and safety impacts that are directly linked to its operations, products, or services by its business relationships. 
  </p>`,
  "403-8": `
  <div>
      <strong>Disclosure 403-8 Workers covered by an occupational health and safety management system</strong>
  </div>

    <div><strong>REQUIREMENTS</strong></div>
<strong>
The reporting organization shall report the following information:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>If the organization has implemented an occupational health and safety management
system based on legal requirements and/or recognized standards/guidelines:
<ul style='list-style-type:lower-roman;padding-left:40px' >
<li>
the number and percentage of all employees and workers who are not employees
but whose work and/or workplace is controlled by the organization, who are
covered by such a system;</li>
<li>
the number and percentage of all employees and workers who are not employees
but whose work and/or workplace is controlled by the organization, who are
covered by such a system that has been internally audited;</li>
<li>
the number and percentage of all employees and workers who are not employees
but whose work and/or workplace is controlled by the organization, who are
covered by such a system that has been audited or certified by an external party.
</li>
</li>
<li>
Whether and, if so, why any workers have been excluded from this disclosure, including
the types of worker excluded.</li>
<li>
Any contextual information necessary to understand how the data have been compiled,
such as any standards, methodologies, and assumptions used.</li>
</ul>
</strong><br><hr>
  <div><strong>GUIDANCE</strong></div>
  <b>Background</b><br>
  <p style="text-indent:30px; text-align:justify">
      Occupational health and safety management systems can serve as an effective approach to managing and continually eliminating hazards and minimizing risks. It is a systems-based approach that seeks to integrate occupational health and safety management into overall business processes. A system typically moves through a ‘plan-do-check-act’ cycle, promoting leadership and practice through meaningful consultation and participation of workers from all job levels in the organization.
  </p>
  <p style="text-indent:30px; text-align:justify">
      A systems-based approach, which encompasses fully integrated processes, can be a significant advancement over an approach that considers hazard identification, risk assessment, and incident investigation as isolated activities. Focusing on system deficiencies can enable an organization to identify deficiencies in its overall management of occupational health and safety; address resources, policy, and operational controls; and ensure continual improvement
  </p>
  <div><strong>Guidance for Disclosure 403-8</strong></div>
  <p style="text-indent:30px; text-align:justify">
      This disclosure indicates what proportion of an organization’s employees, and workers who are not employees but whose work and/or workplace is controlled by the organization, are covered by an occupational health and safety management system based on legal requirements and/or recognized standards/guidelines. The list of legal requirements and/or recognized standards/guidelines used by the reporting organization in its occupational health and safety management system are reported using Disclosures 403-1-a-i and 403-1-a-ii in the Topic management disclosures section.
  </p>
  <p style="text-indent:30px; text-align:justify">
      If not all workers are covered by the occupational health and safety management system, the organization can report whether any of the workers not covered are at high risk of work-related injury or ill health.
  </p>
  <p style="text-indent:30px; text-align:justify">
      In addition to the information required by this disclosure, the organization can report the number and percentage of sites covered by an occupational health and safety management system based on legal requirements and/or recognized standards/guidelines. <br />
      The organization can also describe:
  </p>
  <ul style="padding-left:40px;list-style-type:disc">
      <li>
          the approach used for internal audits (e.g., whether they are carried out following an internally-developed audit standard or a recognized audit standard, what is the qualification of the auditors);
      </li>
      <li>
          whether any processes or functions have been excluded from the scope of the audit or certification, and how occupational health and safety performance is being monitored in those areas;
      </li>
      <li>
          the audit or certification standard used.
      </li>
  </ul>
  <p style="text-indent:30px; text-align:justify">
      Audits by external parties may include both second-party and third-party audits. Second-party audits are usually performed by customers or others on behalf of customers, or by any other external parties that have a formal interest in the organization. Third-party audits are performed by independent organizations such as registrars (i.e., certification bodies) or regulators.
  </p>
  <div><strong>Guidance for Disclosure 403-8-b</strong></div>
  <p style="text-indent:30px; text-align:justify">
      Types of worker can be based on criteria such as full-time, part-time, non-guaranteed hours, permanent or temporary basis, type or degree of control (e.g., control of work or workplace, sole or shared control), and location, among others
  </p>`,
  "403-9": `
  <div>
      <strong>Disclosure 403-9 Work-related injuries</strong>
  </div>
  <div><strong>REQUIREMENTS</strong></div>
  <strong>
  The reporting organization shall report the following information:
  <ul  style='list-style-type:lower-alpha;padding-left:40px'>
  <li>
For all employees:<br>
 <ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>The number and rate of fatalities as a result of work-related injury;</li>
<li>The number and rate of high-consequence work-related injuries (excluding
fatalities);</li>
<li>The number and rate of recordable work-related injuries;</li>
<li>The main types of work-related injury;</li>
<li>The number of hours worked.</li>
</ul>
</li>
<li>
For all workers who are not employees but whose work and/or workplace is controlled
by the organization:
 <ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>The number and rate of fatalities as a result of work-related injury;</li>
<li>The number and rate of high-consequence work-related injuries (excluding
fatalities);</li>
<li>The number and rate of recordable work-related injuries;</li>
<li>The main types of work-related injury;</li>
<li>The number of hours worked.</li>
</ul>
</li>
<li>
The work-related hazards that pose a risk of high-consequence injury, including:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>how these hazards have been determined;</li>
<li>which of these hazards have caused or contributed to high-consequence injuries
during the reporting period;</li>
<li>
actions taken or underway to eliminate these hazards and minimize risks using
the hierarchy of controls.</li>
</ul>
</li>
<li>
Any actions taken or underway to eliminate other work-related hazards and minimize
risks using the hierarchy of controls.</li>
<li>
Whether the rates have been calculated based on 200,000 or 1,000,000 hours worked.</li>
<li>
Whether and, if so, why any workers have been excluded from this disclosure, including
the types of worker excluded.</li>
<li>
Any contextual information necessary to understand how the data have been compiled,
such as any standards, methodologies, and assumptions used.</li>
</ul>
Compilation requirements<br>
2.1 When compiling the information specified in Disclosure 403-9, the reporting
organization shall:
<ul  style='list-style-type:none;padding-left:40px'>
<li>2.1.1 exclude fatalities in the calculation of the number and rate of high-
consequence work-related injuries;</li>
<li>2.1.2 include fatalities as a result of work-related injury in the calculation of the
number and rate of recordable work-related injuries;</li>
<li>2.1.3 include injuries as a result of commuting incidents only where the transport
has been organized by the organization;</li>
<li>2.1.4 calculate the rates based on either 200,000 or 1,000,000 hours worked, using
the following formulas:</li>
</ul>
<div style='border:1px solid black;display:flex;'>
    <div>Rate of fatalities as a
result of work-related
injury</div>
        <div> = </div>
    <div style='display:flex;flex-direction:column'>
        <div style='border-bottom:1px solid black'>Number of fatalities as a result
of work-related injury</div>
        <div>Number of hours worked</div>
    </div>
    <div>x</div>
        <div>[200,000 or 1,000,000]</div>
</div>
<div style='border:1px solid black;display:flex;'>
    <div>Rate of high-consequence
work-related injuries
(excluding fatalities)</div>
        <div> = </div>
    <div style='display:flex;flex-direction:column'>
        <div style='border-bottom:1px solid black'>Number of high-consequence
work-related injuries
(excluding fatalities)</div>
        <div>Number of hours worked</div>
    </div>
    <div>x</div>
        <div>[200,000 or 1,000,000]</div>
</div>


<div style='border:1px solid black;display:flex;'>
    <div>Rate of recordable work-related injuries</div>
        <div> = </div>
    <div style='display:flex;flex-direction:column'>
        <div style='border-bottom:1px solid black'>Number of recordable work-
related injuries</div>
        <div>Number of hours worked</div>
    </div>
    <div>x</div>
        <div>[200,000 or 1,000,000]</div>
</div>

  </strong><br><hr>
  <div><strong>RECOMMENDATIONS</strong></div>
      The reporting organization should report the following additional information:
      <ul style="padding-left:40px">
          <li>
              2.2.1	If the numbers and rates reported are significantly higher for certain types of injury, countries, business lines, or workers’ demographics (e.g., sex, gender, migrant status, age, or worker type), a breakdown of these data;
          </li>
          <li>
              2.2.2	A breakdown of the number of recordable work-related injuries by type of incident;
          </li>
          <li>
              2.2.3	If chemical hazards have been identified in Disclosure 403-9-c, a list of the chemicals;
          </li>
          <li>
              2.2.4	The number of high-potential work-related incidents identified;
          </li>   
          <li>
              2.2.5	The number of close calls identified
          </li>
      </ul>
  <br>
  <div><strong>GUIDANCE</strong></div>
  <strong>Guidance for Disclosure 403-9</strong>
  <p style="text-indent:30px; text-align:justify">
      This disclosure covers work-related injuries. Data on work-related injuries are a measure of the extent of harm suffered by workers; they are not a measure of safety. <br>
      An increase in the number or rate of reported incidents does not necessarily mean that there have been a greater number of incidents than before; it can indicate an improvement in the recording and reporting of incidents. <br>
      If an increase in the number or rate of reported incidents is the result of the organization’s actions to improve the reporting and recording of fatalities, injuries, and ill health, or its actions to expand the scope of its management system to cover more workers or workplaces, the reporting organization can explain this and report on these actions and their results. <br>
      Types of work-related injury can include death, amputation of a limb, laceration, fracture, hernia, burns, loss of consciousness, and paralysis, among others. <br>
      In the context of this Standard, work-related musculoskeletal disorders are covered under ill health (and not injuries) and are to be reported using Disclosure 403-10. If the organization operates in a jurisdiction where worker compensation systems classify musculoskeletal disorders as injuries, the organization can explain this and report these disorders using Disclosure 403-9. <br>
      Injuries involving members of the public as a result of a work-related incident are not included in this disclosure, but the organization can report this information separately. For example, the organization can report incidents where a vehicle driven by a worker causes the deaths of other road users or incidents where visitors are injured during their visit to the organization’s workplace. <br>
      <div><strong>Guidance for reporting on high-consequence work-related injuries</strong></div>
      As per the definition of recordable work-related injury, the organization is required to report all work-related injuries as part of the ‘number and rate of recordable work-related injuries’. In addition, the organization is required to separately report high-consequence work- related injuries, with a breakdown by:
      <ul style="padding-left:40px;list-style-type:disc">
          <li>
              fatalities, to be reported using Disclosures 403-9-a-i and 403-9-b-i.
          </li>
          <li>
            other injuries from which the worker cannot recover (e.g., amputation of a limb), or does not or is not expected to recover fully to pre-injury health status within 6 months (e.g., fracture
              with complications), to be reported using Disclosures 403-9-a-ii and 403-9-b-ii.
          </li>
      </ul>
      <p style="text-indent:30px; text-align:justify">
          The definition of ‘high-consequence work-related injury’ uses ‘recovery time’, instead of ‘lost time’, as the criterion for determining the severity of an injury. Lost time is an indicator of the loss of productivity for an organization as a result of a work-related injury; it does not necessarily indicate the extent of harm suffered by a worker.
          ‘Recovery time’, in contrast, refers to the time needed for a worker to recover fully to pre-injury health status; it does not refer to the time needed for a worker to return to work. In some cases, a worker might return to work before full recovery.
          In addition to reporting information on high-consequence work-related injuries based on recovery time as required by this disclosure, the organization can also report the number and rate of work-related injuries that resulted in lost-workday cases, the average number of lost days per lost-workday case, the number of lost workdays, and the absentee rate.
      </p>
      <div><strong>Guidance for Disclosure 403-9-c </strong></div>
      <p style="text-indent:30px; text-align:justify">
          This disclosure covers work-related hazards that pose a risk of high-consequence injury if not controlled, even when there are control measures in place. The hazards might have been identified proactively through risk assessment, or reactively as a result of either a high-potential incident or a high-consequence injury.
          Examples of work-related hazards causing or contributing to high-consequence injuries include excessive workload demands, tripping hazards, or exposure to flammable materials.<br>
          If the identified work-related hazards vary significantly across different locations, the organization may group or disaggregate these by relevant categories, such as by geographical area or business line. Similarly, if there are a high number of hazards, the organization may group or categorize them to facilitate reporting.
          When reporting how it has determined which work-related hazards pose a risk of high- consequence injury using Disclosure 403-9-c-i, the organization can describe the criteria or threshold used to determine which hazards pose such a risk and which do not. The processes to identify hazards and assess risks, and to apply the hierarchy of controls, are reported using Disclosure 403-2-a.<br>
          Disclosure 403-9-c-ii does not require reporting which work-related hazards have caused or contributed to which high-consequence injuries during the reporting period; it requires the aggregate analysis of all work-related hazards that resulted in high-consequence injuries.
          If a work-related incident resulting in a high-consequence injury is still under investigation at the end of the reporting period, the organization can state this in its reported information. The organization can report on actions taken during the reporting period to eliminate hazards and minimize risks that were identified, or to address work-related incidents that took place, in prior reporting periods.
      </p>
      <div><strong>Guidance for Disclosure 403-9-d</strong></div>
      <p style="text-indent:30px; text-align:justify">
          This disclosure covers any actions taken or underway to eliminate other work-related hazards and minimize risks (i.e., not covered in Disclosure 403-9-c) using the hierarchy of controls. This disclosure can include actions taken in response to non-high-consequence work-related injuries, and work-related incidents with low probability of causing high-consequence injuries.
      </p>    
      <div><strong>Guidance for Disclosure 403-9-f</strong></div>
      <p style="text-indent:30px; text-align:justify">
          Types of worker can be based on criteria such as full-time, part-time, non-guaranteed hours, permanent or temporary basis, type or degree of control (e.g., control of work or workplace, sole or shared control), and location, among others.
      </p>    
      <div><strong>Guidance for Disclosure 403-9-g</strong></div>
      <p style="text-indent:30px; text-align:justify">
          If the organization follows the ILO code of practice on Recording and notification of occupational accidents and diseases, it can state this in response to Disclosure 403-9-g.
          If the organization does not follow the ILO code of practice, it can indicate which system of rules it applies in recording and reporting work-related injuries and its relationship to the ILO code of practice.<br>
          If the organization cannot directly calculate the number of hours worked, it may estimate this on the basis of normal or standard hours of work, taking into account entitlements to periods of paid leave of absence from work (e.g., paid vacations, paid sick leave, public holidays) and explain this in its reported information.
          When the organization cannot directly calculate or estimate the number of hours worked (e.g., because the workers performed non-routine work during an emergency situation, or because the performed work was not paid for by the hour), it is required to provide a reason for this omission as set out in GRI 1: Foundation 2021.
      </p>
      <div><strong>Guidance for clause 2.1.3</strong></div>
      <p style="text-indent:30px; text-align:justify">
          Clause 2.1.3 requires the organization to include injuries as a result of commuting incidents in cases where the transport has been organized by the organization (e.g., company or contracted bus or vehicle). The organization can report other commuting incidents separately; for example if this information is to be reported under local law.
      </p>    
      <div><strong>Guidance for clause 2.1.4</strong></div>
      <p style="text-indent:30px; text-align:justify">
          Clause 2.1.4 requires the organization to calculate the rates based on either 200,000 or 1,000,000 hours worked.
          Standardized rates allow for meaningful comparisons of statistics, for example between different periods or organizations, or help account for differences in the number of workers in the reference group and the number of hours worked by them.
          A rate based on 200,000 hours worked indicates the number of work-related injuries per 100 full-time workers over a one-year timeframe, based on the assumption that one full-time worker works 2,000 hours per year. For example, a rate of 1.0 means that, on average, there is one work-related injury for every group of 100 full-time workers over a one-year timeframe. A rate based on 1,000,000 hours worked indicates the number of work-related injuries per 500 full- time workers over a one-year timeframe.
          A rate based on 200,000 hours worked might be more suitable for small organizations.
          In addition to standardized rates, this disclosure requires the organization to report absolute data (i.e., numbers), to allow information users to calculate the rates themselves using other methodologies if needed.
      </p>
      <div><strong>Guidance for clause 2.2.1 and 2.2.2</strong></div>
      <p style="text-indent:30px; text-align:justify">
          Target 8.8 of the UN Sustainable Development Goals aims to ‘protect labour rights and promote safe and secure working environments for all workers, including migrant workers, in particular women migrants, and those in precarious employment’. Some groups might be at increased risk of work-related injury due to demographic factors such as sex, gender, migrant status, or age; it can thus be beneficial to break down data on work-related injuries by these demographic criteria. <br>
          ILO Convention 143 ‘Migrant Workers (Supplementary Provisions) Convention’ defines ‘migrant worker’ as ‘a person who migrates or who has migrated from one country to another with a view to being employed otherwise than on his own account and includes any person regularly admitted as a migrant worker’. <br>
          If the data on work-related injuries are driven primarily by certain types of injury (e.g., amputation, paralysis) or incident (e.g., explosion, road accident), the organization can provide a breakdown of this information.
      </p>
  </p>`,
  "403-10": `
  <div>
      <strong>Disclosure 403-10 Work-related ill health</strong>
  </div>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The reporting organization shall report the following information:
    <ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>For all employees:
    <ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>The number of fatalities as a result of work-related ill health;</li>
<li>The number of cases of recordable work-related ill health;</li>
<li>The main types of work-related ill health.</li>
</ul>
</li>
<li>
For all workers who are not employees but whose work and/or workplace is controlled
by the organization:
    <ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>The number of fatalities as a result of work-related ill health;</li>
<li>The number of cases of recordable work-related ill health;</li>
<li>The main types of work-related ill health.</li>
</ul>
</li>
<li>
The work-related hazards that pose a risk of ill health, including:
    <ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>how these hazards have been determined;</li>
<li>which of these hazards have caused or contributed to cases of ill health during the
reporting period;</li>
<li>actions taken or underway to eliminate these hazards and minimize risks using
the hierarchy of controls.</li>
</ul>
</li>
<li>
Whether and, if so, why any workers have been excluded from this disclosure, including
the types of worker excluded.</li>
<li>
Any contextual information necessary to understand how the data have been compiled,
such as any standards, methodologies, and assumptions used.</li>
</ul>

Compilation requirements<br>
2.3 When compiling the information specified in Disclosure 403-10, the reporting
organization shall include fatalities as a result of work-related ill health in the
calculation of the number of cases of recordable work-related ill health.
    </strong><br><hr>
  <div><strong>RECOMMENDATIONS</strong></div>
      The reporting organization should report the following additional information:
      <ul style="padding-left:40px">
          <li>
              2.4.1  If the numbers reported are significantly higher for certain types of ill health, countries, business lines, or workers’ demographics (e.g., sex, gender, migrant status, age, or worker type), a breakdown of these data;
          </li>
          <li>
              2.4.2  If chemical hazards have been identified in Disclosure 403-10-c, a list of the chemicals;
          <li>
          <li>
              2.4.3  The number of employees and workers who are not employees but whose work and/or workplace is controlled by the organization, exposed to each hazard identified in Disclosure 403-10-c.
          </li>
      </ul>
  <br>
  <div><strong>GUIDANCE</strong></div>
  <p style="text-indent:30px; text-align:justify">
      Work-related ill health can include acute, recurring, and chronic health problems caused or aggravated by work conditions or practices. They include musculoskeletal disorders, skin and respiratory diseases, malignant cancers, diseases caused by physical agents (e.g., noise- induced hearing loss, vibration-caused diseases), and mental illnesses (e.g., anxiety, post- traumatic stress disorder). This disclosure covers, but is not limited to, the diseases included in the ILO List of Occupational Diseases. In the context of this Standard, work-related musculoskeletal disorders are covered under ill health (and not injuries) and are to be reported using this disclosure. 
  </p>
  <p style="text-indent:30px; text-align:justify">
      This disclosure covers all cases of work-related ill health notified to the reporting organization or identified by the organization through medical surveillance, during the reporting period. The organization might be notified of cases of work-related ill health through reports by affected workers, compensation agencies, or healthcare professionals. The disclosure may include cases of work-related ill health that were detected during the reporting period among former workers. If the organization determines, for example through investigation, that the notified case
      of work-related ill health is not due to exposure whilst working for the organization, it can explain this in its reported information.
  </p>
  <p style="text-indent:30px; text-align:justify">
      This disclosure covers both short-latency and long-latency work-related ill health. Latency refers to the time period between exposure and the onset of ill health.
  </p>
  <p style="text-indent:30px; text-align:justify">
      Many cases of long-latency work-related ill health go undetected; if detected, they might not necessarily be due to exposures with one employer. For example, a worker might be exposed to asbestos while working for different employers over time, or might suffer from a long-latency disease that turns fatal many years after the worker has left the organization. For this reason, data on work-related ill health are to be complemented with information on work-related hazards.
  </p>
  <p style="text-indent:30px; text-align:justify">
      In some situations, an organization might not be able to collect or publicly disclose data on work-related ill health. The following are examples of these situations:
  </p>
  <ul style="padding-left:40px;list-style-type:disc">
      <li>
          National or regional regulations, contractual obligations, health insurance provisions, and other legal requirements related to the privacy of workers’ health-related information, might prevent organizations from collecting, maintaining, and publicly reporting these data.
      </li>
      <li>
          The nature of information on workers’ exposure to psychosocial factors, largely based on self-disclosure and in many instances protected under healthcare privacy regulations, might limit organizations in disclosing this information.
      </li>
  </ul>
  <p style="text-indent:30px; text-align:justify">
      In these situations, the organization is required to provide a reason for omission of these data as set out in GRI 1: Foundation 2021.
  </p>
  <p style="text-indent:30px; text-align:justify">
      Cases of ill health involving members of the public as a result of a work-related incident are not included in this disclosure, but the organization can report this information separately. An example of such an incident is when a chemical substance spill causes ill health among members of a nearby community.
  </p>
  <div><strong>Guidance for Disclosure 403-10-c</strong></div>
      <p style="text-indent:30px; text-align:justify">This disclosure includes exposures to the 'International Agency for Research on Cancer (IARC) Group 1’ (carcinogenic to humans), ‘IARC Group 2A’ (probably carcinogenic to humans), and ‘IARC Group 2B’ (possibly carcinogenic to humans) agents.</p>
  <div><strong>Guidance for Disclosure 403-10-d</strong></div>
      <p style="text-indent:30px; text-align:justify">Types of worker can be based on criteria such as full-time, part-time, non-guaranteed hours, permanent or temporary basis, type or degree of control (e.g., control of work or workplace, sole or shared control), and location, among others.</p>
  <div><strong>Guidance for Disclosure 403-10-e</strong></div>
      <p style="text-indent:30px; text-align:justify">If the organization follows the ILO code of practice on Recording and notification of occupational accidents and diseases, it can state this in response to Disclosure 403-10-e.<br>
      If the organization does not follow the ILO code of practice, it can indicate which system of rules it applies in recording and reporting work-related ill health and its relationship to the ILO code of practice.</p>
  <div><strong>Guidance for clause 2.4.1</strong></div>
  <p style="text-indent:30px; text-align:justify">If the data on work-related ill health are driven primarily by certain types of ill health or disease (e.g., respiratory diseases, skin diseases) or incident (e.g., exposure to bacteria or viruses), the organization can provide a breakdown of this information.
  </p>`,
  "404-1": `
  <div>
      <strong>Disclosure 404-1 Average hours of training per year per employee</strong>
  </div>
  <div><b>REQUIREMENTS</b></div>
  <strong>
  The reporting organization shall report the following information:
  <ul  style='list-style-type:lower-alpha;padding-left:40px'>
	<li>Average hours of training that the organization’s employees have undertaken during the reporting period, by:
  <ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>gender;</li>
<li>employee category.</li>
</ul>
</li>
</ul>
  </strong>
  <div><strong>RECOMMENDATIONS</strong></div>
      2.1 When compiling  the information  specified in Disclosure 404-1, the reporting organization should:
      <ul style="list-style-type:none;padding-left:40px;">
          <li>
              2.1.1   express employee numbers as either head count or full-time equivalent (FTE), and disclose and apply the approach consistently in the period, and between periods;
          </li>
          <li>
              2.1.2   use data from Disclosure 2-7 in GRI 2: General Disclosures 2021 to identify the total number of employees;
          </li>   
          <li>
              2.1.3   draw from the information used for Disclosure 405-1 in GRI 405: Diversity and Equal Opportunity 2016 to identify the total number of employees by employee category.
          </li>
      </ul>
  <div><strong>GUIDANCE</strong></div>
  <p style="text-indent:30px; text-align:justify">
      This disclosure provides insight into the scale of an organization’s investment in training, and the degree to which the investment is made across the entire employee base.
      In the context of this Standard, ‘training’ refers to:
  <ul style="padding-left:40px;list-style-type:disc">
      <li>
        all types of vocational training and instruction;
      </li>
      <li>
        paid educational leave provided by an organization for its employees;
      </li>
      <li>
        training or education pursued externally and paid for in whole or in part by an organization;
      </li>
      <li>
        training on specific topics.
      </li>
  </ul>
  </p>
  <div style='display:flex'>
    <div>Average training hours per employee</div>
    <div> = </div>
    <div style='display:flex;flex-direction:column'>
    <div style='margin-bottom:1px solid black'>Total number of training hours provided to employees</div>
    <div>Total number of employees</div>
    </div>

    <div style='display:flex'>
    <div>Average training hours per male</div>
    <div> = </div>
    <div style='display:flex;flex-direction:column'>
    <div style='margin-bottom:1px solid black'>Total number of training hours provided to male employees</div>
    <div>Total number of male employees</div>
    </div>

    <div style='display:flex'>
    <div>Average training hours per employee category</div>
    <div> = </div>
    <div style='display:flex;flex-direction:column'>
    <div style='margin-bottom:1px solid black'>Total number of training hours provided to each category of employees</div>
    <div>Total number of employees in each category</div>
  </div>   
  A number of calculations can be undertaken to report on employee categories. These calculations are specific to each organization.

  `,
  "404-2": `
  <div>
      <strong>Disclosure 404-2  Programs for upgrading employee skills and transition assistance programs</strong>
  </div>
  <div><strong>REQUIREMENTS</strong></div>
  <strong>
  The reporting organization shall report the following information:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>Type and scope of programs implemented and assistance provided to upgrade employee skills.</li>
<li>
Transition assistance programs provided to facilitate continued employability and the management of career endings resulting from retirement or termination of employment.</li>
</ul>
  </strong>
  <div><strong>GUIDANCE</strong></div>
  <p style=" text-align:justify">
      Employee training programs that aim to upgrade skills can include:
      <ul style="padding-left:40px;list-style-type:disc">
          <li>
              internal training courses;
          </li>
          <li>
              funding support for external training or education;
          </li>
          <li>
              the provision of sabbatical periods with guaranteed return to employment.
          </li>   
      </ul>
      Transition assistance programs provided to support employees who are retiring or who have been terminated can include:
      <ul style="padding-left:40px;list-style-type:disc">
          <li>
              pre-retirement planning for intended retirees;
          </li>
          <li>
              retraining for those intending to continue working;
          </li>
          <li>
              severance pay, which can take into account employee age and years of service;
          </li>
          <li>
              job placement services;
          </li>
          <li>
              assistance (such as training, counselling) on transitioning to a non-working life.
          </li>
      </ul>
  </p>
  <b>Background</b><br>
  <p style="text-indent:30px; text-align:justify">
      Programs for upgrading employee skills allow an organization to plan skills acquisition that equips employees to meet strategic targets in a changing work environment. More skilled employees enhance the organization’s human capital and contribute to employee satisfaction, which correlates strongly with improved performance. For those facing retirement, confidence and quality of work relations is improved by the knowledge that they are supported in their transition from work to retirement
  </p>`,
  "404-3": `
  <div>
      <strong>Disclosure 404-3 Percentage of employees receiving regular performance and career development reviews</strong>
  </div>
    <div><strong>REQUIREMENTS</strong></div>
  <strong>  The reporting organization shall report the following information:

    <ul  style='list-style-type:lower-alpha;padding-left:40px'>
    <li>
    Percentage of total employees by gender and by employee category who received a regular performance and career development review during the reporting period.
</li>
    </ul></strong><br><hr>
  <div><strong>RECOMMENDATIONS</strong></div>
      When compiling the information specified in Disclosure 404-3, the reporting organization should:
      <ul style="padding-left:40px;">
          <li>
              2.2.1   use data from Disclosure 2-7 in GRI 2: General Disclosures 2021 to identify the total number of employees;
          </li>
          <li>
              2.2.2   draw from the information used for Disclosure 405-1 in GRI 405: Diversity and Equal Opportunity 2016 to identify the total number of employees by employee category
          </li>
      </ul>
  <div><strong>GUIDANCE</strong></div>
  <b>Background</b><br>
  <p style="text-indent:30px; text-align:justify">
      This disclosure measures the extent to which an organization regularly appraises employee performance. This aids the personal development of individual employees. It also contributes to skills management and to the development of human capital within the organization. This disclosure also demonstrates the extent to which this system is applied throughout the organization, and whether there is inequity of access to these opportunities.
  </p>
  <p style="text-indent:30px; text-align:justify">
      Regular performance and career development reviews can also enhance employee satisfaction, which correlates with improved organizational performance. This disclosure helps demonstrate how an organization works to monitor and maintain the skill sets of its employees. When reported in conjunction with Disclosure 404-2, the disclosure also helps to illustrate how the organization approaches skills enhancement.
  </p>`,
  "405-1": `
<div>
    <strong>Disclosure 405-1 Diversity of governance bodies and employees</strong>
</div>
<div><strong>REQUIREMENTS</strong></div>
<div><strong>
The reporting organization shall report the following information:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>Percentage of individuals within the organization’s governance bodies in each of the following diversity categories:
<ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>Gender;</li>
<li>Age group: under 30 years old, 30-50 years old, over 50 years old;</li>
<li>Other indicators of diversity where relevant (such as minority or vulnerable groups).</li>
</ul>
</li>
<li>
Percentage of employees per employee category in each of the following diversity categories:
<ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>
Gender;</li>
<li>
Age group: under 30 years old, 30-50 years old, over 50 years old;</li>
<li>
Other indicators of diversity where relevant (such as minority or vulnerable groups).</li>
</ul>
</li>
</ul>
</strong></div>
<div><strong>RECOMMENDATIONS</strong></div>
When compiling the information specified in Disclosure 405-1, the reporting organization should use data from Disclosure 2-7 in GRI 2: General Disclosures 2021 to identify the total number of employees.
  <div><strong>GUIDANCE</strong></div>
  <p style="text-indent:30px; text-align:justify">
      Examples of governance bodies that exist within an organization can be the board of directors, management committee, or a similar body for a non-corporate organization.
  </p>
  <p style="text-indent:30px; text-align:justify">
      An organization can identify any other indicators of diversity used in its own monitoring and recording that are relevant for reporting.
  </p>
  <p style="text-indent:30px; text-align:justify">
      This disclosure provides a quantitative measure of diversity within an organization and can be used in conjunction with sectoral or regional benchmarks. Comparisons between broad employee diversity and management team diversity offer information on equal opportunity.
  </p>
  <p style="text-indent:30px; text-align:justify">
      Information reported in this disclosure also helps in assessing which issues can be of particular relevance to certain segments of the governance bodies or employees.
  </p>`,
  "405-2": `
  <div>
      <strong>Disclosure 405-2 Ratio of basic salary and remuneration of women to men</strong>
  </div>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The reporting organization shall report the following information:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>Ratio of the basic salary and remuneration of women to men for each employee category, by significant locations of operation.</li>
<li>The definition used for ‘significant locations of operation’.</li>
</ul>
    </strong><br><hr>
  <div><strong>RECOMMENDATIONS</strong></div>
      The reporting organization can draw from the information used for Disclosure 405-1 to identify the total number of employees in each employee category by gender.
  <div><strong>GUIDANCE</strong></div>
  <p style="text-indent:30px; text-align:justify">
      An organization can take an active role in reviewing its operations and decisions, in order to promote diversity, eliminate gender bias, and support equal opportunity. These principles apply equally to recruitment, opportunities for advancement, and remuneration policies. Equality of remuneration is also an important factor in retaining qualified employees.
  </p>`,
  "406-1": `
  <div>
      <strong>Disclosure 406-1 Incidents of discrimination and corrective actions taken</strong>
  </div>
  <div><strong>REQUIREMENTS</strong></div>
  <strong>
  The reporting organization shall report the following information:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>Total number of incidents of discrimination during the reporting period.</li>
<li>Status of the incidents and actions taken with reference to the following:
<ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>Incident reviewed by the organization;</li>
<li>Remediation plans being implemented;</li>
<li>Remediation plans that have been implemented, with results reviewed through routine internal management review processes;</li>
<li>Incident no longer subject to action.</li>
</li>

</ul>
Compilation requirements<br>
2.1 When compiling the information specified in Disclosure 406-1, the reporting organization shall include incidents of discrimination on grounds of race, color, sex, religion, political opinion, national extraction, or social origin as defined by the ILO, or other relevant forms of discrimination involving internal and/or external stakeholders across operations in the reporting period.
  </strong>
  <div><strong>GUIDANCE</strong></div>
  <p style="text-indent:30px; text-align:justify">
      In the context of this disclosure, an ‘incident’ refers to a legal action or complaint registered with the reporting organization or competent authorities through a formal process, or an instance of non-compliance identified by the organization through established procedures. Established procedures to identify instances of non-compliance can include management system audits, formal monitoring programs, or grievance mechanisms.
  </p>
  <p style="text-indent:30px; text-align:justify">
      An incident is no longer subject to action if it is resolved, the case is completed, or no further action is required by the organization. For example, an incident for which no further action is required can include cases that were withdrawn or where the underlying circumstances that led to the incident no longer exist.
  </p>
  <p style="text-indent:30px; text-align:justify">
      According to ILO instruments, discrimination can occur on the grounds of race, color, sex, religion, political opinion, national extraction, and social origin. Discrimination can also occur based on factors such as age, disability, migrant status, HIV and AIDS, gender, sexual orientation, genetic predisposition, and lifestyles, among others.
  </p>
  <p style="text-indent:30px; text-align:justify">
      The presence and effective implementation of policies to avoid discrimination are a basic expectation of responsible business conduct.
  </p>`,
  "407-1": `
  <div>
      <strong>Disclosure 407-1 Operations and suppliers at significant risk for incidents of child labor</strong>
  </div>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The reporting organization shall report the following information:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>Operations and suppliers in which workers’ rights to exercise freedom of association or collective bargaining may be violated or at significant risk either in terms of:
<ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>type of operation (such as manufacturing plant) and supplier;</li>
<li>countries or geographic areas with operations and suppliers considered at risk.</li>
</ul>
</li>
<li>
Measures taken by the organization in the reporting period intended to support rights to exercise freedom of association and collective bargaining.</li>
</ul>
    </strong><br><hr>
  <div><strong>GUIDANCE</strong></div>
  <p style="text-indent:30px; text-align:justify">
      The process for identifying operations and suppliers, as specified in Disclosure 407-1, can reflect the reporting organization’s approach to risk assessment on this issue. It can also draw from recognized international data sources, such as the various outcomes of the ILO Supervisory bodies and the recommendations of the ILO Committee of Freedom of Association.
  </p>
  <p style="text-indent:30px; text-align:justify">
      When reporting the measures taken, the organization can refer to the ILO ‘Tripartite Declaration of Principles Concerning Multinational Enterprises and Social Policy’ and Organisation for Economic Co-operation and Development (OECD) OECD Guidelines for Multinational Enterprises for further guidance.
  </p>
  <b>Background</b><br>
  <p style="text-indent:30px; text-align:justify">
      This disclosure concerns an organization’s due diligence with respect to any negative impacts its activities have had on the human rights of workers to form or join trade unions and to bargain collectively. This can include policies and processes with respect to the organization's business relationships, including its suppliers. It can also include the due diligence process to identify operations and suppliers where these rights are at risk.
  </p>
  <p style="text-indent:30px; text-align:justify">
      It also aims to reveal actions that have been taken to support these rights across an organization’s range of operations. This disclosure does not require the organization to express a specific opinion on the quality of national legal systems.
  </p>
  <p style="text-indent:30px; text-align:justify">
      Collective agreements can be made at the level of the organization, at the level of a particular site, at the industry level, and at the national level in countries where this is the practice.
  </p>
  <p style="text-indent:30px; text-align:justify">
      Collective agreements can cover specific groups of workers, for example, those performing a specific activity or working at a specific location.
  </p>
  <p style="text-indent:30px; text-align:justify">
      An organization is expected to respect the rights of workers to exercise freedom of association and collective bargaining. It is also expected to not benefit from or contribute to such violations through its business relationships (e.g., suppliers).
  </p>`,
  "408-1": `
  <div>
      <strong>Disclosure 408-1 Operations and suppliers at significant risk for incidents of child labor</strong>
  </div>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The reporting organization shall report the following information:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>
Operations and suppliers considered to have significant risk for incidents of:
<ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>child labor;</li>
<li>young workers exposed to hazardous work.</li>
</ul>
</li>
<li>
Operations and suppliers considered to have significant risk for incidents of child labor either in terms of:
<ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>
type of operation (such as manufacturing plant) and supplier;</li>
<li>
countries or geographic areas with operations and suppliers considered at risk.</li>
</ul>
</li>
<li>
Measures taken by the organization in the reporting period intended to contribute to the effective abolition of child labor.</li>
</ul>
    </strong>
  <div><strong>GUIDANCE</strong></div>
  <p style="text-indent:30px; text-align:justify">
      The process for identifying operations and suppliers, as specified in Disclosure 408-1, can reflect the reporting organization’s approach to risk assessment on this issue. It can also draw from recognized international data sources, such as the ILO Information and reports on the application of Conventions and Recommendations.
  </p>
  <p style="text-indent:30px; text-align:justify">
      When reporting the measures taken, the organization can refer to the ILO ‘Tripartite Declaration of Principles Concerning Multinational Enterprises and Social Policy’ and Organisation for Economic Co-operation and Development (OECD) OECD Guidelines for Multinational Enterprises for further guidance.
  </p>
  <p style="text-indent:30px; text-align:justify">
      In the context of the GRI Standards, a ‘young worker’ is defined as a person above the applicable minimum working age and younger than 18 years of age. Note that Disclosure 408-1 does not require quantitative reporting on child labor or the number of young workers. Rather, it asks for reporting on the operations and suppliers considered to have significant risk for incidents of child labor or young workers exposed to hazardous work.
  </p>
  <b>Background</b><br>
  <p style="text-indent:30px; text-align:justify">
      Child labor is subject to ILO Conventions 138 ‘Minimum Age Convention’ (ILO Convention 138) and 182 ‘Worst Forms of Child Labour Convention’ (ILO Convention 182).
  </p>
  <p style="text-indent:30px; text-align:justify">
      ‘Child labor’ refers to an abuse, which is not to be confused with ‘children working’ or with ‘young persons working’, which may not be abuses as stipulated in ILO Convention 138.
  </p>
  <p style="text-indent:30px; text-align:justify">
      The minimum age for working differs by country. ILO Convention 138 specifies a minimum age of 15 years or the age of completion of compulsory schooling (whichever is higher). However, there is an exception for certain countries where economies and educational facilities are insufficiently developed and a minimum age of 14 years might apply. These countries of exception are specified by the ILO in response to special application by the country concerned, and in consultation with representative organizations of employers and workers.
  </p>
  <p style="text-indent:30px; text-align:justify">
      ILO Convention 138 stipulates that ‘national laws or regulations may permit the employment or work of persons 13 to 15 years of age on light work which is (a) not likely to be harmful to their health or development; and (b) not such as to prejudice their attendance at school, their participation in vocational orientation or training programmes approved by the competent authority or their capacity to benefit from the instruction received’.
  </p>    
  <p style="text-indent:30px; text-align:justify">
      While child labor takes many different forms, a priority is to eliminate without delay the worst forms of child labor as defined by Article 3 of ILO Convention 182. This includes all forms of slavery or practices similar to slavery (such as sale, trafficking, forced or compulsory labor, serfdom, recruitment for armed conflict); the use, procuring or offering of a child for prostitution or illicit activities and any work that is likely to harm the health, safety or morals of children. ILO Convention 182 is intended to set priorities for states; however, organizations are expected not to use this convention to justify forms of child labor.
  </p>
  <p style="text-indent:30px; text-align:justify">
      Child labor results in under-skilled and unhealthy workers for tomorrow and perpetuates poverty across generations, thus impeding sustainable development. The abolition of child labor is therefore necessary for both economic and human development.           
  </p>`,
  "409-1": `
  <div><strong>Disclosure 409-1 Operations and suppliers at significant risk for incidents of forced or compulsory labor
</strong></div>
  <br>
  <div><strong>REQUIREMENTS</strong></div>
  <strong>
  The reporting organization shall report the following information:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>
Operations and suppliers considered to have significant risk for incidents of forced or compulsory labor either in terms of:
<ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>type of operation (such as manufacturing plant) and supplier;</li>
<li>countries or geographic areas with operations and suppliers considered at risk.</li>
</ul>
</li>
<li>
Measures taken by the organization in the reporting period intended to contribute to the elimination of all forms of forced or compulsory labor.
</li>
</ul>
  </strong>
<div><b>GUIDANCE</b></div>
<b>Guidance for Disclosure 409-1</b>
The process for identifying operations and suppliers, as specified in Disclosure 409-1, can reflect the reporting organization’s approach to risk assessment on this issue. It can also draw from recognized international data sources, such as the ILO Information and reports on the application of Conventions and Recommendations (see reference [1] in the Bibliography).<br>
When reporting the measures taken, the organization can refer to the ILO ‘Tripartite Declaration of Principles Concerning Multinational Enterprises and Social Policy’ and Organisation for Economic Co-operation and Development (OECD) OECD Guidelines for Multinational Enterprises for further guidance.<br>
<b>Background</b><br>
Forced or compulsory labor exists globally in a variety of forms. The most extreme examples are slave labor and bonded labor, but debts can also be used as a means of maintaining workers in a state of forced labor. Indicators of forced labor can also include withholding identity papers, requiring compulsory deposits, and compelling workers, under threat of firing, to work extra hours to which they have not previously agreed.<br>
Eliminating forced labor remains an important challenge. Forced labor is not only a serious violation of a fundamental human right, it also perpetuates poverty and is a hindrance to economic and human development.<br>

The presence and effective implementation of policies for eliminating all forms of forced or compulsory labor are a basic expectation of responsible business conduct. Organizations with multinational operations are required by law in some countries to provide information on their efforts to eradicate forced labor in their supply chains.
  `,
  "410-1": `
      <div><strong>Disclosure 410-1 Security personnel trained in human
rights policies or procedures</strong></div>
  <br>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The reporting organization shall report the following information:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>	Percentage of security personnel who have received formal training in the organization’s human rights policies or specific procedures and their application to security.</li>
<li>
Whether training requirements also apply to third-party organizations providing security personnel.</li>
</ul>
    </strong>
  <div><strong>RECOMMENDATIONS</strong></div>
  2.1 When compiling the information specified in Disclosure 410-1-a, the reporting
organization should:<br>
<ul style='list-style-type:none;padding-left:40px'>
<li>
2.1.1 calculate the percentage using the total number of security personnel, whether
they are employees of the organization or employees of third-party organizations;
</li>
<li>
2.1.2  state whether employees of third-party organizations are also included in the
calculation.
</li>
</ul>
<br>
<strong>GUIDANCE</strong><br>
The training can refer either to training dedicated to the subject of human rights or to a human
rights module within a general training program. Training can cover issues such as the use of
force, inhuman or degrading treatment or discrimination, or identification and registering.<br>
The use of security personnel can play an essential role in allowing an organization to operate
in a safe and productive manner, and can contribute to the security of local communities and
populations.
However, as set out in the International Code of Conduct for Private Security Service Providers,
the use of security personnel can also have negative impacts on local populations and on the
upholding of human rights and the rule of law.
According to the UN Human Rights Office of the High Commissioner, ‘human rights education
constitutes an essential contribution to the long-term prevention of human rights abuses and
represents an important investment in the endeavor to achieve a just society in which all human
rights of all persons are valued and respected.1
Training security personnel in human rights can therefore help to ensure their appropriate
conduct towards third parties, particularly regarding the use of force. This disclosure indicates
the proportion of the security force that can reasonably be assumed to be aware of an
organization’s expectations of human rights performance. Information provided under this
disclosure can demonstrate the extent to which management systems pertaining to human
rights are implemented.
  `,
  "411-1": `
        <div><strong>Disclosure 411-1 Incidents of violations involving rights
of indigenous peoples</strong></div>
  <br>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The reporting organization shall report the following information:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>Total number of identified incidents of violations involving the rights of indigenous peoples during the reporting period.</li>
<li>
Status of the incidents and actions taken with reference to the following:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>
Incident reviewed by the organization;</li>
<li>
Remediation plans being implemented;</li>
<li>
Remediation plans that have been implemented, with results reviewed through routine internal management review processes;</li>
<li>
Incident no longer subject to action.</li>
</ul>
</li>
</ul>
    </strong><br><hr>
  <div><strong>RECOMMENDATIONS</strong></div>
  2.1 When compiling the information specified in Disclosure 411-1, the reporting organization
should include incidents involving the rights of indigenous peoples among:<br>
<ul style='padding-left:40px'>
<li>
2.1.1  workers performing the organization’s activities;
</li>
<li>
2.1.2 communities likely to be affected by existing or planned activities of the
organization.
</li>
</ul>
<br>
<b>GUIDANCE</b>
In the context of this disclosure, an ‘incident’ refers to a legal action or complaint registered with
the reporting organization or competent authorities through a formal process, or an instance of
non-compliance identified by the organization through established procedures. Established
procedures to identify instances of non-compliance can include management system audits,
formal monitoring programs, or grievance mechanisms.<br>

The number of recorded incidents involving the rights of indigenous peoples provides
information about the implementation of an organization’s policies relating to indigenous
peoples. This information helps to indicate the state of relations with stakeholder communities.
This is particularly important in regions where indigenous peoples reside, or have interests
near operations of the organization.
  `,
  "413-1": `
          <div><strong>Disclosure 413-1 Operations with local community
engagement, impact assessments, and development
programs</strong></div>
  <br>
  <div><strong>REQUIREMENTS</strong></div>
  <strong>
  The reporting organization shall report the following information:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
Percentage of operations with implemented local community engagement, impact assessments, and/or development programs, including the use of:
<li>
<ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>social impact assessments, including gender impact assessments, based on participatory processes;</li>
<li>
environmental impact assessments and ongoing monitoring;</li>
<li>
public disclosure of results of environmental and social impact assessments;</li>
<li>
local community development programs based on local communities’ needs;</li>
<li>
stakeholder engagement plans based on stakeholder mapping;</li>
<li>
broad based local community consultation committees and processes that include vulnerable groups;</li>
<li>
works councils, occupational health and safety committees and other worker representation bodies to deal with impacts;</li>
<li>
formal local community grievance processes.</li>
</li>
</ul>
</li>
</ul>
  </strong><br><hr>
  <div><strong>RECOMMENDATIONS</strong></div>
 2.1 When compiling the information specified in Disclosure 413-1, the reporting organization
should use the data from Disclosure 2-6 in GRI 2: General Disclosures 2021 to identify
the total number of operations, if the organization has reported its total number of
operations when describing its activities.<br>
<br>
<b>GUIDANCE</b><br>
<b>Background</b><br>
A key element in managing impacts on people in local communities is assessment and
planning in order to understand the actual and potential impacts, and strong engagement with
local communities to understand their expectations and needs. There are many elements that
can be incorporated into local community engagement, impact assessments, and development
programs. This disclosure seeks to identify which elements have been consistently applied,
organization-wide.
Where possible, organizations are expected to anticipate and avoid negative impacts on local
communities. Where this is not possible, or where residual impacts remain, organizations are
expected to manage those impacts appropriately, including grievances, and to compensate
local communities for negative impacts.
Establishing a timely and effective stakeholder identification and engagement process is
important to help organizations understand the vulnerability of local communities and how these
might be affected by the organization’s activities. A stakeholder engagement process both in
early planning stages as well as during operations, can help establish lines of communication
between an organization’s various departments (planning, finance, environment, production,
etc.) and key stakeholder interest groups in the community. This enables an organization to
consider the views of community stakeholders in its decisions, and to address its potential
impacts on local communities in a timely manner.
Organizations can utilize a number of useful tools to engage communities, including social and
human rights impact assessments, which include a diverse set of approaches for proper
identification of stakeholders and community characteristics. These can be based on issues
such as ethnic background, indigenous descent, gender, age, migrant status, socioeconomic
status, literacy levels, disabilities, income level, infrastructure availability or specific human
health vulnerabilities which may exist within stakeholder communities.
An organization is expected to consider the differentiated nature of local communities and to
take specific action to identify and engage vulnerable groups. This might require adopting
differentiated measures to allow the effective participation of vulnerable groups, such as making
information available in alternate languages or format for those who are not literate or who do
not have access to printed materials. Where necessary, organizations are expected to establish
additional or separate processes so that negative impacts on vulnerable or disadvantaged
groups are avoided, minimized, mitigated or compensated
  `,
  "413-2": `
            <div><strong>Disclosure 413-2 Operations with significant actual and
potential negative impacts on local communities</strong></div>
  <br>
    <div><strong>REQUIREMENTS</strong></div>
  <div><strong>
  The reporting organization shall report the following information:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
	<li>Operations with significant actual and potential negative impacts on local communities, including:
<ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>
the location of the operations;</li>
<li>
the significant actual and potential negative impacts of operations.</li>
</ul>
</li></ul>
</strong>
  </div>
  <div><strong>RECOMMENDATIONS</strong></div>
  2.2 When compiling the information specified in Disclosure 413-2, the reporting organization
should:<br>
<ul style='padding-left:40px'>
<li>
2.2.1 report the vulnerability and risk to local communities from potential negative
impacts due to factors including:
<ul style='padding-left:40px'>
<li>
2.2.1.1 the degree of physical or economic isolation of the local community;</li>
<li>2.2.1.2 the level of socioeconomic development, including the degree of gender
equality within the community;</li>
<li>2.2.1.3 the state of socioeconomic infrastructure, including health and education
infrastructure;</li>
<li>2.2.1.4 the proximity to operations;</li>
<li>2.2.1.5 the level of social organization;</li>
<li>2.2.1.6 the strength and quality of the governance of local and national institutions
around local communities;</li>
</ul>
</li>
<li>
2.2.2 report the exposure of the local community to its operations due to higher than
average use of shared resources or impact on shared resources, including:
<ul style='padding-left:40px'>
<li>
2.2.2.1 the use of hazardous substances that have an impact on the environment
and human health in general, and specifically have an impact on
reproductive health;</li>
<li>
2.2.2.2 the volume and type of pollution released;</li>
<li>
2.2.2.3 the status as major employer in the local community;</li>
<li>
2.2.2.4 land conversion and resettlement;</li>
<li>
2.2.2.5 natural resource consumption;</li>
</ul>
</li>
<li>
2.2.3 for each of the significant actual and potential negative economic, social, cultural,
and/or environmental impacts on local communities and their rights, describe:
<ul style='padding-left:40px'>
<li>
2.2.3.1 the intensity or severity of the impact;</li>
<li>
2.2.3.2 the likely duration of the impact;</li>
<li>
2.2.3.3 the reversibility of the impact;</li>
<li>
2.2.3.4 the scale of the impact.</li></ul>
</li>
</ul>
<br>
<b>GUIDANCE</b>
Internal sources of information about actual and potential negative impacts of operations on
local communities can include:
<ul style='padding-left:40px;list-style-type:disc'>
<li>actual performance data;</li>
<li> internal investment plans and associated risk assessments;</li>
<li> all data collected with topic disclosures as they relate to individual communities. For
example: GRI 203: Indirect Economic Impacts 2016, GRI 301: Materials 2016, GRI 302:
Energy 2016, GRI 303: Water and Effluents 2018, GRI 304: Biodiversity 2016, GRI 305:
Emissions 2016, GRI 306: Waste 2020, GRI 403: Occupational Health and Safety 2018, GRI
408: Child Labor 2016, GRI 409: Forced or Compulsory Labor 2016, GRI 410: Security
Practices 2016, GRI 411: Rights of Indigenous Peoples 2016, and GRI 416: Customer Health
and Safety 2016.</li>
<br>
<b>Background</b><br>
This disclosure is focused on significant actual and potential negative impacts related to an
organization’s operations and not on community investments or donations, which are
addressed by GRI 201: Economic Performance 2016.
This disclosure informs stakeholders about an organization’s awareness of its negative
impacts on local communities. It also enables the organization to better prioritize and improve
its organization-wide attention to local communities.
  `,
  "414-1": `
<div><strong>Disclosure 414-1 New suppliers that were screened
using social criteria</strong></div>
  <br>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The reporting organization shall report the following information:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>Percentage of new suppliers that were screened using social criteria.</li>
</ul>
    </strong>
  <div><strong>GUIDANCE</strong></div>
Social criteria can include the topics covered in other GRI Topic Standards (e.g., GRI 401:
Employment 2016, GRI 403: Occupational Health and Safety 2018, GRI 408: Child Labor 2016,
GRI 409: Forced or Compulsory Labor 2016).<br>
<b>Background</b><br>
This disclosure informs stakeholders about the percentage of suppliers selected or contracted
subject to due diligence processes for social impacts.<br>
An organization is expected to initiate due diligence as early as possible in the development of a
new relationship with a supplier.<br>
Impacts may be prevented or mitigated at the stage of structuring contracts or other agreements,
as well as via ongoing collaboration with suppliers.
  `,
  "414-2": `
  <div><strong>Disclosure 414-2 Negative social impacts in the supply
chain and actions taken</strong></div>
  <br>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The reporting organization shall report the following information:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>Number of suppliers assessed for social impacts.</li>
<li>Number of suppliers identified as having significant actual and potential negative social impacts.</li>
<li>Significant actual and potential negative social impacts identified in the supply chain.</li>
<li>Percentage of suppliers identified as having significant actual and potential negative social impacts with which improvements were agreed upon as a result of assessment.</li>
<li>Percentage of suppliers identified as having significant actual and potential negative social impacts with which relationships were terminated as a result of assessment, and why.</li>
</ul>

    </strong><br><hr>
  <div><strong>RECOMMENDATIONS</strong></div>
 2.1 When compiling the information specified in Disclosure 414-2, the reporting organization
should, where it provides appropriate context on significant impacts, provide a
breakdown of the information by:<br>
<ul style='padding-left:40px'>
<li>
2.1.1 the location of the supplier;</li>
<li>
2.1.2 the significant actual and potential negative social impact.</li>
</ul><br>
<b>GUIDANCE</b><br>
Negative impacts include those that are either caused or contributed to by an organization, or
that are directly linked to its operations, products, or services by its relationship with a supplier.<br>
Assessments for social impacts can include the topics covered in other GRI Topic Standards
(e.g., GRI 401: Employment 2016, GRI 403: Occupational Health and Safety 2018, GRI 408:
Child Labor 2016, GRI 409: Forced or Compulsory Labor 2016).<br>
Assessments can be made against agreed performance expectations that are set and
communicated to the suppliers prior to the assessment.<br>
Assessments can be informed by audits, contractual reviews, two-way engagement, and
complaint and grievance mechanisms.<br>
Improvements can include changing an organization’s procurement practices, the adjustment of
performance expectations, capacity building, training, and changes to processes.<br>

This disclosure informs stakeholders about an organization’s awareness of significant actual
and potential negative social impacts in the supply chain.
  `,
  "415-1": `
    <div><strong>Disclosure 415-1 Political contributions</strong></div>
  <br>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The reporting organization shall report the following information:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>Total monetary value of financial and in-kind political contributions made directly and indirectly by the organization by country and recipient/beneficiary.</li>
<li>
If applicable, how the monetary value of in-kind contributions was estimated.</li>
</ul>
Compilation requirements<br>
2.1 When compiling the information specified in Disclosure 415-1, the reporting organization shall calculate financial political contributions in compliance with national accounting rules, where these exist.

    </strong><br><hr>
  <div><strong>GUIDANCE</strong></div>
  <b>Background</b><br>
  The purpose of this disclosure is to identify an organization’s support for political causes.<br>
This disclosure can provide an indication of the extent to which an organization’s political
contributions are in line with its stated policies, goals, or other public positions.<br>
Direct or indirect contributions to political causes can also present corruption risks, because
they can be used to exert undue influence on the political process. Many countries have
legislation that limits the amount an organization can spend on political parties and candidates
for campaigning purposes. If an organization channels contributions indirectly through
intermediaries, such as lobbyists or organizations linked to political causes, it can improperly
circumvent such legislation.
  `,
  "416-1": `
      <div><strong>Disclosure 416-1 Assessment of the health and safety
impacts of product and service categories</strong></div>
  <br>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The reporting organization shall report the following information:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>
Percentage of significant product and service categories for which health and safety impacts are assessed for improvement.
</li>
</ul>
    </strong><br><hr>
  <div><strong>GUIDANCE</strong></div>
  This measure helps to identify the existence and range of systematic efforts to address health
and safety across the life cycle of a product or service. In reporting the information in Disclosure
416-1, the reporting organization can also describe the criteria used for the assessment.
  `,
  "416-2": `
        <div><strong>Disclosure 416-2 Incidents of non-compliance
concerning the health and safety impacts of products
and services</strong></div>
  <br>
  <div><strong>REQUIREMENTS</strong></div>

  <strong>

The reporting organization shall report the following information:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>Total number of incidents of non-compliance with regulations and/or voluntary codes concerning the health and safety impacts of products and services within the reporting period, by:
<ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>incidents of non-compliance with regulations resulting in a fine or penalty;</li>
<li>incidents of non-compliance with regulations resulting in a warning;</li>
<li>incidents of non-compliance with voluntary codes.</li>
</ul>
</li>
<li>If the organization has not identified any non-compliance with regulations and/or voluntary codes, a brief statement of this fact is sufficient.</li>
</ul>
Compilation requirements<br>
2.1 When compiling the information specified in Disclosure 416-2, the reporting organization shall:
<ul  style='list-style-type:none;padding-left:40px'>
<li>2.1.1 exclude incidents of non-compliance in which the organization was determined not to be at fault;</li>
<li>2.1.2 exclude incidents of non-compliance related to Incidents related to labeling are reported in Disclosure 417-2 of GRI 417: Marketing and Labelling 2016;</li>
<li>2.1.3 if applicable, identify any incidents of non-compliance that relate to events in periods prior to the reporting period.</li>
</ul>
  </strong>
  <div><strong>GUIDANCE</strong></div>
The incidents of non-compliance that occur within the reporting period can relate to incidents
formally resolved during the reporting period, whether they occurred in periods prior to the
reporting period or not.<br>
<b>Background</b><br>
Protection of health and safety is a recognized goal of many national and international
regulations. Customers expect products and services to perform their intended functions
satisfactorily, and not pose a risk to health and safety. Customers have a right to non-hazardous
products. Where their health and safety is affected, customers also have the right to seek
redress.<br>
This disclosure addresses the life cycle of the product or service once it is available for use, and
therefore subject to regulations and voluntary codes concerning the health and safety of
products and services.
  `,
  "417-1": `
          <div><strong>Disclosure 417-1 Requirements for product and service
information and labeling</strong></div>
  <br>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The reporting organization shall report the following information:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>Whether each of the following types of information is required by the organization’s procedures for product and service information and labeling:
<ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>The sourcing of components of the product or service;</li>
<li>Content, particularly with regard to substances that might produce an environmental or social impact;</li>
<li>Safe use of the product or service;</li>
<li>Disposal of the product and environmental or social impacts;</li>
<li>Other (explain).</li>
</ul>
</li>
<li>
Percentage of significant product or service categories covered by and assessed for compliance with such procedures.</li>
</ul>
    </strong><br><hr>
  <div><strong>GUIDANCE</strong></div>
  <b>Background</b><br>
Customers and end users need accessible and adequate information about the positive and
negative environmental and social impacts of products and services. This can include
information on the safe use of a product or service, the disposal of the product, or the sourcing
of its components. Access to this information helps customers to make informed purchasing
choices.
  `,
  "417-2": `
            <div><strong>Disclosure 417-2 Incidents of non-compliance
concerning product and service information and labeling</strong></div>
  <br>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The reporting organization shall report the following information:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>Total number of incidents of non-compliance with regulations and/or voluntary codes concerning product and service information and labeling, by:
<ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>incidents of non-compliance with regulations resulting in a fine or penalty;</li>
<li>incidents of non-compliance with regulations resulting in a warning;</li>
<li>incidents of non-compliance with voluntary codes.</li>
</ul>
</li>
<li>
If the organization has not identified any non-compliance with regulations and/or voluntary codes, a brief statement of this fact is sufficient.</li>
</ul>
Compilation requirements<br>
2.1 When compiling the information specified in Disclosure 417-2, the reporting organization shall:
<ul  style='list-style-type:none;padding-left:40px'>
<li>exclude incidents of non-compliance in which the organization was determined not to be at fault;</li>
<li>if applicable, identify any incidents of non-compliance that relate to events in periods prior to the reporting period.</li>
</ul>
    </strong>
  <div><strong>GUIDANCE</strong></div>
The incidents of non-compliance that occur within the reporting period can relate to incidents
formally resolved during the reporting period, whether they occurred in periods prior to the
reporting period or not.<br>
<b>Background</b><br>
Providing appropriate information and labeling with respect to economic, environmental, and
social impacts can be linked to compliance with certain types of regulations, laws, and codes. It
is, for example, linked to compliance with regulations, national laws, and the Organisation for
Economic Co-operation and Development (OECD) OECD Guidelines for Multinational
Enterprises. It is also potentially linked to compliance with strategies for brand and market
differentiation.<br>
The display and provision of information and labeling for products and services are subject to
many regulations and laws. Non-compliance can indicate either inadequate internal
management systems and procedures or ineffective implementation. The trends revealed by
this disclosure can indicate improvements or a deterioration in the effectiveness of internal
controls.
  `,
  "417-3": `
              <div><strong>Disclosure 417-3 Incidents of non-compliance
concerning marketing communications</strong></div>
  <br>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The reporting organization shall report the following information:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>
Total number of incidents of non-compliance with regulations and/or voluntary codes concerning marketing communications, including advertising, promotion, and sponsorship, by:
<ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>incidents of non-compliance with regulations resulting in a fine or penalty;</li>
<li>incidents of non-compliance with regulations resulting in a warning;</li>
<li>incidents of non-compliance with voluntary codes.</li>
</ul>
</li>
<li>
If the organization has not identified any non-compliance with regulations and/or voluntary codes, a brief statement of this fact is sufficient.
</li>
</ul>
Compilation requirements<br>
2.2 When compiling the information specified in Disclosure 417-3, the reporting organization shall:
<ul  style='list-style-type:none;padding-left:40px'>
<li>exclude incidents of non-compliance in which the organization was determined not to be at fault;</li>
<li>if applicable, identify any incidents of non-compliance that relate to events in periods prior to the reporting period.</li>
</ul>

    </strong>
  <div><strong>GUIDANCE</strong></div>

  The incidents of non-compliance that occur within the reporting period can relate to incidents
formally resolved during the reporting period, whether they occurred in periods prior to the
reporting period or not.

<b>Background</b><br>
Marketing is an important method of communication between organizations and customers, and
is subject to many regulations, laws, and voluntary codes, such as the International Chamber of
Commerce (ICC)’s Consolidated Code of Advertising and Marketing Communication Practice.<br>
An organization is expected to use fair and responsible practices in its business and dealings
with customers. Fair and responsible marketing requires the organization to communicate
transparently about the economic, environmental, and social impacts of its brands, products,
and services. Fair and responsible marketing also avoids any deceptive, untruthful, or
discriminatory claims, and does not take advantage of a customers’ lack of knowledge or
choices.
  `,
  "418-1": `
<div><strong>Disclosure 418-1 Substantiated complaints concerning
breaches of customer privacy and losses of customer
data</strong></div>
  <br>
    <div><strong>REQUIREMENTS</strong></div>
    <strong>
    The reporting organization shall report the following information:
<ul  style='list-style-type:lower-alpha;padding-left:40px'>
<li>Total number of substantiated complaints received concerning breaches of customer privacy, categorized by:
complaints received from outside parties and substantiated by the organization;
<ul  style='list-style-type:lower-roman;padding-left:40px'>
<li>complaints from regulatory bodies.</li>
<li>Total number of identified leaks, thefts, or losses of customer data.</li>
</ul>
</li>
<li>
If the organization has not identified any substantiated complaints, a brief statement of this fact is sufficient.</li>
</ul>
Compilation requirements<br>
2.1 When compiling the information specified in Disclosure 418-1, the reporting organization shall indicate if a substantial number of these breaches relate to events in preceding years.

    </strong><br><hr>
  <div><strong>GUIDANCE</strong></div>
<b>Background</b><br>
Protection of customer privacy is a generally recognized goal in national regulations and
organizational policies. As set out in the Organisation for Economic Co-operation and
Development (OECD) OECD Guidelines for Multinational Enterprises, organizations are
expected to ‘respect consumer privacy and take reasonable measures to ensure the security of
personal data that they collect, store, process or disseminate’.<br>
To protect customer privacy, an organization is expected to limit its collection of personal data, to
collect data by lawful means, and to be transparent about how data are gathered, used, and
secured. The organization is also expected to not disclose or use personal customer
information for any purposes other than those agreed upon, and to communicate any changes
in data protection policies or measures to customers directly.<br>
This disclosure provides an evaluation of the success of management systems and procedures
relating to customer privacy protection.
  `,
};
